import React, { useState, useEffect, useRef, useMemo } from "react";
import styled from "styled-components";
import { toast } from "react-toastify";
import {
  FiPlus,
  FiEdit,
  FiTrash2,
  FiUsers,
  FiPackage,
  FiBarChart2,
  FiImage,
  FiSettings,
  FiGrid,
  FiLayers,
  FiUploadCloud,
  FiTag,
  FiMenu,
  FiX,
  FiArrowLeft,
  FiLogOut,
  FiBox,
  FiFolder,
  FiFileText,
  FiChevronLeft,
  FiChevronRight,
  FiSearch,
} from "react-icons/fi";
import { showConfirm } from '../utils/sweetAlert';
import { FaTimes, FaArrowRight } from "react-icons/fa";
import api from "../services/api";
import ReactSelect from "react-select";


// Importar tipos
import { Product, Category, SliderItem, Family, Subfamily } from "../types";

// Importar componentes
import UsersTab from "../components/admin/UsersTab";
import RolesTab from "../components/admin/RolesTab";
import BannersTab from "../components/admin/BannersTab";
import ExcelUpload from '../components/admin/ExcelUpload';
import CombosTab from '../components/admin/CombosTab';
import FooterTab from '../components/admin/FooterTab';
import FamilySelector from '../components/admin/FamilySelector';
import SubfamilySelector from '../components/admin/SubfamilySelector';

// Importar servicios
import { userService } from "../services/userService";
import { sliderService } from "../services/sliderService";

import { useSettings } from "../context/SettingsContext";
import { Settings } from "../services/settingsService";
import { productService } from "../services/productService";
import { categoryService } from "../services/categoryService";
import { familyService } from "../services/familyService";
import { subfamilyService } from "../services/subfamilyService";
import { dailyDealService } from "../services/dailyDealService";


import { getImageUrl, API_URL } from "../config";
import { useAuth } from "../context/AuthContext";
import { useNavigate } from "react-router-dom";

// Importar componentes estilizados
import {
  ActionButton,
  ActionButtons,
  Button,
  ButtonGroup,
  Form,
  FormGroup,
  IconButton,
  Input,
  Label,
  Modal,
  ModalContent,
  Select,
  StatCard,
  StatsGrid,
  Table,
  TableCell,
  TableHeader,
  TextArea,
} from "../components/shared/StyledComponents";

// Definir interfaces
interface DailyDeal {
  id: string;
  product_id: string;
  product: Product;
  price: number;
  active: boolean;
  start_date: string;
  end_date: string;
}

const Plus = FiPlus as React.ComponentType<React.SVGProps<SVGSVGElement>>;
const Edit = FiEdit as React.ComponentType<React.SVGProps<SVGSVGElement>>;
const Trash = FiTrash2 as React.ComponentType<{
  size?: number;
  className?: string;
  style?: React.CSSProperties;
}>;
const Users = FiUsers as React.ComponentType<React.SVGProps<SVGSVGElement>>;
const Package = FiPackage as React.ComponentType<React.SVGProps<SVGSVGElement>>;
const BarChart = FiBarChart2 as React.ComponentType<
  React.SVGProps<SVGSVGElement>
>;
const Image = FiImage as React.ComponentType<React.SVGProps<SVGSVGElement>>;
const SettingsIcon = FiSettings as React.ComponentType<
  React.SVGProps<SVGSVGElement>
>;
const Grid = FiGrid as React.ComponentType<React.SVGProps<SVGSVGElement>>;
const Layers = FiLayers as React.ComponentType<React.SVGProps<SVGSVGElement>>;
const UploadCloud = FiUploadCloud as React.ComponentType<
  React.SVGProps<SVGSVGElement>
>;
const Tag = FiTag as React.ComponentType<React.SVGProps<SVGSVGElement>>;
const Menu = FiMenu as React.ComponentType<React.SVGProps<SVGSVGElement>>;
const X = FiX as React.ComponentType<React.SVGProps<SVGSVGElement>>;
const ArrowLeft = FiArrowLeft as React.ComponentType<
  React.SVGProps<SVGSVGElement>
>;
const LogOut = FiLogOut as React.ComponentType<React.SVGProps<SVGSVGElement>>;
const Box = FiBox as React.ComponentType<React.SVGProps<SVGSVGElement>>;
const FolderIcon = FiFolder as React.ComponentType<
  React.SVGProps<SVGSVGElement>
>;
const FileTextIcon = FiFileText as React.ComponentType<
  React.SVGProps<SVGSVGElement>
>;
const Times = FaTimes as React.ComponentType<React.SVGProps<SVGSVGElement>>;
const ArrowRight = FaArrowRight as React.ComponentType<
  React.SVGProps<SVGSVGElement>
>;

// --- NUEVO: Gestión de Espacios ---
interface SpaceProduct {
  id?: number;
  product_id: number;
  x: number;
  y: number;
  destacado: boolean;
  orden?: number;
  name?: string;
  image_url?: string;
}

interface Space {
  id: number;
  name?: string; // Agregar este campo
  image_url: string | null;
  created_at: string;
  updated_at: string;
  products: SpaceProduct[];
  destacados: SpaceProduct[];
}

// Componentes estilizados para el nuevo diseño
const AdminLayout = styled.div`
  display: flex;
  min-height: 100vh;
  background-color: #f8f9fa;
  overflow-x: hidden;
`;

const SidebarOverlay = styled.div<{ open: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 999;
  display: ${(props) => (props.open ? "block" : "none")};
  
  @media (min-width: 769px) {
    display: none;
  }
`;

const Sidebar = styled.div<{ open: boolean }>`
  width: ${(props) => (props.open ? "280px" : "70px")};
  background: #242a5b;
  color: white;
  transition: all 0.3s ease-in-out;
  overflow-y: auto;
  overflow-x: hidden;
  position: fixed;
  height: 100vh;
  z-index: 1000;
  left: 0;
  display: flex;
  flex-direction: column;
  box-shadow: 0 0 20px rgba(0,0,0,0.2);

  @media (max-width: 768px) {
    transform: translateX(${(props) => (props.open ? "0" : "-100%")});
    width: 260px;
  }
`;

const SidebarHeader = styled.div<{ open: boolean }>`
  padding: ${props => props.open ? '1.5rem' : '1rem'};
  display: flex;
  align-items: center;
  justify-content: ${props => props.open ? 'space-between' : 'center'};
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  min-height: 80px;
  position: relative;
  overflow: hidden;
`;

const SidebarTitle = styled.h1`
  font-size: 1.5rem;
  margin: 0;
  color: white;
  font-weight: 600;
`;

const WelcomeMessage = styled.p`
  font-size: 0.9rem;
  margin: 0.5rem 0 0 0;
  color: rgba(255, 255, 255, 0.8);
  font-weight: 400;
  line-height: 1.4;
`;

const SidebarMenu = styled.ul`
  list-style: none;
  padding: 1rem 0;
  margin: 0;
  flex: 1;
  display: flex;
  flex-direction: column;
`;

// Componente de tooltip para cuando el sidebar está colapsado
const Tooltip = styled.div<{ show: boolean }>`
  position: absolute;
  left: 80px;
  background: #242a5b;
  color: white;
  padding: 8px 12px;
  border-radius: 6px;
  font-size: 14px;
  white-space: nowrap;
  opacity: ${props => props.show ? '1' : '0'};
  visibility: ${props => props.show ? 'visible' : 'hidden'};
  transform: translateY(-50%);
  transition: all 0.2s ease;
  z-index: 1003;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  
  &::before {
    content: '';
    position: absolute;
    left: -6px;
    top: 50%;
    transform: translateY(-50%);
    border: 6px solid transparent;
    border-right-color: #242a5b;
  }
`;

const SidebarMenuItem = styled.li<{ active: boolean; sidebarOpen: boolean }>`
  padding: 0;
  margin: 0.25rem 0;
  display: flex;
  align-items: center;
  padding: 0.75rem ${(props) => (props.sidebarOpen ? "1.5rem" : "0")};
  justify-content: ${(props) => (props.sidebarOpen ? "flex-start" : "center")};
  color: ${(props) => (props.active ? "white" : "rgba(255,255,255,0.7)")};
  text-decoration: none;
  transition: all 0.2s ease;
  background: ${(props) =>
    props.active ? "rgba(255,255,255,0.1)" : "transparent"};
  border-left: 4px solid
    ${(props) => (props.active ? "#FDBF37" : "transparent")};
  cursor: pointer;
  position: relative;
  overflow: hidden;
  white-space: nowrap;

  &:hover {
    background: rgba(255, 255, 255, 0.05);
    color: white;
  }
  
  /* Mostrar tooltip al hacer hover cuando el sidebar está cerrado */
  &:hover ${Tooltip} {
    opacity: ${props => props.sidebarOpen ? '0' : '1'};
    visibility: ${props => props.sidebarOpen ? 'hidden' : 'visible'};
  }
`;

const SidebarIcon = styled.div<{ sidebarOpen?: boolean }>`
  margin-right: ${(props) => (props.sidebarOpen ? "0.75rem" : "0")};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.25rem;
`;

const SidebarLogout = styled.button<{ sidebarOpen: boolean }>`
  display: flex;
  align-items: center;
  justify-content: ${(props) => (props.sidebarOpen ? "flex-start" : "center")};
  width: 100%;
  padding: 0.75rem ${(props) => (props.sidebarOpen ? "1.5rem" : "0")};
  background: transparent;
  border: none;
  color: rgba(255, 255, 255, 0.7);
  text-decoration: none;
  transition: all 0.2s ease;
  cursor: pointer;
  margin-top: auto;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  position: relative;
  overflow: hidden;
  white-space: nowrap;

  &:hover {
    background: rgba(255, 255, 255, 0.05);
    color: white;
  }

  &:focus {
    outline: none;
  }
  
  /* Mostrar tooltip al hacer hover cuando el sidebar está cerrado */
  &:hover ${Tooltip} {
    opacity: ${props => props.sidebarOpen ? '0' : '1'};
    visibility: ${props => props.sidebarOpen ? 'hidden' : 'visible'};
  }
`;

const MainContent = styled.div.withConfig({
  shouldForwardProp: (prop) => prop !== 'sidebarOpen'
}) <{ sidebarOpen: boolean }>`
  flex: 1;
  padding: 2rem;
  margin-left: ${(props) => (props.sidebarOpen ? "280px" : "70px")};
  transition: all 0.3s ease-in-out;
  min-width: 0;
  overflow-x: hidden;

  @media (max-width: 768px) {
    margin-left: 0;
    width: 100%;
    padding: 1rem;
  }
`;

const BackButton = styled.button`
  position: fixed;
  bottom: 20px;
  right: 20px;
  width: 50px;
  height: 50px;
  border-radius: 50%;
  background-color: #242a5b;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  cursor: pointer;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
  z-index: 999;
  transition: all 0.2s ease-in-out;

  &:hover {
    background-color: #FDBF37;
    transform: scale(1.05);
  }

  &:focus {
    outline: none;
  }

  svg {
    font-size: 1.5rem;
  }
`;

const TopBar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
  background: white;
  padding: 1rem;
  border-radius: 10px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
`;

const MenuToggle = styled.button`
  background:rgba(255, 255, 255, 0);
  color:rgb(255, 255, 255);
  font-size: 1.5rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0.5rem;
  border-radius: 8px;
  transition: all 0.2s ease;
  z-index: 1001;
`;

const TopBarButton = styled.button`
  background: none;
  border: none;
  color: #242a5b;
  font-size: 1.5rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0.5rem;
  border-radius: 8px;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(0, 0, 0, 0.05);
  }

  &:focus {
    outline: none;
  }

  svg {
    font-size: 1.5rem;
  }
`;

const PageTitle = styled.h1`
  margin: 0;
  font-size: 1.5rem;
  color: #242a5b;
  font-weight: 600;
`;

const ContentCard = styled.div`
  background: white;
  border-radius: 10px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
  padding: 1.5rem;
  overflow: hidden;
`;

const ProductImage = styled.img`
  width: 50px;
  height: 50px;
  object-fit: cover;
  border-radius: 8px;
`;

// Botón flotante para toggle del sidebar (Opción 3)
// Nuevo toggle integrado dentro del sidebar
const SidebarToggle = styled.button<{ open: boolean }>`
  background: transparent;
  border: none;
  color: white;
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  position: ${props => props.open ? 'static' : 'absolute'};
  top: ${props => props.open ? 'auto' : '20px'};
  left: ${props => props.open ? 'auto' : '14px'};
  margin: ${props => props.open ? '0' : '0'};
  
  &:hover {
    color: rgba(255, 255, 255, 0.8);
    transform: scale(1.1);
  }
  
  &:active {
    transform: scale(0.9);
  }
  
  svg {
    font-size: 30px;
    transition: all 0.2s ease;
  }
  
  /* Solo visible en PC (min-width: 769px) */
  @media (max-width: 768px) {
    display: none;
  }
`;

// Componente de tooltip para cuando el sidebar está colapsado
// Contenedor del título con animaciones
const SidebarTitleContainer = styled.div<{ open: boolean }>`
  opacity: ${props => props.open ? '1' : '0'};
  transform: ${props => props.open ? 'translateX(0)' : 'translateX(-20px)'};
  transition: all 0.3s ease;
  pointer-events: ${props => props.open ? 'auto' : 'none'};
`;

const MenuButton = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  z-index: 1001;
  
  /* Solo visible en móvil (max-width: 768px) */
  @media (min-width: 769px) {
    display: none;
  }
  
  .button {
    width: 44px;
    height: 44px;
    border-radius: 10px;
    background: linear-gradient(135deg, var(--primary-orange), #FDBF37);
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    box-shadow: 0 4px 15px rgba(255, 140, 66, 0.3);
    transition: all 0.3s cubic-bezier(0.68, -0.55, 0.265, 1.55);
    
    &:hover {
      transform: scale(1.05);
      box-shadow: 0 6px 18px rgba(255, 140, 66, 0.4);
    }
  }
  
  .tooltip {
    position: absolute;
    top: 100%;
    left: 50%;
    transform: translateX(-50%) translateY(10px);
    background-color: #242a5b;
    color: white;
    padding: 5px 10px;
    border-radius: 20px;
    font-size: 12px;
    font-weight: 500;
    margin-top: 8px;
    opacity: 0;
    white-space: nowrap;
    pointer-events: none;
    transition: all 0.3s ease;
  }
  
  &:hover .tooltip {
    opacity: 1;
    transform: translateX(-50%) translateY(5px);
  }
`;

const ContentWrapper = styled.div`
  min-height: calc(100vh - 4rem);
  display: flex;
  flex-direction: column;
`;

const StatIcon = styled.div<{ color: string }>`
  width: 60px;
  height: 60px;
  border-radius: 12px;
  background: ${(props) => props.color};
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 1.5rem;
`;

const StatInfo = styled.div`
  flex: 1;
`;

const StatValue = styled.div`
  font-size: 2rem;
  font-weight: 700;
  color: #2c3e50;
  margin-bottom: 0.25rem;
`;

const StatLabel = styled.div`
  color: #7f8c8d;
  font-size: 0.9rem;
  text-transform: uppercase;
  letter-spacing: 1px;
`;

// Mock data corregido
// const mockProducts: Product[] = [
//   {
//     id: "1",
//     name: "Laptop HP Pavilion",
//     description: "Laptop de alto rendimiento",
//     image_url:
//       "https://via.placeholder.com/300x200/3498db/ffffff?text=Laptop+HP",
//     category_id: "1",
//     category_name: "Electrónicos",
//     brand: "HP",
//     model: "Pavilion",
//     stock: 15,
//     featured: true,
//   },
//   {
//     id: "2",
//     name: "Mouse Inalámbrico Logitech",
//     description: "Mouse ergonómico con conectividad Bluetooth",
//     image_url:
//       "https://via.placeholder.com/300x200/e74c3c/ffffff?text=Mouse+Logitech",
//     category_id: "2",
//     category_name: "Accesorios",
//     brand: "Logitech",
//     model: "MX Master",
//     stock: 25,
//     featured: false,
//   },
// ];

interface ProductFormData {
  name: string;
  description: string;
  image_url: string;
  category_id: string;
  family_id: string;
  subfamily_id: string;
  category_name?: string;
  brand?: string;
  model?: string;
  stock: number;
  featured: boolean;
  additional_images?: File[];
}

// Mover los componentes estilizados fuera del componente funcional
const SliderImage = styled.img`
  width: 100px;
  height: 60px;
  object-fit: cover;
  border-radius: 8px;
`;

// Agregar estilos para la lista visual de productos destacados
const FeaturedProductsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  max-height: 220px;
  overflow-y: auto;
  margin-bottom: 0.5rem;
`;
const FeaturedProductItem = styled.div<{ disabled?: boolean }>`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem;
  border: 1px solid #ddd;
  border-radius: 8px;
  cursor: ${(props) => (props.disabled ? "not-allowed" : "pointer")};
  opacity: ${(props) => (props.disabled ? 0.5 : 1)};
  transition: all 0.2s ease;

  &:hover {
    background-color: ${(props) =>
    props.disabled ? "transparent" : "#f5f5f5"};
    border-color: ${(props) =>
    props.disabled ? "#ddd" : "var(--primary-orange)"};
  }

  input[type="checkbox"] {
    margin: 0;
  }

  div {
    flex: 1;
    min-width: 0;

    span {
      font-weight: 500;
      display: block;
      margin-bottom: 2px;
    }

    small {
      font-size: 0.8rem;
      opacity: 0.7;
    }
  }
`;
const ProductMiniImg = styled.img`
  width: 40px;
  height: 40px;
  object-fit: cover;
  border-radius: 6px;
  border: 1px solid #ddd;
`;
const SelectedProducts = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-top: 0.5rem;
`;
const SelectedProductTag = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: var(--primary-orange);
  color: white;
  padding: 0.5rem 0.75rem;
  border-radius: 20px;
  font-size: 0.9rem;

  div {
    flex: 1;
    min-width: 0;

    span {
      font-weight: 500;
      display: block;
    }

    small {
      font-size: 0.75rem;
      opacity: 0.8;
    }
  }
`;
const RemoveBtn = styled.button`
  background: none;
  border: none;
  color: #e74c3c;
  font-size: 1.1rem;
  cursor: pointer;
  margin-left: 0.25rem;
`;

const ProductGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 10px;
  max-height: 300px;
  overflow-y: auto;
  border: 1px solid #ddd;
  border-radius: 8px;
  padding: 10px;
  background-color: #f9f9f9;
`;

const ProductCard = styled.div<{ selected: boolean }>`
  padding: 10px;
  border: 2px solid ${(props) => (props.selected ? "#007bff" : "#ddd")};
  border-radius: 8px;
  cursor: pointer;
  background-color: ${(props) => (props.selected ? "#e3f2fd" : "white")};
  transition: all 0.2s ease;

  &:hover {
    border-color: #007bff;
    background-color: #f0f8ff;
  }
`;

const ProductName = styled.h4`
  margin: 0 0 5px 0;
  font-size: 14px;
  color: #333;
  font-weight: 600;
`;

const ProductInfo = styled.p`
  margin: 0;
  font-size: 12px;
  color: #666;
`;










const AdminProductTooltipCard = styled.div<{ $visible: boolean }>`
  position: absolute;
  top: 100%;
  left: 50%;
  transform: translateX(-50%) translateY(10px);
  background: white;
  border-radius: 12px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.15);
  padding: 16px;
  width: 280px;
  z-index: 1000;
  opacity: ${(props) => (props.$visible ? 1 : 0)};
  visibility: ${(props) => (props.$visible ? "visible" : "hidden")};
  transition: all 0.3s ease;
  border: 1px solid #e9ecef;
  cursor: pointer;

  &:hover {
    transform: translateX(-50%) translateY(8px);
    box-shadow: 0 12px 40px rgba(0, 0, 0, 0.2);
  }

  &::before {
    content: "";
    position: absolute;
    top: -8px;
    left: 50%;
    transform: translateX(-50%);
    width: 0;
    height: 0;
    border-left: 8px solid transparent;
    border-right: 8px solid transparent;
    border-bottom: 8px solid white;
  }
`;

const AdminProductTooltipImage = styled.img`
  width: 60px;
  height: 60px;
  object-fit: cover;
  border-radius: 8px;
  border: 1px solid #e9ecef;
`;

const AdminProductTooltipContent = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
`;

const AdminProductTooltipInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const AdminProductTooltipName = styled.h4`
  margin: 0 0 4px 0;
  font-size: 14px;
  font-weight: 600;
  color: #2c3e50;
  line-height: 1.3;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const AdminProductTooltipPrice = styled.p`
  margin: 0 0 8px 0;
  font-size: 13px;
  color: #7f8c8d;
  font-weight: 500;
`;

const AdminProductTooltipButton = styled.button`
  background: linear-gradient(135deg, var(--primary-orange), #ff8c42);
  color: white;
  border: none;
  border-radius: 6px;
  padding: 6px 12px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  transition: all 0.3s ease;
  width: 100%;
  justify-content: center;

  &:hover {
    background: linear-gradient(135deg, #FDBF37, var(--primary-orange));
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(255, 140, 66, 0.3);

    svg {
      color: #fff;
      transform: translateX(2px);
    }
  }

  svg {
    transition: all 0.3s ease;
    color: rgba(255, 255, 255, 0.8);
  }
`;

const AdminProductTooltipArrow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: rgba(255, 140, 66, 0.1);
  transition: all 0.3s ease;
  cursor: pointer;

  svg {
    color: var(--primary-orange);
    transition: all 0.3s ease;
    width: 16px;
    height: 16px;
  }
`;

// Componentes de paginación
const PaginationContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 0.5rem;
  margin-top: 2rem;
  padding: 1.5rem;
  background: linear-gradient(135deg, #ffffff, #f8f9fa);
  border-radius: 16px;
  border: 1px solid #e9ecef;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
`;

const PaginationButton = styled.button<{ active?: boolean; disabled?: boolean }>`
  padding: 0.75rem 1rem;
  border: none;
  background: ${props => {
    if (props.disabled) return '#e9ecef';
    if (props.active) return '#FDBF37';
    return '#f8f9fa';
  }};
  color: ${props => {
    if (props.disabled) return '#adb5bd';
    if (props.active) return '#212529';
    return '#495057';
  }};
  border-radius: 50%;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  font-size: 0.875rem;
  font-weight: ${props => props.active ? '700' : '600'};
  transition: all 0.3s ease;
  min-width: 44px;
  height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: ${props => {
    if (props.disabled) return 'none';
    if (props.active) return '0 4px 12px rgba(255, 193, 7, 0.4)';
    return '0 2px 6px rgba(0,0,0,0.1)';
  }};
  margin: 0 4px;

  &:hover:not(:disabled) {
    background: ${props => {
      if (props.active) return '#FDBF37';
      return '#e9ecef';
    }};
    transform: translateY(-2px);
    box-shadow: ${props => {
      if (props.active) return '0 6px 16px rgba(255, 193, 7, 0.5)';
      return '0 4px 12px rgba(0,0,0,0.15)';
    }};
  }

  &:disabled {
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
    opacity: 0.6;
  }

  svg {
    width: 16px;
    height: 16px;
  }
`;

const PaginationInfo = styled.span`
  font-size: 0.875rem;
  color: #495057;
  margin: 0 1.5rem;
  white-space: nowrap;
  font-weight: 600;
  background: linear-gradient(135deg, #fff3cd, #FDBF37);
  padding: 0.75rem 1.25rem;
  border-radius: 20px;
  border: 1px solid #FDBF37;
  box-shadow: 0 2px 8px rgba(255, 193, 7, 0.2);
`;

const PaginationEllipsis = styled.span`
  padding: 0.5rem;
  color: #999;
  font-size: 0.875rem;

  &:hover {
    background: var(--primary-orange);
    transform: scale(1.1);
    box-shadow: 0 4px 12px rgba(255, 140, 66, 0.3);

    svg {
      color: white;
      transform: translateX(3px);
    }
  }
`;

const CustomProductSelector = styled.div`
  position: relative;
  min-width: 220px;
  flex: 1;
`;

const ProductSelectorInput = styled.input`
  width: 100%;
  padding: 8px 12px;
  border: 2px solid #e9ecef;
  border-radius: 8px;
  font-size: 14px;
  outline: none;
  transition: border-color 0.2s ease;

  &:focus {
    border-color: var(--primary-orange);
  }

  &::placeholder {
    color: #6c757d;
  }
`;

const ProductDropdown = styled.div<{ $isOpen: boolean }>`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: white;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  max-height: 300px;
  overflow-y: auto;
  z-index: 1000;
  display: ${(props) => (props.$isOpen ? "block" : "none")};
  margin-top: 4px;
`;

const ProductOption = styled.div<{ $isSelected: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  cursor: pointer;
  transition: background-color 0.2s ease;
  background-color: ${(props) =>
    props.$isSelected ? "#e3f2fd" : "transparent"};

  &:hover {
    background-color: #f8f9fa;
  }

  &:first-child {
    border-top-left-radius: 8px;
    border-top-right-radius: 8px;
  }

  &:last-child {
    border-bottom-left-radius: 8px;
    border-bottom-right-radius: 8px;
  }
`;

const ProductOptionImage = styled.img`
  width: 32px;
  height: 32px;
  object-fit: cover;
  border-radius: 4px;
  border: 1px solid #e9ecef;
`;

const ProductOptionInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const ProductOptionName = styled.div`
  font-size: 14px;
  font-weight: 500;
  color: #2c3e50;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const ProductOptionBrand = styled.div`
  font-size: 12px;
  color: #6c757d;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const NoProductsMessage = styled.div`
  padding: 12px;
  text-align: center;
  color: #6c757d;
  font-size: 14px;
`;

interface ProductSelectorProps {
  products: Product[];
  selectedProductId: number;
  onProductChange: (productId: number) => void;
  placeholder?: string;
}

const ProductSelector: React.FC<ProductSelectorProps> = ({
  products,
  selectedProductId,
  onProductChange,
  placeholder = "Selecciona producto",
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const selectorRef = useRef<HTMLDivElement>(null);

  const selectedProduct = products.find(
    (p) => Number(p.id) === selectedProductId
  );

  // Filtrar productos basado en búsqueda mejorada
  const filteredProducts = useMemo(() => {
    if (!searchTerm.trim()) {
      return products.slice(0, 20); // Mostrar más productos inicialmente
    }
    
    const searchTermLower = searchTerm.toLowerCase().trim();
    
    return products.filter((product) => {
      const nameMatch = product.name.toLowerCase().includes(searchTermLower);
      const brandMatch = product.brand && product.brand.toLowerCase().includes(searchTermLower);
      const modelMatch = product.model && product.model.toLowerCase().includes(searchTermLower);
      const categoryMatch = product.category_name && product.category_name.toLowerCase().includes(searchTermLower);
      
      return nameMatch || brandMatch || modelMatch || categoryMatch;
    }).slice(0, 50); // Limitar a 50 resultados para mejor rendimiento
  }, [products, searchTerm]);

  // Cerrar dropdown al hacer click fuera
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        selectorRef.current &&
        !selectorRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchTerm(value);
    setIsOpen(true);
  };

  const handleProductSelect = (product: Product) => {
    onProductChange(Number(product.id));
    setSearchTerm("");
    setIsOpen(false);
  };

  const handleInputFocus = () => {
    setIsOpen(true);
  };

  const handleClearSelection = () => {
    onProductChange(0);
    setSearchTerm("");
    setIsOpen(false);
  };

  const displayValue = isOpen ? searchTerm : (selectedProduct ? selectedProduct.name : "");

  return (
    <CustomProductSelector ref={selectorRef}>
      <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
        <ProductSelectorInput
          type="text"
          value={displayValue}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          placeholder={placeholder}
          style={{ paddingRight: selectedProduct ? '40px' : '12px' }}
        />
        {selectedProduct && (
          <button
            type="button"
            onClick={handleClearSelection}
            style={{
              position: 'absolute',
              right: '8px',
              background: 'none',
              border: 'none',
              color: '#6c757d',
              cursor: 'pointer',
              fontSize: '16px',
              padding: '2px',
              borderRadius: '50%',
              width: '20px',
              height: '20px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
            title="Limpiar selección"
          >
            ×
          </button>
        )}
      </div>
      <ProductDropdown $isOpen={isOpen}>
        {filteredProducts.length > 0 ? (
          <>
            {searchTerm && (
              <div style={{
                padding: '8px 12px',
                backgroundColor: '#f8f9fa',
                borderBottom: '1px solid #e9ecef',
                fontSize: '12px',
                color: '#6c757d'
              }}>
                {filteredProducts.length} resultado{filteredProducts.length !== 1 ? 's' : ''} encontrado{filteredProducts.length !== 1 ? 's' : ''}
              </div>
            )}
            {filteredProducts.map((product) => (
              <ProductOption
                key={product.id}
                $isSelected={Number(product.id) === selectedProductId}
                onClick={() => handleProductSelect(product)}
              >
                <ProductOptionImage
                  src={getImageUrl(product.image_url)}
                  alt={product.name}
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    target.src = "/placeholder-image.jpg";
                  }}
                />
                <ProductOptionInfo>
                  <ProductOptionName>
                    {searchTerm ? (
                      <span dangerouslySetInnerHTML={{
                        __html: product.name.replace(
                          new RegExp(`(${searchTerm})`, 'gi'),
                          '<mark style="background-color: #fff3cd; padding: 0;">$1</mark>'
                        )
                      }} />
                    ) : (
                      product.name
                    )}
                  </ProductOptionName>
                  <ProductOptionBrand>
                    {product.brand && product.model
                      ? `${product.brand} - ${product.model}`
                      : product.brand || product.model || "Sin marca"}
                    {product.category_name && (
                      <span style={{ marginLeft: '8px', color: '#28a745' }}>
                        • {product.category_name}
                      </span>
                    )}
                  </ProductOptionBrand>
                </ProductOptionInfo>
              </ProductOption>
            ))}
          </>
        ) : (
          <NoProductsMessage>
            {searchTerm
              ? `No se encontraron productos que coincidan con "${searchTerm}"`
              : "No hay productos disponibles"}
          </NoProductsMessage>
        )}
      </ProductDropdown>
    </CustomProductSelector>
  );
};

const AdminPanel = () => {
  const { logout, user, hasPermission } = useAuth();
  const navigate = useNavigate();

  // Referencia para rastrear si el usuario ha interactuado con el sidebar
  const hasUserToggledSidebar = useRef(false);

  // Estado para el sidebar - abierto por defecto en PC, cerrado en móvil
  const [sidebarOpen, setSidebarOpen] = useState(window.innerWidth > 768);

  // Efecto para manejar el redimensionamiento de la ventana
  useEffect(() => {
    const handleResize = () => {
      // Solo cambiar el estado del sidebar si el usuario no ha interactuado con él
      if (!hasUserToggledSidebar.current) {
        setSidebarOpen(window.innerWidth > 768);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Funciones auxiliares
  const toggleSidebar = () => {
    hasUserToggledSidebar.current = true; // Marcar que el usuario ha interactuado con el sidebar
    setSidebarOpen(!sidebarOpen);
  };

  const handleLogout = () => {
    logout();
    toast.success("Sesión cerrada exitosamente", {
      position: "top-right",
      autoClose: 3000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
    });
    navigate("/login");
  };

  const handleBackToHome = () => {
    navigate("/");
  };

  // Estados principales
  const [activeTab, setActiveTab] = useState(() => {
    // Recuperar la sección activa del localStorage, por defecto "products"
    return localStorage.getItem('adminPanelActiveTab') || "products";
  });

  // Función helper para cambiar el tab activo y guardarlo en localStorage
  const changeActiveTab = (tabName: string) => {
    setActiveTab(tabName);
    localStorage.setItem('adminPanelActiveTab', tabName);
  };

  // Usar el contexto de configuraciones
  const { settings, updateSettings: updateSettingsContext, refreshSettings, loading: settingsLoading } = useSettings();
  const [localSettings, setLocalSettings] = useState<Settings>(settings);
  
  // Sincronizar configuraciones locales con el contexto
  useEffect(() => {
    setLocalSettings(settings);
  }, [settings]);
  const [products, setProducts] = useState<Product[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);

  // Estados para paginación
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalProducts, setTotalProducts] = useState(0);
  const [productsPerPage] = useState(10);
  const [loadingProducts, setLoadingProducts] = useState(false);

  // Estados para búsqueda y filtros
  const [searchTerm, setSearchTerm] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [filteredProducts, setFilteredProducts] = useState<Product[]>([]);

  const [stats, setStats] = useState({
    totalProducts: 0,
    totalUsers: 0,
  });

  // Estados para productos
  const [showModal, setShowModal] = useState(false);
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);
  const [formData, setFormData] = useState<ProductFormData>({
    name: "",
    description: "",
    image_url: "",
    category_id: "",
    family_id: "",
    subfamily_id: "",
    stock: 0,
    featured: false,
  });
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [additionalImageFiles, setAdditionalImageFiles] = useState<File[]>([]);

  // Estados para familias y subfamilias
  const [families, setFamilies] = useState<Family[]>([]);
  const [subfamilies, setSubfamilies] = useState<Subfamily[]>([]);
  const [filteredFamilies, setFilteredFamilies] = useState<Family[]>([]);
  const [filteredSubfamilies, setFilteredSubfamilies] = useState<Subfamily[]>(
    []
  );

  // Estados para paginación de familias
  const [familiesCurrentPage, setFamiliesCurrentPage] = useState(1);
  const [familiesTotalPages, setFamiliesTotalPages] = useState(1);
  const [familiesTotalItems, setFamiliesTotalItems] = useState(0);
  const familiesPerPage = 5;
  
  // Estado para búsqueda de familias
  const [familiesSearchTerm, setFamiliesSearchTerm] = useState("");

  // Estados para búsqueda de familias en modal de subfamilias
  const [modalFamilySearchTerm, setModalFamilySearchTerm] = useState("");
  const [modalFilteredFamilies, setModalFilteredFamilies] = useState<Family[]>([]);
  const [modalSelectedFamilyName, setModalSelectedFamilyName] = useState("");
  const [modalShowFamilyDropdown, setModalShowFamilyDropdown] = useState(false);
  const modalFamilyDropdownRef = useRef<HTMLDivElement>(null);

  // Estados para filtro principal de subfamilias
  const familyDropdownRef = useRef<HTMLDivElement>(null);

  // Estados para búsqueda de categorías en modal de familias
  const [categorySearchTerm, setCategorySearchTerm] = useState("");
  const [filteredCategoriesForFamily, setFilteredCategoriesForFamily] = useState<Category[]>([]);
  const [selectedCategoryName, setSelectedCategoryName] = useState("");
  const [showCategoryDropdown, setShowCategoryDropdown] = useState(false);
  const categoryDropdownRef = useRef<HTMLDivElement>(null);

  // Estados para paginación de subfamilias
  const [subfamiliesCurrentPage, setSubfamiliesCurrentPage] = useState(1);
  const [subfamiliesTotalPages, setSubfamiliesTotalPages] = useState(1);
  const [subfamiliesTotalItems, setSubfamiliesTotalItems] = useState(0);
  const subfamiliesPerPage = 5;
  
  // Estados para búsqueda y filtrado de subfamilias
  const [subfamiliesSearchTerm, setSubfamiliesSearchTerm] = useState("");
  const [familyFilter, setFamilyFilter] = useState("all");
  
  // Estados para el buscador de familias en subfamilias
  const [familySearchTerm, setFamilySearchTerm] = useState("");
  const [allFamiliesForSubfamilies, setAllFamiliesForSubfamilies] = useState<Family[]>([]);
  const [filteredFamiliesForSubfamily, setFilteredFamiliesForSubfamily] = useState<Family[]>([]);
  const [selectedFamilyName, setSelectedFamilyName] = useState("Todas las familias");
  const [showFamilyDropdown, setShowFamilyDropdown] = useState(false);

  // Estados para modales de familias
  const [showFamilyModal, setShowFamilyModal] = useState(false);
  const [editingFamily, setEditingFamily] = useState<Family | null>(null);
  const [familyFormData, setFamilyFormData] = useState({
    name: "",
    description: "",
    category_id: "",
    image: null as File | null,
  });
  const [familyImageFile, setFamilyImageFile] = useState<File | null>(null);

  // Estados para modales de subfamilias
  const [showSubfamilyModal, setShowSubfamilyModal] = useState(false);
  const [editingSubfamily, setEditingSubfamily] = useState<Subfamily | null>(
    null
  );
  const [subfamilyFormData, setSubfamilyFormData] = useState({
    name: "",
    description: "",
    family_id: "",
  });
  
  // Estados separados para el buscador de familias en el modal de subfamilias (ya declarados arriba)

  // Estados para categorías
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState<Category | null>(null);
  // Extender categoryFormData para incluir home_featured_products
  const [categoryFormData, setCategoryFormData] = useState({
    name: "",
    description: "",
    image: null as File | null,
    category_id: "",
    home_featured_products: [] as string[],
    show_in_home: false,
  });
  const [productSearchTerm, setProductSearchTerm] = useState("");
  const [duplicateProductMessage, setDuplicateProductMessage] =
    useState<string>("");
  const [showSelectedProducts, setShowSelectedProducts] =
    useState<boolean>(false);
  const [tempSelectedProducts, setTempSelectedProducts] = useState<string[]>(
    []
  );

  // Agregar un nuevo estado para todos los productos
  const [allProducts, setAllProducts] = useState<Product[]>([]);
  const [loadingAllProducts, setLoadingAllProducts] = useState(false);

  // Estados para paginación del selector de productos
  const [productSelectorPage, setProductSelectorPage] = useState(1);
  const [productSelectorPerPage] = useState(10);

  // Estados para paginación del modal de ofertas del día
  const [dailyDealProductPage, setDailyDealProductPage] = useState(1);
  const [dailyDealProductPerPage] = useState(10);
  const [paginatedProductsForDailyDeal, setPaginatedProductsForDailyDeal] = useState<Product[]>([]);

  // Estados para slider
  const [sliderItems, setSliderItems] = useState<SliderItem[]>([]);
  const [showSliderModal, setShowSliderModal] = useState(false);
  const [editingSliderItem, setEditingSliderItem] = useState<SliderItem | null>(
    null
  );
  interface SliderFormData {
    title: string;
    description: string;
    link: string;
    active: boolean;
    category_id: string | null; // Categoría para contenedor 1
    container_products: {
      container2: string | null;
      container3: string | null;
      container4: string | null;
      container5: string | null;
    };
  }

  const [sliderFormData, setSliderFormData] = useState<SliderFormData>({
    title: "",
    description: "",
    link: "",
    active: true,
    category_id: null, // Categoría para contenedor 1
    container_products: {
      container2: null,
      container3: null,
      container4: null,
      container5: null,
    },
  });
  const [sliderImageFile, setSliderImageFile] = useState<File | null>(null);
  const [sliderMobileImageFile, setSliderMobileImageFile] = useState<File | null>(null);

  // Estados para Daily Deals
  const [dailyDeals, setDailyDeals] = useState<DailyDeal[]>([]);
  const [allDailyDeals, setAllDailyDeals] = useState<DailyDeal[]>([]);
  const [showDailyDealModal, setShowDailyDealModal] = useState(false);
  const [editingDailyDeal, setEditingDailyDeal] = useState<DailyDeal | null>(
    null
  );
  const [dailyDealFormData, setDailyDealFormData] = useState({
    product_id: "",
    active: true,
  });
  const [dailyDealSearchTerm, setDailyDealSearchTerm] = useState("");
  const [filteredProductsForDailyDeal, setFilteredProductsForDailyDeal] =
    useState<Product[]>([]);

  // Estados para paginación de Daily Deals
  const [dailyDealsCurrentPage, setDailyDealsCurrentPage] = useState(1);
  const [dailyDealsTotalPages, setDailyDealsTotalPages] = useState(1);
  const [dailyDealsTotalItems, setDailyDealsTotalItems] = useState(0);
  const dailyDealsPerPage = 5;







  // Tipos auxiliares para los objetos de búsqueda y resultados
  const sliderContainers = [
    "container2",
    "container3",
    "container4",
    "container5",
  ] as const;
  type SliderContainerKey = (typeof sliderContainers)[number];

  type SliderProductSearchState = Record<SliderContainerKey, string>;
  type SliderProductResultsState = Record<SliderContainerKey, Product[]>;

  // Estados para búsqueda de productos en cada contenedor del slider
  const [sliderProductSearch, setSliderProductSearch] =
    useState<SliderProductSearchState>({
      container2: "",
      container3: "",
      container4: "",
      container5: "",
    });
  const [sliderProductResults, setSliderProductResults] =
    useState<SliderProductResultsState>({
      container2: [],
      container3: [],
      container4: [],
      container5: [],
    });

  // useEffect para limpiar resultados cuando cambia la categoría
  useEffect(() => {
    // Limpiar los resultados de búsqueda cuando cambia la categoría
    setSliderProductResults({
      container2: [],
      container3: [],
      container4: [],
      container5: [],
    });
    // También limpiar los términos de búsqueda
    setSliderProductSearch({
      container2: '',
      container3: '',
      container4: '',
      container5: '',
    });
  }, [sliderFormData.category_id]);

  // useEffect para manejar el cierre del dropdown de familias al hacer clic fuera
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (familyDropdownRef.current && !familyDropdownRef.current.contains(event.target as Node)) {
        setShowFamilyDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // useEffect para manejar el cierre del dropdown de categorías al hacer clic fuera
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (categoryDropdownRef.current && !categoryDropdownRef.current.contains(event.target as Node)) {
        setShowCategoryDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Función para buscar productos por contenedor
  const handleSliderProductSearch = async (
    container: SliderContainerKey,
    value: string
  ) => {
    setSliderProductSearch((prev) => ({ ...prev, [container]: value }));
    if (value.length < 2) {
      setSliderProductResults((prev) => ({ ...prev, [container]: [] }));
      return;
    }
    
    try {
      // Obtener todos los productos para la búsqueda
      const allProductsData = await productService.getAllProducts();
      
      // Filtrar productos por texto de búsqueda
      let filtered = allProductsData.filter(
        (p) =>
          p.name.toLowerCase().includes(value.toLowerCase()) ||
          (p.description &&
            p.description.toLowerCase().includes(value.toLowerCase())) ||
          (p.brand && p.brand.toLowerCase().includes(value.toLowerCase())) ||
          (p.model && p.model.toLowerCase().includes(value.toLowerCase()))
      );
      
      // Si hay una categoría seleccionada en el contenedor 1, filtrar solo productos de esa categoría
      if (sliderFormData.category_id) {
        filtered = filtered.filter((p) => p.category_id === sliderFormData.category_id);
      }
      
      setSliderProductResults((prev) => ({ ...prev, [container]: filtered }));
    } catch (error) {
      console.error('Error al buscar productos para el slider:', error);
      setSliderProductResults((prev) => ({ ...prev, [container]: [] }));
    }
  };

  // Función para seleccionar producto
  const handleSelectSliderProduct = (
    container: SliderContainerKey,
    product: Product
  ) => {
    setSliderFormData((prev) => ({
      ...prev,
      container_products: {
        ...prev.container_products,
        [container]: product.id,
      },
    }));
    setSliderProductSearch((prev) => ({ ...prev, [container]: "" }));
    setSliderProductResults((prev) => ({ ...prev, [container]: [] }));
  };

  // Función para quitar producto seleccionado
  const handleRemoveSliderProduct = (container: SliderContainerKey) => {
    setSliderFormData((prev) => ({
      ...prev,
      container_products: {
        ...prev.container_products,
        [container]: null,
      },
    }));
  };

  // Agregar función para cargar configuraciones


  // Cargar datos al montar el componente
  useEffect(() => {
    fetchFamilies(undefined, 1, familiesSearchTerm); // Cargar familias con paginación desde la página 1
    // Cargar subfamilias usando el filtro de familia inicial
    const initialFamilyId = familyFilter === "all" ? undefined : familyFilter;
    fetchSubfamilies(initialFamilyId, 1, subfamiliesSearchTerm);
  }, []);

  // Efecto para manejar la búsqueda y filtrado de familias
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setFamiliesCurrentPage(1); // Resetear a la primera página
      fetchFamilies(getCurrentCategoryId(), 1, familiesSearchTerm);
    }, 300); // Debounce de 300ms

    return () => clearTimeout(timeoutId);
  }, [familiesSearchTerm, categoryFilter, categories]);

  // Efecto para manejar el cambio de filtro de familia en subfamilias
  useEffect(() => {
    setSubfamiliesCurrentPage(1); // Resetear a la primera página
    const familyId = familyFilter === "all" ? undefined : familyFilter;
    fetchSubfamilies(familyId, 1, subfamiliesSearchTerm);
  }, [familyFilter]);

  // Efecto para manejar la búsqueda y filtrado de subfamilias
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setSubfamiliesCurrentPage(1); // Resetear a la primera página
      const familyId = familyFilter === "all" ? undefined : familyFilter;
      fetchSubfamilies(familyId, 1, subfamiliesSearchTerm);
    }, 300); // Debounce de 300ms

    return () => clearTimeout(timeoutId);
  }, [subfamiliesSearchTerm]);

  // Efecto para cargar todas las familias independientemente para el filtro de subfamilias
  useEffect(() => {
    const fetchAllFamiliesForSubfamilies = async () => {
      try {
        const response = await familyService.getFamiliesWithPagination(1, 1000); // Obtener todas las familias
        setAllFamiliesForSubfamilies(response.families);
        setFilteredFamiliesForSubfamily(response.families.slice(0, 10)); // Mostrar las primeras 10
      } catch (error) {
        console.error("Error al cargar familias para subfamilias:", error);
        setAllFamiliesForSubfamilies([]);
        setFilteredFamiliesForSubfamily([]);
      }
    };

    fetchAllFamiliesForSubfamilies();
  }, []);

  // Efecto para filtrar familias en el buscador de subfamilias con debounce optimizado
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (familySearchTerm.trim() === "") {
        setFilteredFamiliesForSubfamily(allFamiliesForSubfamilies.slice(0, 10));
      } else {
        const searchLower = familySearchTerm.toLowerCase();
        const filtered = allFamiliesForSubfamilies.filter(family =>
          family.name.toLowerCase().includes(searchLower)
        ).slice(0, 10);
        setFilteredFamiliesForSubfamily(filtered);
      }
    }, 150); // Debounce reducido a 150ms para mejor responsividad

    return () => clearTimeout(timeoutId);
  }, [familySearchTerm, allFamiliesForSubfamilies]);

  // Efecto para manejar clics fuera del dropdown de familias
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (familyDropdownRef.current && !familyDropdownRef.current.contains(event.target as Node)) {
        setShowFamilyDropdown(false);
      }
    };

    if (showFamilyDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showFamilyDropdown]);

  // Efecto para actualizar el nombre de la familia seleccionada
  useEffect(() => {
    if (familyFilter === "all") {
      setSelectedFamilyName("Todas las familias");
      setFamilySearchTerm("");
    } else {
      const selectedFamily = allFamiliesForSubfamilies.find(family => family.id.toString() === familyFilter);
      if (selectedFamily) {
        setSelectedFamilyName(selectedFamily.name);
        setFamilySearchTerm(selectedFamily.name);
      }
    }
  }, [familyFilter, allFamiliesForSubfamilies]);

  // Efecto para cargar familias en el modal cuando se abra
  useEffect(() => {
    if (showSubfamilyModal && allFamiliesForSubfamilies.length > 0) {
      setModalFilteredFamilies(allFamiliesForSubfamilies.slice(0, 5));
    }
  }, [showSubfamilyModal, allFamiliesForSubfamilies]);

  // Función para cargar productos con paginación
  const fetchProducts = async (page: number = 1) => {
    try {
      setLoadingProducts(true);
      const response = await productService.getProducts(page, productsPerPage);
      setProducts(response.products);
      setCurrentPage(response.currentPage);
      setTotalPages(response.totalPages);
      setTotalProducts(response.total);
      setFilteredProducts(response.products);
    } catch (error) {
      console.error("Error al cargar productos:", error);
      toast.error("Error al cargar productos del servidor");
    } finally {
      setLoadingProducts(false);
    }
  };

  // Función para cargar familias con paginación
  const fetchFamilies = async (categoryId?: string, page: number = 1, searchTerm?: string) => {
    try {
      // Usar el servicio familyService con paginación
      const response = await familyService.getFamiliesWithPagination(page, familiesPerPage, categoryId, searchTerm);

      // Verificar que la respuesta tenga la estructura correcta
      if (!response || !response.families || !response.pagination) {
        throw new Error('Respuesta del servidor con estructura incorrecta');
      }

      // Actualizar el estado de todas las familias
      setFamilies(response.families);
      setFamiliesCurrentPage(response.pagination.currentPage);
      setFamiliesTotalPages(response.pagination.totalPages);
      setFamiliesTotalItems(response.pagination.totalItems);

      // Si se proporciona un categoryId, actualizar las familias filtradas
      if (categoryId) {
        setFilteredFamilies(response.families);
      } else {
        // Si no hay categoryId, limpiar las familias filtradas
        setFilteredFamilies([]);
      }

    } catch (error) {
      console.error("Error al cargar familias:", error);
      toast.error("Error al cargar familias del servidor");
      // En caso de error, resetear el estado a valores seguros
      setFilteredFamilies([]);
      setFamilies([]);
      setFamiliesTotalPages(1);
      setFamiliesTotalItems(0);
      setFamiliesCurrentPage(1);
    }
  };

  // Función auxiliar para obtener el categoryId basado en el filtro actual
  const getCurrentCategoryId = () => {
    if (categoryFilter !== "all") {
      const category = categories.find((cat) => cat.name === categoryFilter);
      return category?.id;
    }
    return undefined;
  };

  // Función para cambiar de página en familias
  const handleFamiliesPageChange = (newPage: number) => {
    setFamiliesCurrentPage(newPage);
    fetchFamilies(getCurrentCategoryId(), newPage, familiesSearchTerm);
  };

  // Función para manejar cambio de página de subfamilias
  const handleSubfamiliesPageChange = (newPage: number) => {
    setSubfamiliesCurrentPage(newPage);
    fetchSubfamilies(familyFilter === "all" ? undefined : familyFilter, newPage, subfamiliesSearchTerm);
  };

  // Función para obtener el ID de familia actual para filtrado
  const getCurrentFamilyId = () => {
    if (familyFilter === "all") return undefined;
    return familyFilter;
  };

  // Funciones para manejar el buscador de familias en subfamilias
  const handleFamilySelect = (family: Family | null) => {
    console.log('Familia seleccionada:', family);
    
    if (family) {
      console.log('Estableciendo filtro de familia a:', family.id.toString());
      setFamilyFilter(family.id.toString());
      setSelectedFamilyName(family.name);
      setFamilySearchTerm(family.name);
    } else {
      console.log('Estableciendo filtro de familia a: all');
      setFamilyFilter("all");
      setSelectedFamilyName("Todas las familias");
      setFamilySearchTerm("");
    }
    
    // Cerrar el dropdown después de un pequeño delay
    setTimeout(() => {
      setShowFamilyDropdown(false);
    }, 100);
  };

  const handleFamilySearchChange = (value: string) => {
    setFamilySearchTerm(value);
    setShowFamilyDropdown(true);
  };

  const handleFamilyInputFocus = () => {
    setShowFamilyDropdown(true);
  };

  const handleFamilyInputBlur = () => {
    setTimeout(() => setShowFamilyDropdown(false), 300);
  };

  // Función para cargar subfamilias con paginación, búsqueda y filtrado
  const fetchSubfamilies = async (familyId?: string, page: number = 1, searchTerm: string = "") => {
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: subfamiliesPerPage.toString(),
      });

      if (familyId && familyId !== "all") {
        params.append("family_id", familyId);
      }

      if (searchTerm.trim()) {
        params.append("search", searchTerm.trim());
      }

      const url = `${API_URL}/subfamilies?${params.toString()}`;

      const response = await fetch(url, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        cache: 'no-store',
      });

      if (!response.ok) {
        throw new Error(`Error HTTP: ${response.status}`);
      }

      const data = await response.json();
      
      if (data.subfamilies && data.pagination) {
        // Respuesta con paginación
        setSubfamilies(data.subfamilies);
        setSubfamiliesCurrentPage(data.pagination.currentPage);
        setSubfamiliesTotalPages(data.pagination.totalPages);
        setSubfamiliesTotalItems(data.pagination.totalItems);
      } else {
        // Respuesta sin paginación (fallback)
        const subfamiliesArray = Array.isArray(data) ? data : [];
        setSubfamilies(subfamiliesArray);
        setSubfamiliesTotalPages(1);
        setSubfamiliesCurrentPage(1);
        setSubfamiliesTotalItems(subfamiliesArray.length);
      }

      console.log("Subfamilias cargadas:", data);
    } catch (error) {
      console.error("Error al cargar subfamilias:", error);
      toast.error("Error al cargar subfamilias");
      setSubfamilies([]);
      setSubfamiliesTotalPages(1);
      setSubfamiliesCurrentPage(1);
      setSubfamiliesTotalItems(0);
    }
  };

  // Función para cargar todas las daily deals
  const fetchAllDailyDeals = async () => {
    try {
      const response = await fetch(`${API_URL}/daily-deals?limit=1000`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        cache: 'no-store',
      });

      if (!response.ok) {
        throw new Error(`Error HTTP: ${response.status}`);
      }

      const data = await response.json();
      
      if (data.dailyDeals) {
        setAllDailyDeals(data.dailyDeals);
      } else {
        const dailyDealsArray = Array.isArray(data) ? data : [];
        setAllDailyDeals(dailyDealsArray);
      }

      console.log("Todas las daily deals cargadas:", data);
    } catch (error) {
      console.error("Error al cargar daily deals:", error);
      toast.error("Error al cargar ofertas diarias");
      setAllDailyDeals([]);
    }
  };

  // Función para filtrar y paginar daily deals
  const getFilteredDailyDeals = () => {
    let filtered = allDailyDeals;

    // Aplicar filtro de búsqueda
    if (dailyDealSearchTerm.trim()) {
      filtered = filtered.filter((deal: any) => 
        deal.product && 
        deal.product.name && 
        deal.product.name.toLowerCase().includes(dailyDealSearchTerm.toLowerCase())
      );
    }

    return filtered;
  };

  // Función para obtener daily deals paginados
  const getPaginatedDailyDeals = () => {
    const filtered = getFilteredDailyDeals();
    const startIndex = (dailyDealsCurrentPage - 1) * dailyDealsPerPage;
    const endIndex = startIndex + dailyDealsPerPage;
    return filtered.slice(startIndex, endIndex);
  };

  // Función para cargar daily deals con paginación
  const fetchDailyDeals = async (page: number = 1) => {
    // Solo actualizar la página actual, el filtrado se hace localmente
    setDailyDealsCurrentPage(page);
  };

  // Efecto para actualizar la paginación cuando cambian los filtros
  useEffect(() => {
    const filtered = getFilteredDailyDeals();
    const totalPages = Math.ceil(filtered.length / dailyDealsPerPage);
    
    setDailyDeals(getPaginatedDailyDeals());
    setDailyDealsTotalPages(totalPages);
    setDailyDealsTotalItems(filtered.length);
    
    // Si la página actual es mayor que el total de páginas, ir a la primera página
    if (dailyDealsCurrentPage > totalPages && totalPages > 0) {
      setDailyDealsCurrentPage(1);
    }
  }, [allDailyDeals, dailyDealSearchTerm, dailyDealsCurrentPage, dailyDealsPerPage]);

  // Función para manejar cambio de página de daily deals
  const handleDailyDealsPageChange = (newPage: number) => {
    setDailyDealsCurrentPage(newPage);
    fetchDailyDeals(newPage);
  };

  // Función para ver detalles de un producto desde daily deals
  const handleViewProduct = (product: any) => {
    if (product) {
      changeActiveTab('products');
      console.log("Mostrando producto:", product);
      // Aquí podrías agregar lógica adicional para mostrar el producto específico
    }
  };

  // Función para cargar todos los productos de una categoría
  const loadAllProductsForCategory = async (categoryId: string) => {
    try {
      setLoadingAllProducts(true);
      // Cargar todos los productos sin paginación
      const response = await fetch(
        `${API_URL}/products?category=${categoryId}&limit=10000`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error("Error al cargar productos de la categoría");
      }

      const data = await response.json();
      setAllProducts(data.products || []);
    } catch (error) {
      console.error("Error al cargar productos de la categoría:", error);
      toast.error("Error al cargar productos de la categoría");
      setAllProducts([]);
    } finally {
      setLoadingAllProducts(false);
    }
  };

  // Función para cargar TODOS los productos para el modal de ofertas del día
  const loadAllProductsForDailyDeals = async () => {
    try {
      setLoadingAllProducts(true);
      // Cargar todos los productos sin filtros
      const response = await fetch(
        `${API_URL}/products?limit=5000`, // Aumentar el límite para obtener todos los productos
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error("Error al cargar todos los productos");
      }

      const data = await response.json();
      setAllProducts(data.products || []);
    } catch (error) {
      console.error("Error al cargar todos los productos:", error);
      toast.error("Error al cargar todos los productos");
      setAllProducts([]);
    } finally {
      setLoadingAllProducts(false);
    }
  };

  // Función para buscar productos con filtros en el servidor
  const searchProducts = async (page: number = 1) => {
    try {
      setLoadingProducts(true);

      // Construir parámetros de búsqueda
      const params = new URLSearchParams();
      params.append("page", page.toString());
      params.append("limit", productsPerPage.toString());

      if (searchTerm) {
        params.append("search", searchTerm);
      }

      if (categoryFilter !== "all") {
        // Encontrar el ID de la categoría por nombre
        const category = categories.find((cat) => cat.name === categoryFilter);
        if (category) {
          params.append("category", category.id);
        }
      }

      // Hacer la petición al servidor
      const response = await fetch(`${API_URL}/products?${params.toString()}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });

      if (!response.ok) {
        throw new Error("Error al buscar productos");
      }

      const data = await response.json();

      setProducts(data.products);
      setFilteredProducts(data.products);
      setCurrentPage(data.currentPage);
      setTotalPages(data.totalPages);
      setTotalProducts(data.total);
    } catch (error) {
      console.error("Error al buscar productos:", error);
      toast.error("Error al buscar productos");
    } finally {
      setLoadingProducts(false);
    }
  };

  // Efecto para buscar productos cuando cambian los filtros
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      searchProducts(1); // Reiniciar a la página 1 cuando cambian los filtros
    }, 500); // Debounce de 500ms

    return () => clearTimeout(timeoutId);
  }, [searchTerm, categoryFilter, categories]);

  // Función para manejar la subida de logos
  // Estados para archivos de logos pendientes
  const [pendingLogos, setPendingLogos] = useState<{
    desktop?: File;
    mobile?: File;
  }>({});

  const handleLogoUpload = (e: React.ChangeEvent<HTMLInputElement>, logoType: 'desktop' | 'mobile') => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Guardar el archivo temporalmente
    setPendingLogos(prev => ({
      ...prev,
      [logoType]: file
    }));

    // Crear una URL temporal para mostrar la vista previa
    const tempUrl = URL.createObjectURL(file);
    setLocalSettings(prev => ({
      ...prev,
      [logoType === 'desktop' ? 'desktop_logo' : 'mobile_logo']: tempUrl
    }));

    toast.info(`Logo ${logoType === 'desktop' ? 'de escritorio' : 'móvil'} seleccionado. Presiona "Guardar Configuraciones" para aplicar los cambios.`);
  };

  // Función para actualizar configuraciones
  const handleSettingsSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      
      // Primero subir los logos pendientes
      const updatedSettings = { ...localSettings };
      
      // Subir logo de escritorio si hay uno pendiente
      if (pendingLogos.desktop) {
        const formData = new FormData();
        formData.append('logo', pendingLogos.desktop);
        formData.append('type', 'desktop');

        const response = await fetch(`${API_URL}/settings/upload-logo`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
          body: formData
        });

        if (!response.ok) {
          throw new Error('Error al subir el logo de escritorio');
        }

        const result = await response.json();
        updatedSettings.desktop_logo = result.logoUrl;
      }
      
      // Subir logo móvil si hay uno pendiente
      if (pendingLogos.mobile) {
        const formData = new FormData();
        formData.append('logo', pendingLogos.mobile);
        formData.append('type', 'mobile');

        const response = await fetch(`${API_URL}/settings/upload-logo`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
          body: formData
        });

        if (!response.ok) {
          throw new Error('Error al subir el logo móvil');
        }

        const result = await response.json();
        updatedSettings.mobile_logo = result.logoUrl;
      }
      
      // Actualizar configuraciones con las nuevas URLs usando el contexto
      await updateSettingsContext(updatedSettings);
      
      // Limpiar logos pendientes
      setPendingLogos({});
      
      // Actualizar el estado local con las URLs finales
      setLocalSettings(updatedSettings);
      
      toast.success("Configuraciones actualizadas exitosamente");
    } catch (error) {
      console.error("Error updating settings:", error);
      toast.error("Error al actualizar configuraciones");
    }
  };

  // Cargar datos iniciales
  useEffect(() => {
    const fetchData = async () => {
      try {
        // Cargar categorías primero
        const categoriesData = await categoryService.getCategories();
        setCategories(categoriesData);

        // Las familias se cargan con paginación en el otro useEffect

        // Cargar subfamilias
        const subfamiliesData = await subfamilyService.getSubfamilies();
        setSubfamilies(subfamiliesData);

        // Cargar usuarios solo si tiene permiso
        let usersData: any[] = [];
        if (hasPermission('users')) {
          usersData = await userService.getUsers();
        }

        // Cargar elementos del slider
        const sliderData = await sliderService.getSliderItems();
        setSliderItems(sliderData);

        // Cargar todas las ofertas diarias
        await fetchAllDailyDeals();

        // Cargar todos los productos para el modal de ofertas del día
        await loadAllProductsForDailyDeals();

        // Actualizar estadísticas (obtener el total real)
        const allProductsResponse = await productService.getAllProducts();
        setStats({
          totalProducts: allProductsResponse.length,
          totalUsers: hasPermission('users') ? usersData.length : 0,
        });
      } catch (error) {
        console.error("Error al cargar datos:", error);
        toast.error("Error al cargar datos del servidor");
      }
    };

    fetchData();
  }, []);

  // Cargar productos después de que las categorías estén disponibles
  useEffect(() => {
    if (categories.length > 0) {
      searchProducts(1);
    }
  }, [categories]);

  // Inicializar productos filtrados para daily deals
  useEffect(() => {
    if (allProducts.length > 0) {
      setFilteredProductsForDailyDeal(allProducts); // Mostrar todos los productos disponibles
      // Mostrar solo los primeros 10 productos inicialmente
      setPaginatedProductsForDailyDeal(allProducts.slice(0, dailyDealProductPerPage));
    }
  }, [allProducts]);

  // Efecto para manejar la paginación del modal de ofertas del día
  useEffect(() => {
    const startIndex = (dailyDealProductPage - 1) * dailyDealProductPerPage;
    const endIndex = startIndex + dailyDealProductPerPage;
    setPaginatedProductsForDailyDeal(filteredProductsForDailyDeal.slice(startIndex, endIndex));
  }, [dailyDealProductPage, filteredProductsForDailyDeal, dailyDealProductPerPage]);



  // Cargar productos seleccionados cuando se edita una categoría
  useEffect(() => {
    if (
      editingCategory &&
      "home_featured_products" in editingCategory &&
      editingCategory.home_featured_products &&
      Array.isArray(editingCategory.home_featured_products)
    ) {
      setTempSelectedProducts([...editingCategory.home_featured_products]);
    } else {
      setTempSelectedProducts([]);
    }
    // Resetear la página del selector cuando se abre el modal
    setProductSelectorPage(1);
  }, [editingCategory]);

  // COMENTAR O ELIMINAR este useEffect si no quieres que se resetee la página al buscar
  /*
  useEffect(() => {
    setProductSelectorPage(1);
  }, [productSearchTerm]);
  */

  // Efecto para manejar el responsive del sidebar
  useEffect(() => {
    const handleResize = () => {
      setSidebarOpen(window.innerWidth > 768);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Manejar cambio de categoría
  const handleCategoryChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const categoryId = e.target.value;

    setFormData((prev) => ({
      ...prev,
      category_id: categoryId,
      family_id: "",
      subfamily_id: "",
    }));

    if (categoryId) {
      fetchFamilies(categoryId, 1, familiesSearchTerm);
    } else {
      setFilteredFamilies([]);
      setFilteredSubfamilies([]);
    }
  };

  // Manejar cambio de familia
  const handleFamilyChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const familyId = e.target.value;
    setFormData((prev) => ({
      ...prev,
      family_id: familyId,
      subfamily_id: "",
    }));

    if (familyId) {
      fetchSubfamilies(familyId);
    } else {
      setFilteredSubfamilies([]);
    }
  };

  // Manejadores de eventos
  const handleAddProduct = () => {
    setEditingProduct(null);
    setFormData({
      name: "",
      description: "",
      image_url: "",
      category_id: "",
      family_id: "",
      subfamily_id: "",
      stock: 0,
      featured: false,
    });
    setImageFile(null);
    setAdditionalImageFiles([]);
    setFilteredFamilies([]);
    setFilteredSubfamilies([]);
    setShowModal(true);
  };

  const handleEditProduct = (product: Product) => {
    setEditingProduct(product);
    
    console.log('Editando producto:', {
      name: product.name,
      category_id: product.category_id,
      family_id: product.family_id,
      subfamily_id: product.subfamily_id
    });
    
    setFormData({
      name: product.name,
      description: product.description || "",
      image_url: product.image_url || "",
      category_id: String(product.category_id),
      family_id: product.family_id ? String(product.family_id) : "",
      subfamily_id: product.subfamily_id ? String(product.subfamily_id) : "",
      brand: product.brand || "",
      model: product.model || "",
      stock: product.stock,
      featured: product.featured,
    });
    setImageFile(null);
    setAdditionalImageFiles([]);

    // Cargar familias y subfamilias para el producto editado
    if (product.category_id) {
      fetchFamilies(product.category_id, 1, familiesSearchTerm);
    }
    if (product.family_id) {
      fetchSubfamilies(product.family_id);
    }

    setShowModal(true);
  };

  const handleDeleteProduct = async (id: string) => {
    const confirmed = await showConfirm("¿Estás seguro de que deseas eliminar este producto?");
    if (confirmed) {
      try {
        await productService.deleteProduct(id);
        toast.success("Producto eliminado con éxito");
        // Refrescar la búsqueda para mantener la paginación correcta
        searchProducts(currentPage);
      } catch (error) {
        console.error("Error al eliminar producto:", error);
        toast.error("Error al eliminar el producto");
      }
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      // Debug: Verificar los valores antes de enviar
      console.log('Datos del formulario antes de enviar:', {
        name: formData.name,
        category_id: formData.category_id,
        family_id: formData.family_id,
        subfamily_id: formData.subfamily_id
      });

      const formDataObj = new FormData();
      formDataObj.append("name", formData.name);
      formDataObj.append("description", formData.description);
      formDataObj.append("stock", formData.stock.toString());
      formDataObj.append("category_id", formData.category_id);
      formDataObj.append("family_id", formData.family_id || "");
      formDataObj.append("subfamily_id", formData.subfamily_id || "");
      formDataObj.append("featured", formData.featured.toString());

      if (formData.brand) formDataObj.append("brand", formData.brand);
      if (formData.model) formDataObj.append("model", formData.model);

      if (imageFile) {
        formDataObj.append("image", imageFile);
      }

      if (additionalImageFiles.length > 0) {
        additionalImageFiles.forEach((file) => {
          formDataObj.append("additional_images", file);
        });
      }

      let updatedProduct;
      if (editingProduct) {
        updatedProduct = await productService.updateProduct(
          editingProduct.id,
          formDataObj
        );
        toast.success("Producto actualizado correctamente");
      } else {
        updatedProduct = await productService.createProduct(formDataObj);
        toast.success("Producto creado correctamente");
      }

      setShowModal(false);
      searchProducts(currentPage);
    } catch (error) {
      console.error("Error al guardar producto:", error);
      toast.error("Error al guardar el producto");
    }
  };

  const handleInputChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    const { name, value, type } = e.target;

    if (name === "category_id") {
      handleCategoryChange(e as React.ChangeEvent<HTMLSelectElement>);
      return;
    }

    if (name === "family_id") {
      handleFamilyChange(e as React.ChangeEvent<HTMLSelectElement>);
      return;
    }

    if (type === "checkbox") {
      const checked = (e.target as HTMLInputElement).checked;
      setFormData((prev) => ({ ...prev, [name]: checked }));
    } else if (type === "file") {
      const files = (e.target as HTMLInputElement).files;
      if (name === "image" && files && files[0]) {
        setImageFile(files[0]);
      } else if (name === "additional_images" && files) {
        setAdditionalImageFiles(Array.from(files));
      }
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  // Funciones para el manejo de categorías
  const handleAddCategory = () => {
    setEditingCategory(null);
    setCategoryFormData({
      name: "",
      description: "",
      image: null,
      home_featured_products: [],
      show_in_home: false,
      category_id: "",
    });
    // Resetear la página del selector
    setProductSelectorPage(1);
    setShowCategoryModal(true);
  };

  const handleEditCategory = (category: Category) => {
    setEditingCategory(category);
    setCategoryFormData({
      name: category.name,
      description: category.description || "",
      image: null,
      home_featured_products:
        "home_featured_products" in category &&
          Array.isArray(category.home_featured_products)
          ? category.home_featured_products
          : [],
      show_in_home:
        "show_in_home" in category && typeof category.show_in_home === "boolean"
          ? category.show_in_home
          : false,
      category_id: category.id,
    });
    // Convertir todos los IDs a string para consistencia
    const featuredProductsAsStrings =
      "home_featured_products" in category &&
        Array.isArray(category.home_featured_products)
        ? category.home_featured_products.map((id) => String(id))
        : [];
    setTempSelectedProducts(featuredProductsAsStrings);

    // Cargar todos los productos de esta categoría
    loadAllProductsForCategory(category.id);

    // Resetear la página del selector
    setProductSelectorPage(1);

    setShowCategoryModal(true);
  };

  const handleCategoryInputChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    const { name, value, type } = e.target;
    if (type === "file") {
      const fileInput = e.target as HTMLInputElement;
      const file = fileInput.files?.[0] || null;
      setCategoryFormData((prev) => ({ ...prev, image: file }));
    } else if (name === "home_featured_products") {
      // Selector múltiple para productos destacados
      const selectElement = e.target as HTMLSelectElement;
      const selected: string[] = [];
      for (let i = 0; i < selectElement.options.length; i++) {
        if (selectElement.options[i].selected)
          selected.push(selectElement.options[i].value);
      }
      setCategoryFormData((prev) => ({ ...prev, [name]: selected }));
    } else if (type === "checkbox") {
      setCategoryFormData((prev) => ({
        ...prev,
        [name]: (e.target as HTMLInputElement).checked,
      }));
    } else {
      setCategoryFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleCategorySubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const formDataObj = new FormData();
      formDataObj.append("name", categoryFormData.name);
      formDataObj.append("description", categoryFormData.description);
      if (categoryFormData.image) {
        formDataObj.append("image", categoryFormData.image);
      }
      // Agregar productos destacados como array serializado usando tempSelectedProducts
      if (tempSelectedProducts && tempSelectedProducts.length > 0) {
        formDataObj.append(
          "home_featured_products",
          JSON.stringify(tempSelectedProducts)
        );
      } else {
        formDataObj.append("home_featured_products", JSON.stringify([]));
      }
      // Agregar visibilidad en home como string
      formDataObj.append(
        "show_in_home",
        categoryFormData.show_in_home ? "true" : "false"
      );
      if (editingCategory) {
        const updatedCategory = await categoryService.updateCategory(
          editingCategory.id,
          formDataObj
        );
        setCategories((prev) =>
          prev.map((cat) =>
            cat.id === editingCategory.id ? updatedCategory : cat
          )
        );
        toast.success("Categoría actualizada correctamente");
      } else {
        const newCategory = await categoryService.createCategory(formDataObj);
        setCategories((prev) => [...prev, newCategory]);
        toast.success("Categoría creada correctamente");
      }
      setShowCategoryModal(false);
    } catch (error) {
      console.error("Error al guardar categoría:", error);
      toast.error("Error al guardar la categoría");
    }
  };

  const handleDeleteCategory = async (categoryId: string) => {
    const confirmed = await showConfirm("¿Estás seguro de que deseas eliminar esta categoría?");
    if (confirmed) {
      try {
        await categoryService.deleteCategory(categoryId);
        toast.success("Categoría eliminada correctamente");

        // Eliminar la categoría de la lista
        setCategories((prev) => prev.filter((cat) => cat.id !== categoryId));
      } catch (error) {
        console.error("Error al eliminar categoría:", error);
        toast.error("Error al eliminar la categoría");
      }
    }
  };

  // Funciones para el manejo de familias
  const handleAddFamily = () => {
    setEditingFamily(null);
    setFamilyFormData({
      name: "",
      description: "",
      category_id: "",
      image: null,
    });
    setFamilyImageFile(null);
    // Limpiar estados de búsqueda de categorías
    setCategorySearchTerm("");
    setSelectedCategoryName("");
    setFilteredCategoriesForFamily([]);
    setShowCategoryDropdown(false);
    setShowFamilyModal(true);
  };

  const handleEditFamily = (family: Family) => {
    setEditingFamily(family);
    setFamilyFormData({
      name: family.name,
      description: family.description || "",
      category_id: family.category_id,
      image: null,
    });
    setFamilyImageFile(null);
    // Inicializar estados de búsqueda de categorías con la categoría actual
    const currentCategory = categories.find(cat => cat.id === family.category_id);
    setCategorySearchTerm(currentCategory ? currentCategory.name : "");
    setSelectedCategoryName(currentCategory ? currentCategory.name : "");
    setFilteredCategoriesForFamily([]);
    setShowCategoryDropdown(false);
    setShowFamilyModal(true);
  };

  const handleFamilyInputChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    const { name, value, type } = e.target as HTMLInputElement;

    if (type === "file") {
      const files = (e.target as HTMLInputElement).files;
      if (files && files.length > 0) {
        setFamilyImageFile(files[0]);
        setFamilyFormData((prev) => ({ ...prev, image: files[0] }));
      }
    } else {
      setFamilyFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  // Función para manejar la búsqueda de categorías en el modal de familias
  const handleCategorySearch = (searchTerm: string) => {
    setCategorySearchTerm(searchTerm);
    
    if (searchTerm.trim() === "") {
      setFilteredCategoriesForFamily([]);
      setShowCategoryDropdown(false);
      return;
    }
    
    const filtered = categories.filter(category => 
      category.name.toLowerCase().includes(searchTerm.toLowerCase())
    );
    
    setFilteredCategoriesForFamily(filtered);
    setShowCategoryDropdown(true);
  };

  // Función para seleccionar una categoría
  const handleCategorySelect = (category: Category) => {
    setFamilyFormData(prev => ({ ...prev, category_id: category.id }));
    setSelectedCategoryName(category.name);
    setCategorySearchTerm(category.name);
    setShowCategoryDropdown(false);
  };

  // Función para limpiar la selección de categoría
  const handleClearCategorySelection = () => {
    setFamilyFormData(prev => ({ ...prev, category_id: "" }));
    setSelectedCategoryName("");
    setCategorySearchTerm("");
    setFilteredCategoriesForFamily([]);
    setShowCategoryDropdown(false);
  };

  const handleFamilySubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      const formData = new FormData();
      formData.append("name", familyFormData.name);
      formData.append("description", familyFormData.description);
      formData.append("category_id", familyFormData.category_id);

      if (familyImageFile) {
        formData.append("image", familyImageFile);
      }

      if (editingFamily) {
        await familyService.updateFamily(
          editingFamily.id,
          formData
        );
        toast.success("Familia actualizada correctamente");
        // Recargar la página actual de familias
        fetchFamilies(getCurrentCategoryId(), familiesCurrentPage, familiesSearchTerm);
      } else {
        await familyService.createFamily(formData);
        toast.success("Familia creada correctamente");
        // Recargar desde la primera página para ver la nueva familia
        fetchFamilies(getCurrentCategoryId(), 1, familiesSearchTerm);
        setFamiliesCurrentPage(1);
      }

      setShowFamilyModal(false);
    } catch (error) {
      console.error("Error al guardar familia:", error);
      toast.error("Error al guardar la familia");
    }
  };

  const handleDeleteFamily = async (familyId: string) => {
    if (window.confirm("¿Estás seguro de que deseas eliminar esta familia?")) {
      try {
        await familyService.deleteFamily(familyId);
        toast.success("Familia eliminada correctamente");

        // Verificar si después de eliminar la página actual quedaría vacía
        const remainingItemsInPage = families.length - 1;
        let targetPage = familiesCurrentPage;
        
        // Si la página actual quedaría vacía y no es la primera página, ir a la anterior
        if (remainingItemsInPage === 0 && familiesCurrentPage > 1) {
          targetPage = familiesCurrentPage - 1;
          setFamiliesCurrentPage(targetPage);
        }
        
        // Recargar las familias
        fetchFamilies(getCurrentCategoryId(), targetPage, familiesSearchTerm);
      } catch (error) {
        console.error("Error al eliminar familia:", error);
        toast.error("Error al eliminar la familia");
      }
    }
  };

  // Funciones para el manejo de subfamilias
  const handleAddSubfamily = () => {
    setEditingSubfamily(null);
    setSubfamilyFormData({
      name: "",
      description: "",
      family_id: "",
    });
    // Limpiar estados de búsqueda de familias
    setFamilySearchTerm("");
    setSelectedFamilyName("");
    setFilteredFamiliesForSubfamily([]);
    setShowFamilyDropdown(false);
    setShowSubfamilyModal(true);
  };

  const handleEditSubfamily = (subfamily: Subfamily) => {
    // Crear una copia profunda del objeto subfamily para evitar problemas de referencia
    setEditingSubfamily({ ...subfamily });

    // Configurar el formulario con los datos actuales
    setSubfamilyFormData({
      name: subfamily.name,
      description: subfamily.description || "",
      family_id: subfamily.family_id,
    });

    // Inicializar estados de búsqueda de familias con la familia actual
    const currentFamily = families.find(fam => fam.id === subfamily.family_id);
    setFamilySearchTerm(currentFamily ? currentFamily.name : "");
    setSelectedFamilyName(currentFamily ? currentFamily.name : "");
    setFilteredFamiliesForSubfamily([]);
    setShowFamilyDropdown(false);

    // Mostrar el modal
    setShowSubfamilyModal(true);
  };

  const handleSubfamilyInputChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    const { name, value } = e.target;
    setSubfamilyFormData((prev) => ({ ...prev, [name]: value }));
  };

  // Función para manejar la búsqueda de familias en el modal de subfamilias
  const handleModalFamilySearch = (searchTerm: string) => {
    setModalFamilySearchTerm(searchTerm);
    
    if (searchTerm.trim() === "") {
      setModalFilteredFamilies(allFamiliesForSubfamilies.slice(0, 5));
      setModalShowFamilyDropdown(true);
      return;
    }
    
    const filtered = allFamiliesForSubfamilies.filter(family => 
      family.name.toLowerCase().includes(searchTerm.toLowerCase())
    ).slice(0, 5);
    
    setModalFilteredFamilies(filtered);
    setModalShowFamilyDropdown(true);
  };

  // Función para seleccionar una familia en el modal
  const handleFamilySelectForModal = (family: Family) => {
    setSubfamilyFormData(prev => ({ ...prev, family_id: family.id }));
    setModalSelectedFamilyName(family.name);
    setModalFamilySearchTerm(family.name);
    setModalShowFamilyDropdown(false);
  };

  // Función para limpiar la selección de familia
  const handleClearFamilySelection = () => {
    setSubfamilyFormData(prev => ({ ...prev, family_id: "" }));
    setModalSelectedFamilyName("");
    setModalFamilySearchTerm("");
    setModalFilteredFamilies([]);
    setModalShowFamilyDropdown(false);
  };

  const handleSubfamilySubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      const subfamilyData = {
        name: subfamilyFormData.name,
        description: subfamilyFormData.description,
        family_id: subfamilyFormData.family_id,
      };

      if (editingSubfamily) {
        const updatedSubfamily = await subfamilyService.updateSubfamily(
          editingSubfamily.id,
          subfamilyData
        );
        // Actualizar el estado con la subfamilia actualizada
        setSubfamilies((prev) =>
          prev.map((subfamily) =>
            subfamily.id === editingSubfamily.id ? updatedSubfamily : subfamily
          )
        );
        toast.success("Subfamilia actualizada correctamente");
      } else {
        const newSubfamily = await subfamilyService.createSubfamily(subfamilyData);
        setSubfamilies((prev) => [...prev, newSubfamily]);
        toast.success("Subfamilia creada correctamente");
      }

      // Cerrar el modal después de la actualización
      setShowSubfamilyModal(false);

      // Recargar las subfamilias sin filtro para mostrar todas las subfamilias
      fetchSubfamilies(undefined, subfamiliesCurrentPage, subfamiliesSearchTerm);
    } catch (error) {
      console.error("Error al guardar subfamilia:", error);
      toast.error("Error al guardar la subfamilia");
    }
  };

  const handleDeleteSubfamily = async (subfamilyId: string) => {
    if (
      window.confirm("¿Estás seguro de que deseas eliminar esta subfamilia?")
    ) {
      try {
        await subfamilyService.deleteSubfamily(subfamilyId);
        toast.success("Subfamilia eliminada correctamente");

        // Calcular si necesitamos cambiar de página después de eliminar
        const remainingItemsInPage = subfamilies.length - 1;
        let targetPage = subfamiliesCurrentPage;
        
        // Si no quedan elementos en la página actual y no estamos en la primera página,
        // ir a la página anterior
        if (remainingItemsInPage === 0 && subfamiliesCurrentPage > 1) {
          targetPage = subfamiliesCurrentPage - 1;
          setSubfamiliesCurrentPage(targetPage);
        }
        
        // Recargar las subfamilias sin filtro
        fetchSubfamilies(undefined, targetPage, subfamiliesSearchTerm);
      } catch (error) {
        console.error("Error al eliminar subfamilia:", error);
        toast.error("Error al eliminar la subfamilia");
      }
    }
  };

  // Funciones para el manejo del slider
  const handleAddSliderItem = async () => {
    setEditingSliderItem(null);
    setSliderFormData({
      title: "",
      description: "",
      link: "",
      active: true,
      category_id: null, // Categoría para contenedor 1
      container_products: {
        container2: null,
        container3: null,
        container4: null,
        container5: null,
      },
    });
    setSliderImageFile(null);
    setSliderMobileImageFile(null);
    
    // Cargar todos los productos para el slider
    try {
      setLoadingAllProducts(true);
      const allProductsData = await productService.getAllProducts();
      setAllProducts(allProductsData);
    } catch (error) {
      console.error("Error al cargar productos:", error);
      toast.error("Error al cargar productos");
    } finally {
      setLoadingAllProducts(false);
    }
    
    setShowSliderModal(true);
  };

  const handleEditSliderItem = async (item: SliderItem) => {
    setEditingSliderItem(item);
    setSliderFormData({
      title: item.title,
      description: item.description,
      link: item.link_url || "",
      active: item.active,
      category_id: item.category_id || null, // Categoría para contenedor 1
      container_products: {
        container2: item.container_products?.container2 || null,
        container3: item.container_products?.container3 || null,
        container4: item.container_products?.container4 || null,
        container5: item.container_products?.container5 || null,
      },
    });
    setSliderImageFile(null);
    setSliderMobileImageFile(null);
    
    // Cargar todos los productos para el slider
    try {
      setLoadingAllProducts(true);
      const allProductsData = await productService.getAllProducts();
      setAllProducts(allProductsData);
    } catch (error) {
      console.error("Error al cargar productos:", error);
      toast.error("Error al cargar productos");
    } finally {
      setLoadingAllProducts(false);
    }
    
    setShowSliderModal(true);
  };

  const handleSliderInputChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    const { name, value, type } = e.target as HTMLInputElement;

    if (type === "checkbox") {
      const { checked } = e.target as HTMLInputElement;
      setSliderFormData((prev) => ({ ...prev, active: checked }));
    } else if (type === "file") {
      const files = (e.target as HTMLInputElement).files;
      if (files && files.length > 0) {
        if (name === "mobile_image") {
          setSliderMobileImageFile(files[0]);
        } else {
          setSliderImageFile(files[0]);
        }
      }
    } else if (name.startsWith("container_products.")) {
      const containerKey = name.split(".")[1];
      setSliderFormData((prev) => ({
        ...prev,
        container_products: {
          ...prev.container_products,
          [containerKey]: value || null,
        },
      }));
    } else {
      setSliderFormData((prev) => ({ ...prev, [name]: value }));
      
      // Si se cambió la categoría, limpiar los resultados de búsqueda de productos
      // para forzar una nueva búsqueda con el filtro de categoría aplicado
      if (name === 'category_id') {
        setSliderProductResults({
          container2: [],
          container3: [],
          container4: [],
          container5: [],
        });
        // También limpiar los términos de búsqueda para que el usuario vuelva a buscar
        setSliderProductSearch({
          container2: '',
          container3: '',
          container4: '',
          container5: '',
        });
      }
    }
  };

  const handleSliderSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      if (!sliderImageFile && !editingSliderItem) {
        toast.error("Debes seleccionar una imagen para el slider");
        return;
      }

      const data = {
        title: sliderFormData.title,
        description: sliderFormData.description,
        link: sliderFormData.link,
        active: sliderFormData.active,
        category_id: sliderFormData.category_id, // Categoría para contenedor 1
        container_products: sliderFormData.container_products,
      };

      let updatedItem: SliderItem;

      if (editingSliderItem && editingSliderItem.id) {
        const position = editingSliderItem.position || parseInt(editingSliderItem.id) || 0;
        const updateData = {
          ...data,
          position: position,
          image: sliderImageFile || undefined,
          mobile_image: sliderMobileImageFile || undefined,
        };

        updatedItem = await sliderService.updateSliderItem(
          editingSliderItem.id,
          updateData
        );

        setSliderItems(
          sliderItems.map((item) =>
            item.id === editingSliderItem.id ? updatedItem : item
          )
        );
        toast.success("Elemento del slider actualizado con éxito");
      } else {
        const createData = {
          ...data,
          image: sliderImageFile as File,
          mobile_image: sliderMobileImageFile || undefined,
        };

        updatedItem = await sliderService.createSliderItem(createData);
        setSliderItems([...sliderItems, updatedItem]);
        toast.success("Elemento del slider agregado con éxito");
      }

      setShowSliderModal(false);
    } catch (error) {
      console.error("Error al guardar elemento del slider:", error);
      toast.error("Error al guardar el elemento del slider");
    }
  };

  const handleDeleteSliderItem = async (id: string) => {

    const confirmed = await showConfirm("¿Estás seguro de que deseas eliminar este elemento del slider?");
    if (confirmed) {

      if (
        window.confirm(
          "¿Estás seguro de que deseas eliminar este elemento del slider?"
        )
      ) {

        try {
          await sliderService.deleteSliderItem(id);
          setSliderItems(sliderItems.filter((item) => item.id !== id));
          toast.success("Elemento del slider eliminado con éxito");
        } catch (error) {
          console.error("Error al eliminar elemento del slider:", error);
          toast.error("Error al eliminar el elemento del slider");
        }
      }
    };
  };

    // Manejadores para Daily Deals
    const handleAddDailyDeal = () => {
      setEditingDailyDeal(null);
      setDailyDealFormData({
        product_id: "",
        active: true,
      });
      setProductSearchTerm("");
      setDailyDealProductPage(1); // Resetear paginación
      setShowDailyDealModal(true);
    };

    const handleEditDailyDeal = (deal: DailyDeal) => {
      setEditingDailyDeal(deal);
      setDailyDealFormData({
        product_id: deal.product_id,
        active: deal.active,
      });
      setProductSearchTerm("");
      setDailyDealProductPage(1); // Resetear paginación
      setShowDailyDealModal(true);
    };

    const handleDeleteDailyDeal = async (id: string) => {
      const confirmed = await showConfirm("¿Estás seguro de que deseas eliminar esta oferta?");
      if (confirmed) {
        try {
          await dailyDealService.deleteDailyDeal(id);
          setDailyDeals(dailyDeals.filter((deal) => deal.id !== id));
          toast.success("Oferta eliminada con éxito");
        } catch (error) {
          console.error("Error al eliminar oferta:", error);
          toast.error(
            error instanceof Error ? error.message : "Error al eliminar la oferta"
          );
        }
      }
    };

    const handleDailyDealSubmit = async (e: React.FormEvent) => {
      e.preventDefault();
      try {
        if (!dailyDealFormData.product_id) {
          toast.error("Por favor, seleccione un producto");
          return;
        }

        const submitData = {
          product_id: dailyDealFormData.product_id,
          active: dailyDealFormData.active,
        };

        if (editingDailyDeal) {
          const updatedDeal = await dailyDealService.updateDailyDeal(
            editingDailyDeal.id,
            submitData
          );
          if (!updatedDeal.product) {
            throw new Error("No se pudo obtener la información del producto");
          }
          setDailyDeals(
            dailyDeals.map((deal) =>
              deal.id === editingDailyDeal.id ? updatedDeal : deal
            )
          );
          toast.success("Oferta actualizada con éxito");
        } else {
          const newDeal = await dailyDealService.createDailyDeal(submitData);
          if (!newDeal.product) {
            throw new Error("No se pudo obtener la información del producto");
          }
          setDailyDeals([...dailyDeals, newDeal]);
          toast.success("Oferta agregada con éxito");
        }

        setShowDailyDealModal(false);
        setProductSearchTerm("");
        setDailyDealProductPage(1); // Resetear paginación
        setDailyDealFormData({
          product_id: "",
          active: true,
        });
      } catch (error) {
        console.error("Error al procesar oferta:", error);
        toast.error(
          error instanceof Error ? error.message : "Error al procesar la oferta"
        );
      }
    };

    const handleDailyDealInputChange = (
      e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
    ) => {
      const { name, value, type } = e.target;
      if (type === "checkbox") {
        const { checked } = e.target as HTMLInputElement;
        setDailyDealFormData((prev) => ({ ...prev, [name]: checked }));
      } else {
        setDailyDealFormData((prev) => ({ ...prev, [name]: value }));
      }
    };

    // Función para buscar productos en daily deals
    const handleDailyDealProductSearch = (searchTerm: string) => {
      setProductSearchTerm(searchTerm); // Usar la nueva variable separada
      setDailyDealProductPage(1); // Resetear a la primera página al buscar
      
      let filtered;
      if (searchTerm.trim() === "") {
        filtered = allProducts; // Usar allProducts en lugar de products
      } else {
        filtered = allProducts.filter(
          (product) =>
            product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            product.description
              .toLowerCase()
              .includes(searchTerm.toLowerCase()) ||
            (product.brand &&
              product.brand.toLowerCase().includes(searchTerm.toLowerCase())) ||
            (product.model &&
              product.model.toLowerCase().includes(searchTerm.toLowerCase()))
        );
      }
      
      setFilteredProductsForDailyDeal(filtered);
      
      // Aplicar paginación
      const startIndex = 0; // Siempre empezar desde la primera página
      const endIndex = dailyDealProductPerPage;
      setPaginatedProductsForDailyDeal(filtered.slice(startIndex, endIndex));
    };

    // Agregar función para seleccionar producto:
    const handleSelectProductForDailyDeal = (productId: string) => {
      setDailyDealFormData((prev) => ({ ...prev, product_id: productId }));
    };

    // Función para cambiar de página
    const handlePageChange = (page: number) => {
      if (page >= 1 && page <= totalPages) {
        searchProducts(page);
      }
    };

    // Función para generar números de página con puntos suspensivos
    const getPageNumbers = (currentPage: number, totalPages: number) => {
      const pageNumbers = [];
      const maxVisiblePages = 5;
      
      if (totalPages <= maxVisiblePages) {
        for (let i = 1; i <= totalPages; i++) {
          pageNumbers.push(i);
        }
      } else {
        if (currentPage <= 3) {
          for (let i = 1; i <= 4; i++) {
            pageNumbers.push(i);
          }
          pageNumbers.push('...');
          pageNumbers.push(totalPages);
        } else if (currentPage >= totalPages - 2) {
          pageNumbers.push(1);
          pageNumbers.push('...');
          for (let i = totalPages - 3; i <= totalPages; i++) {
            pageNumbers.push(i);
          }
        } else {
          pageNumbers.push(1);
          pageNumbers.push('...');
          for (let i = currentPage - 1; i <= currentPage + 1; i++) {
            pageNumbers.push(i);
          }
          pageNumbers.push('...');
          pageNumbers.push(totalPages);
        }
      }
      
      return pageNumbers;
    };

    // Función para renderizar la sección de productos
    const renderProductsSection = () => (
      <div>
        <ActionButton onClick={handleAddProduct}>
          <Plus /> Agregar Producto
        </ActionButton>

        {/* Filtros y búsqueda */}
        <div
          style={{
            display: "flex",
            gap: "1rem",
            marginBottom: "2rem",
            flexWrap: "wrap",
            alignItems: "center",
          }}
        >
          <input
            type="text"
            placeholder="Buscar productos..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{
              padding: "0.75rem",
              border: "2px solid #ecf0f1",
              borderRadius: "8px",
              fontSize: "1rem",
              flex: "1",
              minWidth: "250px",
            }}
          />
          <select
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
            style={{
              padding: "0.75rem",
              border: "2px solid #ecf0f1",
              borderRadius: "8px",
              fontSize: "1rem",
              minWidth: "150px",
            }}
          >
            <option value="all">Todas las categorías</option>
            {categories.map((category) => (
              <option key={category.id} value={category.name}>
                {category.name}
              </option>
            ))}
          </select>
        </div>

        {/* Información de paginación */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "1rem",
            padding: "1rem",
            backgroundColor: "#f8f9fa",
            borderRadius: "8px",
          }}
        >
          <span>
            Mostrando {filteredProducts.length} de {totalProducts} productos
            {searchTerm || categoryFilter !== "all" ? " (filtrados)" : ""}
          </span>
          <span>
            Página {currentPage} de {totalPages}
          </span>
        </div>

        {/* Lista de productos */}
        {loadingProducts ? (
          <div style={{ textAlign: "center", padding: "2rem" }}>
            Cargando productos...
          </div>
        ) : (
          <>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))",
                gap: "1rem",
                marginBottom: "2rem",
              }}
            >
              {filteredProducts.map((product) => (
                <div
                  key={product.id}
                  style={{
                    border: "1px solid #ddd",
                    borderRadius: "8px",
                    padding: "1rem",
                    backgroundColor: "white",
                  }}
                >
                  <img
                    src={getImageUrl(product.image_url)}
                    alt={product.name}
                    style={{
                      width: "100%",
                      height: "200px",
                      objectFit: "cover",
                      borderRadius: "4px",
                      marginBottom: "0.5rem",
                    }}
                  />
                  <h4 style={{ margin: "0.5rem 0", fontSize: "1.1rem" }}>
                    {product.name}
                  </h4>
                  <p
                    style={{
                      color: "#666",
                      fontSize: "0.9rem",
                      margin: "0.5rem 0",
                      display: "-webkit-box",
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: "vertical",
                      overflow: "hidden",
                    }}
                  >
                    {product.description}
                  </p>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      marginTop: "1rem",
                    }}
                  >
                    <span style={{ fontWeight: "bold", color: "#2c3e50" }}>
                      Stock: {product.stock}
                    </span>
                    <div style={{ display: "flex", gap: "0.5rem" }}>
                      <button
                        onClick={() => handleEditProduct(product)}
                        style={{
                          padding: "0.5rem 1rem",
                          backgroundColor: "#3498db",
                          color: "white",
                          border: "none",
                          borderRadius: "4px",
                          cursor: "pointer",
                        }}
                      >
                        Editar
                      </button>
                      <button
                        onClick={() => handleDeleteProduct(product.id)}
                        style={{
                          padding: "0.5rem 1rem",
                          backgroundColor: "#e74c3c",
                          color: "white",
                          border: "none",
                          borderRadius: "4px",
                          cursor: "pointer",
                        }}
                      >
                        Eliminar
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Controles de paginación */}
            {totalPages > 1 && (
              <div style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                gap: '10px',
                marginTop: '30px',
                padding: '20px'
              }}>
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  style={{
                    padding: '10px 15px',
                    background: currentPage === 1 ? '#9E9E9E' : '#2A3A53',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontWeight: '600',
                    cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                    transition: 'all 0.3s ease',
                    boxShadow: currentPage === 1 ? 'none' : '0 3px 10px rgba(42, 58, 83, 0.3)'
                  }}
                  onMouseEnter={(e) => {
                    if (currentPage !== 1) {
                      e.currentTarget.style.transform = 'translateY(-2px)';
                      e.currentTarget.style.boxShadow = '0 5px 15px rgba(42, 58, 83, 0.4)';
                      e.currentTarget.style.background = '#1e2a3a';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (currentPage !== 1) {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = '0 3px 10px rgba(42, 58, 83, 0.3)';
                      e.currentTarget.style.background = '#2A3A53';
                    }
                  }}
                >
                  ← Anterior
                </button>
                
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}>
                  {getPageNumbers(currentPage, totalPages).map((page, index) => (
                    page === '...' ? (
                      <span
                        key={`ellipsis-${index}`}
                        style={{
                          width: '40px',
                          height: '40px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: '#6c757d',
                          fontSize: '16px',
                          fontWeight: '600'
                        }}
                      >
                        ...
                      </span>
                    ) : (
                      <button
                        key={page}
                        onClick={() => handlePageChange(page as number)}
                        style={{
                          width: '40px',
                          height: '40px',
                          background: page === currentPage 
                            ? 'linear-gradient(135deg, #FDBF37 0%, #FDBF37 100%)' 
                            : 'white',
                          color: page === currentPage ? 'white' : '#2c3e50',
                          border: page === currentPage ? 'none' : '2px solid #e9ecef',
                          borderRadius: '50%',
                          fontSize: '14px',
                          fontWeight: '600',
                          cursor: 'pointer',
                          transition: 'all 0.3s ease',
                          boxShadow: page === currentPage 
                            ? '0 4px 12px rgba(255, 193, 7, 0.4)' 
                            : '0 2px 6px rgba(0, 0, 0, 0.1)'
                        }}
                        onMouseEnter={(e) => {
                          if (page !== currentPage) {
                            e.currentTarget.style.background = '#f8f9fa';
                            e.currentTarget.style.transform = 'scale(1.1)';
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (page !== currentPage) {
                            e.currentTarget.style.background = 'white';
                            e.currentTarget.style.transform = 'scale(1)';
                          }
                        }}
                      >
                        {page}
                      </button>
                    )
                  ))}
                </div>
                
                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage >= totalPages}
                  style={{
                    padding: '10px 15px',
                    background: currentPage >= totalPages ? '#9E9E9E' : '#2A3A53',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontWeight: '600',
                    cursor: currentPage >= totalPages ? 'not-allowed' : 'pointer',
                    transition: 'all 0.3s ease',
                    boxShadow: currentPage >= totalPages ? 'none' : '0 3px 10px rgba(42, 58, 83, 0.3)'
                  }}
                  onMouseEnter={(e) => {
                    if (currentPage < totalPages) {
                      e.currentTarget.style.transform = 'translateY(-2px)';
                      e.currentTarget.style.boxShadow = '0 5px 15px rgba(42, 58, 83, 0.4)';
                      e.currentTarget.style.background = '#1e2a3a';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (currentPage < totalPages) {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = '0 3px 10px rgba(42, 58, 83, 0.3)';
                      e.currentTarget.style.background = '#2A3A53';
                    }
                  }}
                >
                  Siguiente →
                </button>
              </div>
            )}
          </>
        )}
      </div>
    );

    // --- NUEVO: Gestión de Espacios ---
    const EspaciosSection = () => {
      const [spaces, setSpaces] = useState<Space[]>([]);
      const [products, setProducts] = useState<Product[]>([]);
      const [loading, setLoading] = useState(false);
      const [formProducts, setFormProducts] = useState<SpaceProduct[]>([]); // productos/hotspots dinámicos
      const [image, setImage] = useState<File | null>(null);
      const [spaceName, setSpaceName] = useState<string>(""); // Agregar este estado
      const [submitting, setSubmitting] = useState(false);
      const [editingSpace, setEditingSpace] = useState<Space | null>(null);
      const imageBoxRef = useRef<HTMLDivElement>(null);
      const draggingIndex = useRef<number | null>(null);

      // Estados para tooltip de admin
      const [selectedAdminHotspot, setSelectedAdminHotspot] = useState<{
        product: Product;
        spaceProduct: SpaceProduct;
      } | null>(null);
      const [adminTooltipVisible, setAdminTooltipVisible] = useState(false);

      useEffect(() => {
        fetchSpaces();
        fetchProducts();
      }, []);

      const fetchSpaces = async () => {
        setLoading(true);
        try {
          const res = await api.get("/spaces");
          setSpaces(res.data);
        } catch (e) {
          toast.error("Error al cargar espacios");
        } finally {
          setLoading(false);
        }
      };
      const fetchProducts = async () => {
        try {
          const res = await api.get("/products?limit=10000");
          setProducts(res.data.products || []);
        } catch (e) {
          toast.error("Error al cargar productos");
        }
      };

      // --- Hotspot Drag & Drop ---
      const handleHotspotMouseDown = (index: number, e: React.MouseEvent) => {
        draggingIndex.current = index;
        document.addEventListener("mousemove", handleDocumentMouseMove);
        document.addEventListener("mouseup", handleDocumentMouseUp);
      };

      // --- Manejo de tooltip de admin ---
      const handleAdminHotspotClick = (
        product: Product,
        spaceProduct: SpaceProduct
      ) => {
        setSelectedAdminHotspot({ product, spaceProduct });
        setAdminTooltipVisible(true);
      };

      const closeAdminTooltip = () => {
        setAdminTooltipVisible(false);
        setSelectedAdminHotspot(null);
      };

      const handleViewProductDetails = () => {
        if (selectedAdminHotspot) {
          // Aquí puedes agregar lógica para mostrar detalles del producto
          console.log("Ver detalles del producto:", selectedAdminHotspot.product);
          closeAdminTooltip();
        }
      };



      const handleDocumentMouseMove = (e: MouseEvent) => {
        if (draggingIndex.current === null || !imageBoxRef.current) return;
        const rect = imageBoxRef.current.getBoundingClientRect();
        const x = ((e.clientX - rect.left) / rect.width) * 100;
        const y = ((e.clientY - rect.top) / rect.height) * 100;
        setFormProducts((prev) =>
          prev.map((p, i) =>
            i === draggingIndex.current
              ? {
                ...p,
                x: Math.max(0, Math.min(100, x)),
                y: Math.max(0, Math.min(100, y)),
              }
              : p
          )
        );
      };

      const handleDocumentMouseUp = () => {
        draggingIndex.current = null;
        document.removeEventListener("mousemove", handleDocumentMouseMove);
        document.removeEventListener("mouseup", handleDocumentMouseUp);
      };

      // --- Agregar producto/hotspot ---
      const handleAddProduct = () => {
        setFormProducts((prev) => [
          ...prev,
          { product_id: 0, x: 50, y: 50, destacado: false },
        ]);
      };
      // --- Eliminar producto/hotspot ---
      const handleRemoveProduct = (index: number) => {
        setFormProducts((prev) => prev.filter((_, i) => i !== index));
      };
      // --- Cambiar producto seleccionado ---
      const handleProductChange = (index: number, value: number) => {
        setFormProducts((prev) =>
          prev.map((p, i) => (i === index ? { ...p, product_id: value } : p))
        );
      };
      // --- Cambiar destacado (máximo 3) ---
      const handleDestacadoChange = (index: number) => {
        const destacadosCount = formProducts.filter((p) => p.destacado).length;
        setFormProducts((prev) =>
          prev.map((p, i) => {
            if (i !== index) return p;
            if (!p.destacado && destacadosCount >= 3) return p; // no permitir más de 3
            return { ...p, destacado: !p.destacado };
          })
        );
      };

      // --- Guardar espacio (crear o editar) ---
      const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (formProducts.length === 0) {
          toast.error("Agrega al menos un producto/hotspot");
          return;
        }
        if (formProducts.filter((p) => p.destacado).length > 3) {
          toast.error("Solo puedes marcar hasta 3 productos como destacados");
          return;
        }
        setSubmitting(true);
        try {
          // Removed console.log for production
          const formData = new FormData();
          if (image) formData.append("image", image);
          formData.append("name", spaceName || "Espacio sin nombre"); // Agregar nombre
          formData.append("products", JSON.stringify(formProducts));
          let res;
          if (editingSpace) {
            res = await api.put(
              `/spaces/${editingSpace.id}`,
              formData,
              { headers: { "Content-Type": "multipart/form-data" } }
            );
            toast.success("Espacio actualizado");
          } else {
            res = await api.post("/spaces", formData, {
              headers: { "Content-Type": "multipart/form-data" },
            });
            toast.success("Espacio creado");
          }
          setImage(null);
          setFormProducts([]);
          setSpaceName(""); // Limpiar el nombre
          setEditingSpace(null);
          fetchSpaces();
        } catch (e: any) {
          console.error("Error detallado al guardar espacio:", e);
          const errorMessage =
            e.response?.data?.message ||
            e.message ||
            "Error desconocido al guardar el espacio";
          toast.error(`Error al guardar el espacio: ${errorMessage}`);
        } finally {
          setSubmitting(false);
        }
      };

      // --- Editar espacio ---
      const handleEdit = (space: Space) => {
        setEditingSpace(space);
        setSpaceName(space.name || ""); // Cargar el nombre existente
        setFormProducts(
          space.products.map((p) => ({
            id: p.id,
            product_id: p.product_id,
            x: p.x,
            y: p.y,
            destacado: p.destacado,
            orden: p.orden,
          }))
        );
        setImage(null);
      };
      // --- Eliminar espacio ---
      const handleDelete = async (id: number) => {

        const confirmed = await showConfirm('¿Eliminar este espacio?');
        if (!confirmed) return;

        if (!window.confirm("¿Eliminar este espacio?")) return;

        try {
          await api.delete(`/spaces/${id}`);
          toast.success("Espacio eliminado");
          fetchSpaces();
        } catch (e) {
          toast.error("Error al eliminar espacio");
        }
      };

      return (
        <div style={{ padding: 24 }}>
          <h2
            style={{
              fontSize: "2rem",
              fontWeight: "700",
              color: "#2c3e50",
              marginBottom: "2rem",
              textAlign: "center",
            }}
          >
            Gestión de Espacios
          </h2>
          <form onSubmit={handleSubmit} style={{ marginBottom: 32 }}>
            <div
              style={{
                display: "flex",
                flexDirection: "row",
                gap: 40,
                alignItems: "flex-start",
                justifyContent: "center",
                width: "100%",
                maxWidth: "1400px",
                margin: "0 auto",
              }}
            >
              <div
                style={{
                  flex: 0.4,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "flex-start",
                  minWidth: 400,
                }}
              >
                <Label
                  style={{
                    marginBottom: 16,
                    fontSize: "1.2rem",
                    fontWeight: "700",
                    color: "#2c3e50",
                    textAlign: "center",
                  }}
                >
                  Imagen del espacio
                </Label>
                <div style={{
                  fontSize: '13px',
                  color: '#6c757d',
                  marginBottom: '12px',
                  textAlign: 'center',
                  fontWeight: '500',
                  background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
                  padding: '8px 16px',
                  borderRadius: '6px',
                  border: '1px solid #dee2e6',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
                }}>
                  📐 Tamaño recomendado: 458 × 407 píxeles
                </div>
                <div
                  ref={imageBoxRef}
                  style={{
                    width: "100%",
                    minHeight: 280,
                    height: "auto",
                    background:
                      "linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)",
                    borderRadius: 12,
                    boxShadow: "0 2px 12px rgba(0,0,0,0.06)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    border: "1px solid #e9ecef",
                    position: "relative",
                    overflow: "hidden",
                    userSelect: "none",
                    transition: "all 0.3s ease",
                    cursor: "pointer",
                  }}
                >
                  {image ? (
                    <img
                      src={URL.createObjectURL(image)}
                      alt="Vista previa"
                      style={{
                        maxWidth: "95%",
                        maxHeight: "95%",
                        objectFit: "contain",
                        borderRadius: 18,
                        boxShadow: "0 1px 4px rgba(0,0,0,0.05)",
                      }}
                    />
                  ) : editingSpace && editingSpace.image_url ? (
                    <img
                      src={getImageUrl(editingSpace.image_url)}
                      alt="Espacio"
                      style={{
                        maxWidth: "95%",
                        maxHeight: "95%",
                        objectFit: "contain",
                        borderRadius: 18,
                        boxShadow: "0 1px 4px rgba(0,0,0,0.05)",
                      }}
                    />
                  ) : (
                    <span style={{ color: "#bbb", fontSize: 22 }}>
                      Previsualización de imagen
                    </span>
                  )}
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => setImage(e.target.files?.[0] || null)}
                    style={{
                      position: "absolute",
                      width: "100%",
                      height: "100%",
                      opacity: 0,
                      cursor: "pointer",
                      left: 0,
                      top: 0,
                    }}
                    title="Seleccionar imagen"
                  />
                  {/* Hotspots dinámicos */}
                  {formProducts.map((p, i) => {
                    const prod = products.find(
                      (prod) => Number(prod.id) === p.product_id
                    );
                    if (!prod) return null;
                    return (
                      <div
                        key={i}
                        style={{
                          position: "absolute",
                          left: `calc(${p.x}% - 12px)`,
                          top: `calc(${p.y}% - 12px)`,
                          width: 24,
                          height: 24,
                          borderRadius: "50%",
                          background: p.destacado ? "#FDBF37" : "#aaa",
                          boxShadow: "0 2px 8px rgba(0,0,0,0.12)",
                          border: "2px solid #fff",
                          cursor: "grab",
                          zIndex: 10,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          transition: "all 0.3s ease",
                        }}
                        onMouseDown={(e) => {
                          // Solo permitir drag si no hay tooltip visible
                          if (!adminTooltipVisible) {
                            handleHotspotMouseDown(i, e);
                          }
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleAdminHotspotClick(prod, p);
                        }}
                        onMouseEnter={(e) => {
                          if (!adminTooltipVisible) {
                            e.currentTarget.style.transform = "scale(1.1)";
                            e.currentTarget.style.boxShadow =
                              "0 4px 16px rgba(0,0,0,0.2)";
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (!adminTooltipVisible) {
                            e.currentTarget.style.transform = "scale(1)";
                            e.currentTarget.style.boxShadow =
                              "0 2px 8px rgba(0,0,0,0.12)";
                          }
                        }}
                        title={prod?.name || ""}
                      >
                        <div
                          style={{
                            position: "absolute",
                            bottom: 28,
                            left: "50%",
                            transform: "translateX(-50%)",
                            background: "#222",
                            color: "#fff",
                            padding: "6px 14px",
                            borderRadius: 8,
                            fontSize: 14,
                            whiteSpace: "nowrap",
                            opacity: 0,
                            pointerEvents: "none",
                            transition: "opacity 0.2s",
                          }}
                          className="hotspot-tooltip"
                        >
                          {prod?.name}
                        </div>
                        {selectedAdminHotspot &&
                          selectedAdminHotspot.product.id === prod.id ? (
                          <Times
                            width={12}
                            height={12}
                            style={{ color: "white" }}
                          />
                        ) : (
                          <div
                            style={{
                              width: 10,
                              height: 10,
                              borderRadius: "50%",
                              background: p.destacado ? "#FDBF37" : "#aaa",
                              boxShadow: "0 0 0 4px #fff",
                            }}
                          />
                        )}

                        {/* Tarjeta emergente del producto en admin */}
                        {selectedAdminHotspot &&
                          selectedAdminHotspot.product.id === prod.id && (
                            <AdminProductTooltipCard
                              $visible={adminTooltipVisible}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleViewProductDetails();
                              }}
                            >
                              <AdminProductTooltipContent>
                                <AdminProductTooltipImage
                                  src={getImageUrl(prod.image_url)}
                                  alt={prod.name}
                                  onError={(e) => {
                                    e.currentTarget.src =
                                      "/placeholder-image.jpg";
                                  }}
                                />
                                <AdminProductTooltipInfo>
                                  <AdminProductTooltipName>
                                    {prod.name}
                                  </AdminProductTooltipName>
                                  <AdminProductTooltipPrice>
                                    {prod.brand && `${prod.brand} • `}
                                    Stock: {prod.stock}
                                    {p.destacado && " • Destacado"}
                                  </AdminProductTooltipPrice>
                                </AdminProductTooltipInfo>
                                <AdminProductTooltipArrow
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleViewProductDetails();
                                  }}
                                >
                                  <ArrowRight width={16} height={16} />
                                </AdminProductTooltipArrow>
                              </AdminProductTooltipContent>
                            </AdminProductTooltipCard>
                          )}
                      </div>
                    );
                  })}

                  {/* Overlay para cerrar tooltip al hacer clic fuera */}
                  {adminTooltipVisible && (
                    <div
                      style={{
                        position: "fixed",
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        zIndex: 999,
                        background: "transparent",
                      }}
                      onClick={closeAdminTooltip}
                    />
                  )}
                </div>
              </div>
              <div
                style={{
                  flex: 0.6,
                  minWidth: 400,
                  background: "linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)",
                  borderRadius: "16px",
                  padding: "24px",
                  boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
                  border: "1px solid #e9ecef",
                }}
              >
                <div
                  style={{
                    marginBottom: "20px",
                    textAlign: "center",
                  }}
                >
                  <h3
                    style={{
                      fontSize: "1.3rem",
                      fontWeight: "600",
                      color: "#2c3e50",
                      marginBottom: "8px",
                    }}
                  >
                    Configuración de Productos
                  </h3>
                  <p
                    style={{
                      fontSize: "0.9rem",
                      color: "#6c757d",
                      margin: 0,
                    }}
                  >
                    Agrega productos y configura sus posiciones en la imagen
                  </p>
                </div>
                <Button
                  type="button"
                  onClick={handleAddProduct}
                  style={{
                    marginBottom: 20,
                    width: "100%",
                    background:
                      "linear-gradient(135deg, var(--primary-orange), #FDBF37)",
                    border: "none",
                    borderRadius: "12px",
                    padding: "12px 20px",
                    fontSize: "1rem",
                    fontWeight: "600",
                    color: "white",
                    boxShadow: "0 4px 15px rgba(255, 140, 66, 0.3)",
                    transition: "all 0.3s ease",
                  }}
                >
                  <Plus style={{ marginRight: "8px" }} /> Agregar producto/hotspot
                </Button>
                {formProducts.map((p, i) => (
                  <div
                    key={i}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 12,
                      marginBottom: 16,
                      background:
                        "linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)",
                      padding: 16,
                      borderRadius: 12,
                      border: "1px solid #e9ecef",
                      boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
                      transition: "all 0.3s ease",
                    }}
                  >
                    <ProductSelector
                      products={products}
                      selectedProductId={p.product_id}
                      onProductChange={(productId) =>
                        handleProductChange(i, productId)
                      }
                      placeholder="Selecciona producto"
                    />
                    <label
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                        fontSize: "14px",
                        fontWeight: "500",
                        color: "#495057",
                        cursor: "pointer",
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={p.destacado}
                        onChange={() => handleDestacadoChange(i)}
                        disabled={
                          !p.destacado &&
                          formProducts.filter((fp) => fp.destacado).length >= 3
                        }
                        style={{
                          margin: 0,
                          width: "18px",
                          height: "18px",
                          accentColor: "var(--primary-orange)",
                        }}
                      />
                      Destacado
                    </label>
                    <IconButton
                      variant="delete"
                      onClick={() => handleRemoveProduct(i)}
                      style={{
                        padding: "8px",
                        minWidth: "auto",
                        borderRadius: "8px",
                        background: "#dc3545",
                        border: "none",
                        color: "white",
                        transition: "all 0.3s ease",
                      }}
                    >
                      <Trash />
                    </IconButton>
                  </div>
                ))}
                <FormGroup>
                  <Label>Nombre del Espacio</Label>
                  <Input
                    type="text"
                    value={spaceName}
                    onChange={(e) => setSpaceName(e.target.value)}
                    placeholder="Ej: Espacio Sala de Estar, Espacio Cocina..."
                    maxLength={255}
                  />
                </FormGroup>

                <div
                  style={{
                    display: "flex",
                    gap: 12,
                    marginTop: 20,
                  }}
                >
                  <Button
                    type="submit"
                    variant="primary"
                    disabled={submitting}
                    style={{
                      flex: 1,
                      background: "linear-gradient(135deg, #28a745, #20c997)",
                      border: "none",
                      borderRadius: "12px",
                      padding: "14px 24px",
                      fontSize: "1.1rem",
                      fontWeight: "600",
                      boxShadow: "0 4px 15px rgba(40, 167, 69, 0.3)",
                    }}
                  >
                    {editingSpace ? "Actualizar" : "Crear"} Espacio
                  </Button>
                  {editingSpace && (
                    <Button
                      type="button"
                      variant="secondary"
                      onClick={() => {
                        setEditingSpace(null);
                        setFormProducts([]);
                        setImage(null);
                      }}
                      style={{
                        background: "#6c757d",
                        border: "none",
                        borderRadius: "12px",
                        padding: "14px 24px",
                        fontSize: "1.1rem",
                        fontWeight: "600",
                        color: "white",
                      }}
                    >
                      Cancelar
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </form>
          <h3
            style={{
              fontSize: "1.5rem",
              fontWeight: "700",
              color: "#2c3e50",
              marginBottom: "1.5rem",
              textAlign: "center",
            }}
          >
            Espacios creados
          </h3>
          {loading ? (
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                padding: "2rem",
                color: "#6c757d",
              }}
            >
              <p>Cargando espacios...</p>
            </div>
          ) : (
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))",
                gap: 24,
                maxWidth: "1200px",
                margin: "0 auto",
              }}
            >
              {spaces.map((space, index) => (
                <div
                  key={space.id}
                  style={{
                    border: "1px solid #e9ecef",
                    borderRadius: 16,
                    padding: 20,
                    minWidth: 320,
                    background: "#fff",
                    boxShadow: "0 6px 24px rgba(0,0,0,0.12)",
                    transition: "all 0.3s ease",
                    cursor: "pointer",
                  }}
                >
                  <h4
                    style={{
                      margin: "0 0 16px 0",
                      fontSize: "1.2rem",
                      fontWeight: "600",
                      color: "#2c3e50",
                      textAlign: "center",
                    }}
                  >
                    {space.name || `Espacio ${index + 1}`}
                  </h4>
                  <div
                    style={{
                      width: "100%",
                      height: 180,
                      background:
                        "linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)",
                      borderRadius: 12,
                      overflow: "hidden",
                      marginBottom: 16,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      border: "2px solid #e9ecef",
                    }}
                  >
                    {space.image_url ? (
                      <img
                        src={getImageUrl(space.image_url)}
                        alt="espacio"
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                        }}
                      />
                    ) : (
                      <span style={{ color: "#aaa", fontSize: "14px" }}>
                        Sin imagen
                      </span>
                    )}
                  </div>
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 6,
                      marginBottom: 12,
                    }}
                  >
                    {space.products.map((p, i) => {
                      const prod = products.find(
                        (prod) => Number(prod.id) === p.product_id
                      );
                      if (!prod) return null;
                      return (
                        <div
                          key={i}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 8,
                            padding: "4px 0",
                          }}
                        >
                          <span style={{ fontWeight: 500, fontSize: "14px" }}>
                            {prod.name}
                          </span>
                          {p.destacado && (
                            <span
                              style={{
                                color: "#FDBF37",
                                fontWeight: 700,
                                fontSize: 12,
                                background: "#fff3cd",
                                padding: "2px 6px",
                                borderRadius: "4px",
                              }}
                            >
                              Destacado
                            </span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                  <div style={{ display: "flex", gap: 8 }}>
                    <Button
                      onClick={() => handleEdit(space)}
                      variant="primary"
                      style={{ padding: "6px 12px", fontSize: "12px" }}
                    >
                      <Edit /> Editar
                    </Button>
                    <Button
                      onClick={() => handleDelete(space.id)}
                      variant="secondary"
                      style={{
                        padding: "6px 12px",
                        fontSize: "12px",
                        backgroundColor: "#dc3545",
                        borderColor: "#dc3545",
                        color: "white",
                      }}
                    >
                      <Trash /> Eliminar
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      );
    };

    return (
      <AdminLayout>
        <SidebarOverlay open={sidebarOpen} onClick={toggleSidebar} />
        
        <Sidebar open={sidebarOpen}>
          <SidebarHeader open={sidebarOpen}>
            <SidebarTitleContainer open={sidebarOpen}>
              <SidebarTitle>Admin Panel</SidebarTitle>
              <WelcomeMessage>
                Bienvenido: {user?.name || "Usuario"}, este es tu panel de
                administración
              </WelcomeMessage>
            </SidebarTitleContainer>
            
            {/* Nuevo toggle integrado */}
            <SidebarToggle open={sidebarOpen} onClick={toggleSidebar}>
              {sidebarOpen ? <FiChevronLeft size={18} /> : <Menu width={18} height={18} />}
            </SidebarToggle>
          </SidebarHeader>

          <SidebarMenu>
            {(hasPermission('products')) && (
            <SidebarMenuItem
              active={activeTab === "products"}
              onClick={() => {
                changeActiveTab("products");
                if (window.innerWidth <= 768) setSidebarOpen(false);
              }}
              sidebarOpen={sidebarOpen}
            >
              <SidebarIcon sidebarOpen={sidebarOpen}>
                <Package />
              </SidebarIcon>
              {sidebarOpen && "Productos"}
              <Tooltip show={!sidebarOpen}>Productos</Tooltip>
            </SidebarMenuItem>)}

            {(hasPermission('users')) && (
            <SidebarMenuItem
              active={activeTab === "users"}
              onClick={() => {
                changeActiveTab("users");
                if (window.innerWidth <= 768) setSidebarOpen(false);
              }}
              sidebarOpen={sidebarOpen}
            >
              <SidebarIcon sidebarOpen={sidebarOpen}>
                <Users />
              </SidebarIcon>
              {sidebarOpen && "Usuarios"}
              <Tooltip show={!sidebarOpen}>Usuarios</Tooltip>
            </SidebarMenuItem>)}

            {(user?.role === 'admin') && (
            <SidebarMenuItem
              active={activeTab === "roles"}
              onClick={() => {
                changeActiveTab("roles");
                if (window.innerWidth <= 768) setSidebarOpen(false);
              }}
              sidebarOpen={sidebarOpen}
            >
              <SidebarIcon sidebarOpen={sidebarOpen}>
                <Users />
              </SidebarIcon>
              {sidebarOpen && "Roles"}
              <Tooltip show={!sidebarOpen}>Roles</Tooltip>
            </SidebarMenuItem>)}

            {(hasPermission('categories')) && (
            <SidebarMenuItem
              active={activeTab === "categories"}
              onClick={() => {
                changeActiveTab("categories");
                if (window.innerWidth <= 768) setSidebarOpen(false);
              }}
              sidebarOpen={sidebarOpen}
            >
              <SidebarIcon sidebarOpen={sidebarOpen}>
                <Layers />
              </SidebarIcon>
              {sidebarOpen && "Categorías"}
              <Tooltip show={!sidebarOpen}>Categorías</Tooltip>
            </SidebarMenuItem>)}

            {(hasPermission('families')) && (
            <SidebarMenuItem
              active={activeTab === "families"}
              onClick={() => {
                changeActiveTab("families");
                if (window.innerWidth <= 768) setSidebarOpen(false);
              }}
              sidebarOpen={sidebarOpen}
            >
              <SidebarIcon sidebarOpen={sidebarOpen}>
                <FolderIcon />
              </SidebarIcon>
              {sidebarOpen && "Familias"}
              <Tooltip show={!sidebarOpen}>Familias</Tooltip>
            </SidebarMenuItem>)}

            {(hasPermission('subfamilies')) && (
            <SidebarMenuItem
              active={activeTab === "subfamilies"}
              onClick={() => {
                changeActiveTab("subfamilies");
                if (window.innerWidth <= 768) setSidebarOpen(false);
              }}
              sidebarOpen={sidebarOpen}
            >
              <SidebarIcon sidebarOpen={sidebarOpen}>
                <FileTextIcon />
              </SidebarIcon>
              {sidebarOpen && "Subfamilias"}
              <Tooltip show={!sidebarOpen}>Subfamilias</Tooltip>
            </SidebarMenuItem>)}

            {(hasPermission('slider')) && (
            <SidebarMenuItem
              active={activeTab === "slider"}
              onClick={() => {
                changeActiveTab("slider");
                if (window.innerWidth <= 768) setSidebarOpen(false);
              }}
              sidebarOpen={sidebarOpen}
            >
              <SidebarIcon sidebarOpen={sidebarOpen}>
                <UploadCloud />
              </SidebarIcon>
              {sidebarOpen && "Slider"}
              <Tooltip show={!sidebarOpen}>Slider</Tooltip>
            </SidebarMenuItem>)}

            {(hasPermission('daily-deals')) && (
            <SidebarMenuItem
              active={activeTab === "daily-deals"}
              onClick={() => {
                changeActiveTab("daily-deals");
                if (window.innerWidth <= 768) setSidebarOpen(false);
              }}
              sidebarOpen={sidebarOpen}
            >
              <SidebarIcon sidebarOpen={sidebarOpen}>
                <Tag />
              </SidebarIcon>
              {sidebarOpen && "Llévate los hoy"}
              <Tooltip show={!sidebarOpen}>Llévate los hoy</Tooltip>
            </SidebarMenuItem>)}

            {(hasPermission('banners')) && (
            <SidebarMenuItem
              active={activeTab === "banners"}
              onClick={() => {
                changeActiveTab("banners");
                if (window.innerWidth <= 768) setSidebarOpen(false);
              }}
              sidebarOpen={sidebarOpen}
            >
              <SidebarIcon sidebarOpen={sidebarOpen}>
                <UploadCloud />
              </SidebarIcon>
              {sidebarOpen && "Banners"}
              <Tooltip show={!sidebarOpen}>Banners</Tooltip>
            </SidebarMenuItem>)}

            {(hasPermission('excel-upload')) && (
            <SidebarMenuItem
              active={activeTab === "excel-upload"}
              onClick={() => {
                changeActiveTab("excel-upload");
                if (window.innerWidth <= 768) setSidebarOpen(false);
              }}
              sidebarOpen={sidebarOpen}
            >
              <SidebarIcon sidebarOpen={sidebarOpen}>
                <UploadCloud />
              </SidebarIcon>
              {sidebarOpen && "Subir Excel"}
              <Tooltip show={!sidebarOpen}>Subir Excel</Tooltip>
            </SidebarMenuItem>)}

            {(hasPermission('settings')) && (
            <SidebarMenuItem
              active={activeTab === "settings"}
              onClick={() => {
                changeActiveTab("settings");
                if (window.innerWidth <= 768) setSidebarOpen(false);
              }}
              sidebarOpen={sidebarOpen}
            >
              <SidebarIcon sidebarOpen={sidebarOpen}>
                <SettingsIcon />
              </SidebarIcon>
              {sidebarOpen && "Configuraciones"}
              <Tooltip show={!sidebarOpen}>Configuraciones</Tooltip>
            </SidebarMenuItem>)}

            {(hasPermission('spaces')) && (
            <SidebarMenuItem
              active={activeTab === "spaces"}
              onClick={() => {
                changeActiveTab("spaces");
                if (window.innerWidth <= 768) setSidebarOpen(false);
              }}
              sidebarOpen={sidebarOpen}
            >
              <SidebarIcon sidebarOpen={sidebarOpen}>
                <Layers />
              </SidebarIcon>
              {sidebarOpen && "Espacios"}
              <Tooltip show={!sidebarOpen}>Espacios</Tooltip>
            </SidebarMenuItem>)}

            {(hasPermission('combos')) && (
            <SidebarMenuItem
              active={activeTab === "combos"}
              onClick={() => {
                changeActiveTab("combos");
                if (window.innerWidth <= 768) setSidebarOpen(false);
              }}
              sidebarOpen={sidebarOpen}
            >
              <SidebarIcon sidebarOpen={sidebarOpen}>
                <Box />
              </SidebarIcon>
              {sidebarOpen && "Combos"}
              <Tooltip show={!sidebarOpen}>Combos</Tooltip>
            </SidebarMenuItem>)}

            {(hasPermission('footer')) && (
            <SidebarMenuItem
              active={activeTab === "footer"}
              onClick={() => {
                changeActiveTab("footer");
                if (window.innerWidth <= 768) setSidebarOpen(false);
              }}
              sidebarOpen={sidebarOpen}
            >
              <SidebarIcon sidebarOpen={sidebarOpen}>
                <FileTextIcon />
              </SidebarIcon>
              {sidebarOpen && "Footer"}
              <Tooltip show={!sidebarOpen}>Footer</Tooltip>
            </SidebarMenuItem>)}
          </SidebarMenu>

          <SidebarLogout onClick={() => {
            handleLogout();
            if (window.innerWidth <= 768) setSidebarOpen(false);
          }} sidebarOpen={sidebarOpen} style={{ position: 'relative' }}>
            <SidebarIcon sidebarOpen={sidebarOpen}>
              <LogOut />
            </SidebarIcon>
            {sidebarOpen && "Cerrar Sesión"}
            <Tooltip show={!sidebarOpen}>Cerrar Sesión</Tooltip>
          </SidebarLogout>
        </Sidebar>

        <MainContent sidebarOpen={sidebarOpen}>
          <ContentWrapper>
            <TopBar>
              <div style={{ display: "flex", alignItems: "center", gap: "1.5rem", width: "100%" }}>
                {!sidebarOpen && (
                  <MenuButton>
                    <div className="button" onClick={toggleSidebar}>
                      <FiMenu size={24} color="white" />
                    </div>
                    <div className="tooltip">Abrir menú</div>
                  </MenuButton>
                )}
                {sidebarOpen && (
                  <TopBarButton 
                    onClick={toggleSidebar} 
                    style={{ 
                      marginLeft: "0",
                      display: window.innerWidth <= 768 ? "flex" : "none"
                    }}
                  >
                    <FiArrowLeft />
                  </TopBarButton>
                )}
                <PageTitle>Panel de Administración</PageTitle>
              </div>
            </TopBar>

          <ContentCard>
            <StatsGrid>
              <StatCard>
                <StatIcon color="#3498db">
                  <Package />
                </StatIcon>
                <StatInfo>
                  <StatValue>{stats.totalProducts}</StatValue>
                  <StatLabel>Total Productos</StatLabel>
                </StatInfo>
              </StatCard>

              <StatCard>
                <StatIcon color="#2ecc71">
                  <Users />
                </StatIcon>
                <StatInfo>
                  <StatValue>{stats.totalUsers}</StatValue>
                  <StatLabel>Total Usuarios</StatLabel>
                </StatInfo>
              </StatCard>
            </StatsGrid>

            {activeTab === "products" && renderProductsSection()}

            {activeTab === "users" && hasPermission('users') && <UsersTab />}
            {activeTab === "roles" && user?.role === 'admin' && <RolesTab />}

            {activeTab === "categories" && hasPermission('categories') && (
              <div>
                <ActionButton onClick={handleAddCategory}>
                  <Plus /> Agregar Categoría
                </ActionButton>

                <Table>
                  <thead>
                    <tr>
                      <TableHeader>Nombre</TableHeader>
                      <TableHeader>Descripción</TableHeader>
                      <TableHeader>Acciones</TableHeader>
                    </tr>
                  </thead>
                  <tbody>
                    {categories.map((category) => (
                      <tr key={category.id}>
                        <TableCell>{category.name}</TableCell>
                        <TableCell>{category.description || "-"}</TableCell>
                        <TableCell>
                          <ActionButtons>
                            <IconButton
                              variant="edit"
                              onClick={() => handleEditCategory(category)}
                            >
                              <Edit />
                            </IconButton>
                            <IconButton
                              variant="delete"
                              onClick={() => handleDeleteCategory(category.id)}
                            >
                              <Trash />
                            </IconButton>
                          </ActionButtons>
                        </TableCell>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            )}

            {activeTab === "families" && hasPermission('families') && (
              <div>
                <ActionButton onClick={handleAddFamily}>
                  <Plus /> Agregar Familia
                </ActionButton>

                {/* Filtros y búsqueda */}
                <div
                  style={{
                    display: "flex",
                    gap: "1rem",
                    marginBottom: "2rem",
                    flexWrap: "wrap",
                    alignItems: "center",
                  }}
                >
                  <input
                    type="text"
                    placeholder="Buscar familias..."
                    value={familiesSearchTerm}
                    onChange={(e) => setFamiliesSearchTerm(e.target.value)}
                    style={{
                      padding: "0.75rem",
                      border: "2px solid #ecf0f1",
                      borderRadius: "8px",
                      fontSize: "1rem",
                      flex: "1",
                      minWidth: "250px",
                    }}
                  />
                  <select
                    value={categoryFilter}
                    onChange={(e) => setCategoryFilter(e.target.value)}
                    style={{
                      padding: "0.75rem",
                      border: "2px solid #ecf0f1",
                      borderRadius: "8px",
                      fontSize: "1rem",
                      minWidth: "150px",
                    }}
                  >
                    <option value="all">Todas las categorías</option>
                    {categories.map((category) => (
                      <option key={category.id} value={category.name}>
                        {category.name}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Información de paginación */}
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: "1rem",
                    padding: "1rem",
                    backgroundColor: "#f8f9fa",
                    borderRadius: "8px",
                  }}
                >
                  <span>
                    Mostrando {families.length} de {familiesTotalItems} familias
                    {familiesSearchTerm || categoryFilter !== "all" ? " (filtradas)" : ""}
                  </span>
                  <span>
                    Página {familiesCurrentPage} de {familiesTotalPages}
                  </span>
                </div>

                <Table>
                  <thead>
                    <tr>
                      <TableHeader>Nombre</TableHeader>
                      <TableHeader>Descripción</TableHeader>
                      <TableHeader>Categoría</TableHeader>
                      <TableHeader>Imagen</TableHeader>
                      <TableHeader>Acciones</TableHeader>
                    </tr>
                  </thead>
                  <tbody>
                    {families.map((family) => (
                      <tr key={family.id}>
                        <TableCell>{family.name}</TableCell>
                        <TableCell>{family.description || "-"}</TableCell>
                        <TableCell>{family.category_name || "-"}</TableCell>
                        <TableCell>
                          {family.image_url ? (
                            <img
                              src={getImageUrl(family.image_url)}
                              alt={family.name}
                              style={{
                                width: "50px",
                                height: "50px",
                                objectFit: "cover",
                                borderRadius: "4px",
                              }}
                            />
                          ) : (
                            "-"
                          )}
                        </TableCell>
                        <TableCell>
                          <ActionButtons>
                            <IconButton
                              variant="edit"
                              onClick={() => handleEditFamily(family)}
                            >
                              <Edit />
                            </IconButton>
                            <IconButton
                              variant="delete"
                              onClick={() => handleDeleteFamily(family.id)}
                            >
                              <Trash />
                            </IconButton>
                          </ActionButtons>
                        </TableCell>
                      </tr>
                    ))}
                  </tbody>
                </Table>

                {/* Paginación */}
                {familiesTotalPages > 1 && (
                  <div style={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    gap: '10px',
                    marginTop: '30px',
                    padding: '20px'
                  }}>
                    <button
                      onClick={() => handleFamiliesPageChange(familiesCurrentPage - 1)}
                      disabled={familiesCurrentPage === 1}
                      style={{
                        padding: '10px 15px',
                        background: familiesCurrentPage === 1 ? '#9E9E9E' : '#2A3A53',
                        color: 'white',
                        border: 'none',
                        borderRadius: '8px',
                        fontSize: '14px',
                        fontWeight: '600',
                        cursor: familiesCurrentPage === 1 ? 'not-allowed' : 'pointer',
                        transition: 'all 0.3s ease',
                        boxShadow: familiesCurrentPage === 1 ? 'none' : '0 3px 10px rgba(42, 58, 83, 0.3)'
                      }}
                      onMouseEnter={(e) => {
                        if (familiesCurrentPage !== 1) {
                          e.currentTarget.style.transform = 'translateY(-2px)';
                          e.currentTarget.style.boxShadow = '0 5px 15px rgba(42, 58, 83, 0.4)';
                          e.currentTarget.style.background = '#1e2a3a';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (familiesCurrentPage !== 1) {
                          e.currentTarget.style.transform = 'translateY(0)';
                          e.currentTarget.style.boxShadow = '0 3px 10px rgba(42, 58, 83, 0.3)';
                          e.currentTarget.style.background = '#2A3A53';
                        }
                      }}
                    >
                      ← Anterior
                    </button>
                    
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}>
                      {getPageNumbers(familiesCurrentPage, familiesTotalPages).map((page, index) => (
                        page === '...' ? (
                          <span
                            key={`ellipsis-${index}`}
                            style={{
                              width: '40px',
                              height: '40px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              color: '#6c757d',
                              fontSize: '16px',
                              fontWeight: '600'
                            }}
                          >
                            ...
                          </span>
                        ) : (
                          <button
                            key={page}
                            onClick={() => handleFamiliesPageChange(page as number)}
                            style={{
                              width: '40px',
                              height: '40px',
                              background: page === familiesCurrentPage 
                                ? 'linear-gradient(135deg, #FDBF37 0%, #FDBF37 100%)' 
                                : 'white',
                              color: page === familiesCurrentPage ? 'white' : '#2c3e50',
                              border: page === familiesCurrentPage ? 'none' : '2px solid #e9ecef',
                              borderRadius: '50%',
                              fontSize: '14px',
                              fontWeight: '600',
                              cursor: 'pointer',
                              transition: 'all 0.3s ease',
                              boxShadow: page === familiesCurrentPage 
                                ? '0 4px 12px rgba(255, 193, 7, 0.4)' 
                                : '0 2px 6px rgba(0, 0, 0, 0.1)'
                            }}
                            onMouseEnter={(e) => {
                              if (page !== familiesCurrentPage) {
                                e.currentTarget.style.background = '#f8f9fa';
                                e.currentTarget.style.transform = 'scale(1.1)';
                              }
                            }}
                            onMouseLeave={(e) => {
                              if (page !== familiesCurrentPage) {
                                e.currentTarget.style.background = 'white';
                                e.currentTarget.style.transform = 'scale(1)';
                              }
                            }}
                          >
                            {page}
                          </button>
                        )
                      ))}
                    </div>
                    
                    <button
                      onClick={() => handleFamiliesPageChange(familiesCurrentPage + 1)}
                      disabled={familiesCurrentPage >= familiesTotalPages}
                      style={{
                        padding: '10px 15px',
                        background: familiesCurrentPage >= familiesTotalPages ? '#9E9E9E' : '#2A3A53',
                        color: 'white',
                        border: 'none',
                        borderRadius: '8px',
                        fontSize: '14px',
                        fontWeight: '600',
                        cursor: familiesCurrentPage >= familiesTotalPages ? 'not-allowed' : 'pointer',
                        transition: 'all 0.3s ease',
                        boxShadow: familiesCurrentPage >= familiesTotalPages ? 'none' : '0 3px 10px rgba(42, 58, 83, 0.3)'
                      }}
                      onMouseEnter={(e) => {
                        if (familiesCurrentPage < familiesTotalPages) {
                          e.currentTarget.style.transform = 'translateY(-2px)';
                          e.currentTarget.style.boxShadow = '0 5px 15px rgba(42, 58, 83, 0.4)';
                          e.currentTarget.style.background = '#1e2a3a';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (familiesCurrentPage < familiesTotalPages) {
                          e.currentTarget.style.transform = 'translateY(0)';
                          e.currentTarget.style.boxShadow = '0 3px 10px rgba(42, 58, 83, 0.3)';
                          e.currentTarget.style.background = '#2A3A53';
                        }
                      }}
                    >
                      Siguiente →
                    </button>
                  </div>
                )}
              </div>
            )}

            {activeTab === "subfamilies" && hasPermission('subfamilies') && (
              <div>
                <ActionButton onClick={handleAddSubfamily}>
                  <Plus /> Agregar Subfamilia
                </ActionButton>

                {/* Filtros y búsqueda */}
                <div
                  style={{
                    display: "flex",
                    gap: "1rem",
                    marginBottom: "2rem",
                    flexWrap: "wrap",
                    alignItems: "center",
                  }}
                >
                  <input
                    type="text"
                    placeholder="Buscar subfamilias..."
                    value={subfamiliesSearchTerm}
                    onChange={(e) => setSubfamiliesSearchTerm(e.target.value)}
                    style={{
                      padding: "0.75rem",
                      border: "2px solid #ecf0f1",
                      borderRadius: "8px",
                      fontSize: "1rem",
                      flex: "1",
                      minWidth: "250px",
                    }}
                  />
                  <div ref={familyDropdownRef} style={{ position: "relative", minWidth: "200px" }}>
                    <input
                      type="text"
                      placeholder="Buscar familia..."
                      value={familySearchTerm}
                      onChange={(e) => handleFamilySearchChange(e.target.value)}
                      onFocus={handleFamilyInputFocus}
                      onBlur={handleFamilyInputBlur}
                      style={{
                        padding: "0.75rem",
                        border: "2px solid #ecf0f1",
                        borderRadius: "8px",
                        fontSize: "1rem",
                        width: "100%",
                        cursor: "pointer",
                      }}
                    />
                    {showFamilyDropdown && (
                      <div
                        style={{
                          position: "absolute",
                          top: "100%",
                          left: 0,
                          right: 0,
                          backgroundColor: "white",
                          border: "1px solid #ddd",
                          borderRadius: "8px",
                          boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
                          zIndex: 1000,
                          maxHeight: "200px",
                          overflowY: "auto",
                        }}
                      >
                        <div
                          onMouseDown={(e) => {
                            e.preventDefault();
                            handleFamilySelect(null);
                          }}
                          style={{
                            padding: "0.75rem",
                            cursor: "pointer",
                            borderBottom: "1px solid #eee",
                            backgroundColor: familyFilter === "all" ? "#f8f9fa" : "white",
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = "#f8f9fa";
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = familyFilter === "all" ? "#f8f9fa" : "white";
                          }}
                        >
                          Todas las familias
                        </div>
                        {filteredFamiliesForSubfamily.map((family) => (
                          <div
                            key={family.id}
                            onMouseDown={(e) => {
                              e.preventDefault();
                              handleFamilySelect(family);
                            }}
                            style={{
                              padding: "0.75rem",
                              cursor: "pointer",
                              borderBottom: "1px solid #eee",
                              backgroundColor: familyFilter === family.id.toString() ? "#f8f9fa" : "white",
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.backgroundColor = "#f8f9fa";
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.backgroundColor = familyFilter === family.id.toString() ? "#f8f9fa" : "white";
                            }}
                          >
                            {family.name}
                          </div>
                        ))}
                        {filteredFamiliesForSubfamily.length === 0 && familySearchTerm && (
                          <div
                            style={{
                              padding: "0.75rem",
                              color: "#666",
                              fontStyle: "italic",
                            }}
                          >
                            No se encontraron familias
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                {/* Información de paginación */}
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: "1rem",
                    padding: "1rem",
                    backgroundColor: "#f8f9fa",
                    borderRadius: "8px",
                  }}
                >
                  <span>
                    Mostrando {subfamilies.length} de {subfamiliesTotalItems} subfamilias
                    {subfamiliesSearchTerm || familyFilter !== "all" ? " (filtradas)" : ""}
                  </span>
                  <span>
                    Página {subfamiliesCurrentPage} de {subfamiliesTotalPages}
                  </span>
                </div>

                <Table>
                  <thead>
                    <tr>
                      <TableHeader>Nombre</TableHeader>
                      <TableHeader>Descripción</TableHeader>
                      <TableHeader>Familia</TableHeader>
                      <TableHeader>Acciones</TableHeader>
                    </tr>
                  </thead>
                  <tbody>
                    {subfamilies.map((subfamily) => (
                      <tr key={subfamily.id}>
                        <TableCell>{subfamily.name}</TableCell>
                        <TableCell>{subfamily.description || "-"}</TableCell>
                        <TableCell>{subfamily.family_name || "-"}</TableCell>
                        <TableCell>
                          <ActionButtons>
                            <IconButton
                              variant="edit"
                              onClick={() => handleEditSubfamily(subfamily)}
                            >
                              <Edit />
                            </IconButton>
                            <IconButton
                              variant="delete"
                              onClick={() => handleDeleteSubfamily(subfamily.id)}
                            >
                              <Trash />
                            </IconButton>
                          </ActionButtons>
                        </TableCell>
                      </tr>
                    ))}
                  </tbody>
                </Table>

                {/* Paginación */}
                {subfamiliesTotalPages > 1 && (
                  <div style={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    gap: '10px',
                    marginTop: '30px',
                    padding: '20px'
                  }}>
                    <button
                      onClick={() => handleSubfamiliesPageChange(subfamiliesCurrentPage - 1)}
                      disabled={subfamiliesCurrentPage === 1}
                      style={{
                        padding: '10px 15px',
                        background: subfamiliesCurrentPage === 1 ? '#9E9E9E' : '#2A3A53',
                        color: 'white',
                        border: 'none',
                        borderRadius: '8px',
                        fontSize: '14px',
                        fontWeight: '600',
                        cursor: subfamiliesCurrentPage === 1 ? 'not-allowed' : 'pointer',
                        transition: 'all 0.3s ease',
                        boxShadow: subfamiliesCurrentPage === 1 ? 'none' : '0 3px 10px rgba(42, 58, 83, 0.3)'
                      }}
                      onMouseEnter={(e) => {
                        if (subfamiliesCurrentPage !== 1) {
                          e.currentTarget.style.transform = 'translateY(-2px)';
                          e.currentTarget.style.boxShadow = '0 5px 15px rgba(42, 58, 83, 0.4)';
                          e.currentTarget.style.background = '#1e2a3a';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (subfamiliesCurrentPage !== 1) {
                          e.currentTarget.style.transform = 'translateY(0)';
                          e.currentTarget.style.boxShadow = '0 3px 10px rgba(42, 58, 83, 0.3)';
                          e.currentTarget.style.background = '#2A3A53';
                        }
                      }}
                    >
                      ← Anterior
                    </button>
                    
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}>
                      {getPageNumbers(subfamiliesCurrentPage, subfamiliesTotalPages).map((page, index) => (
                        page === '...' ? (
                          <span
                            key={`ellipsis-${index}`}
                            style={{
                              width: '40px',
                              height: '40px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              color: '#6c757d',
                              fontSize: '16px',
                              fontWeight: '600'
                            }}
                          >
                            ...
                          </span>
                        ) : (
                          <button
                            key={page}
                            onClick={() => handleSubfamiliesPageChange(page as number)}
                            style={{
                              width: '40px',
                              height: '40px',
                              background: page === subfamiliesCurrentPage 
                                ? 'linear-gradient(135deg, #FDBF37 0%, #FDBF37 100%)' 
                                : 'white',
                              color: page === subfamiliesCurrentPage ? 'white' : '#2c3e50',
                              border: page === subfamiliesCurrentPage ? 'none' : '2px solid #e9ecef',
                              borderRadius: '50%',
                              fontSize: '14px',
                              fontWeight: '600',
                              cursor: 'pointer',
                              transition: 'all 0.3s ease',
                              boxShadow: page === subfamiliesCurrentPage 
                                ? '0 4px 12px rgba(255, 193, 7, 0.4)' 
                                : '0 2px 6px rgba(0, 0, 0, 0.1)'
                            }}
                            onMouseEnter={(e) => {
                              if (page !== subfamiliesCurrentPage) {
                                e.currentTarget.style.background = '#f8f9fa';
                                e.currentTarget.style.transform = 'scale(1.1)';
                              }
                            }}
                            onMouseLeave={(e) => {
                              if (page !== subfamiliesCurrentPage) {
                                e.currentTarget.style.background = 'white';
                                e.currentTarget.style.transform = 'scale(1)';
                              }
                            }}
                          >
                            {page}
                          </button>
                        )
                      ))}
                    </div>
                    
                    <button
                      onClick={() => handleSubfamiliesPageChange(subfamiliesCurrentPage + 1)}
                      disabled={subfamiliesCurrentPage >= subfamiliesTotalPages}
                      style={{
                        padding: '10px 15px',
                        background: subfamiliesCurrentPage >= subfamiliesTotalPages ? '#9E9E9E' : '#2A3A53',
                        color: 'white',
                        border: 'none',
                        borderRadius: '8px',
                        fontSize: '14px',
                        fontWeight: '600',
                        cursor: subfamiliesCurrentPage >= subfamiliesTotalPages ? 'not-allowed' : 'pointer',
                        transition: 'all 0.3s ease',
                        boxShadow: subfamiliesCurrentPage >= subfamiliesTotalPages ? 'none' : '0 3px 10px rgba(42, 58, 83, 0.3)'
                      }}
                      onMouseEnter={(e) => {
                        if (subfamiliesCurrentPage < subfamiliesTotalPages) {
                          e.currentTarget.style.transform = 'translateY(-2px)';
                          e.currentTarget.style.boxShadow = '0 5px 15px rgba(42, 58, 83, 0.4)';
                          e.currentTarget.style.background = '#1e2a3a';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (subfamiliesCurrentPage < subfamiliesTotalPages) {
                          e.currentTarget.style.transform = 'translateY(0)';
                          e.currentTarget.style.boxShadow = '0 3px 10px rgba(42, 58, 83, 0.3)';
                          e.currentTarget.style.background = '#2A3A53';
                        }
                      }}
                    >
                      Siguiente →
                    </button>
                  </div>
                )}
              </div>
            )}

            {activeTab === "slider" && hasPermission('slider') && (
              <div>
                <ActionButton onClick={handleAddSliderItem}>
                  <Plus /> Agregar Elemento al Slider
                </ActionButton>

                <Table>
                  <thead>
                    <tr>
                      <TableHeader>Imagen</TableHeader>
                      <TableHeader>Título</TableHeader>
                      <TableHeader>Descripción</TableHeader>
                      <TableHeader>Estado</TableHeader>
                      <TableHeader>Acciones</TableHeader>
                    </tr>
                  </thead>
                  <tbody>
                    {sliderItems.map((item) => (
                      <tr key={item.id}>
                        <TableCell>
                          <SliderImage
                            src={getImageUrl(item.image_url)}
                            alt={item.title}
                          />
                        </TableCell>
                        <TableCell>{item.title}</TableCell>
                        <TableCell>{item.description}</TableCell>
                        <TableCell>
                          {item.active ? "Activo" : "Inactivo"}
                        </TableCell>
                        <TableCell>
                          <ActionButtons>
                            <IconButton
                              variant="edit"
                              onClick={() => handleEditSliderItem(item)}
                            >
                              <Edit />
                            </IconButton>
                            <IconButton
                              variant="delete"
                              onClick={() => handleDeleteSliderItem(item.id)}
                            >
                              <Trash />
                            </IconButton>
                          </ActionButtons>
                        </TableCell>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            )}

            {activeTab === "settings" && hasPermission('settings') && (
              <div>
                <h2>Configuraciones del Sistema</h2>
                <Form onSubmit={handleSettingsSubmit}>
                  {/* Configuración de recuperación por correo (Gmail) */}
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '20px' }}>
                    <FormGroup>
                      <Label htmlFor="smtp_email">Correo remitente (Gmail)</Label>
                      <Input
                        type="email"
                        id="smtp_email"
                        name="smtp_email"
                        value={localSettings.smtp_email || ''}
                        onChange={(e) =>
                          setLocalSettings((prev) => ({
                            ...prev,
                            smtp_email: e.target.value,
                          }))
                        }
                        placeholder="tu-correo@gmail.com"
                      />
                      <small>
                        Correo Gmail que enviará el código de verificación.
                      </small>
                    </FormGroup>

                    <FormGroup>
                      <Label htmlFor="smtp_app_password">App Password de Google</Label>
                      <Input
                        type="password"
                        id="smtp_app_password"
                        name="smtp_app_password"
                        value={localSettings.smtp_app_password || ''}
                        onChange={(e) =>
                          setLocalSettings((prev) => ({
                            ...prev,
                            smtp_app_password: e.target.value,
                          }))
                        }
                        placeholder="Código de verificación de 16 caracteres"
                      />
                      <small>
                        Genera un App Password en tu cuenta Google (Seguridad → Contraseñas de aplicaciones).
                      </small>
                    </FormGroup>
                  </div>

                  <FormGroup>
                    <Label htmlFor="whatsapp_number">Número de WhatsApp</Label>
                    <Input
                      type="text"
                      id="whatsapp_number"
                      name="whatsapp_number"
                      value={localSettings.whatsapp_number || '+51'}
                      onChange={(e) => {
                        const value = e.target.value;
                        // Asegurar que siempre comience con +51
                        if (!value.startsWith('+51')) {
                          setLocalSettings((prev) => ({
                            ...prev,
                            whatsapp_number: '+51',
                          }));
                          return;
                        }
                        // Permitir solo números después de +51, máximo 9 dígitos
                        const phoneNumber = value.slice(3); // Remover +51
                        const cleanNumber = phoneNumber.replace(/\D/g, ''); // Solo números
                        if (cleanNumber.length <= 9) {
                          setLocalSettings((prev) => ({
                            ...prev,
                            whatsapp_number: '+51' + cleanNumber,
                          }));
                        }
                      }}
                      placeholder="+51999999999"
                      maxLength={12}
                      pattern="\+51[0-9]{9}"
                      title="Ingrese un número de WhatsApp válido de Perú (+51 seguido de 9 dígitos)"
                      required
                    />
                    <small>
                      Formato: +código_país + número (ej: +51999999999)
                    </small>
                  </FormGroup>

                  {/* Configuración de colores del header */}
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginTop: '20px' }}>
                    <FormGroup>
                      <Label htmlFor="header_primary_color">Color Principal del Header</Label>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <Input
                          type="color"
                          id="header_primary_color"
                          name="header_primary_color"
                          value={localSettings.header_primary_color || '#FDBF37'}
                          onChange={(e) =>
                             setLocalSettings((prev) => ({
                               ...prev,
                               header_primary_color: e.target.value,
                             }))
                           }
                          style={{ width: '60px', height: '40px', padding: '2px', border: '1px solid #ccc', borderRadius: '4px' }}
                        />
                        <Input
                          type="text"
                          value={localSettings.header_primary_color || '#FDBF37'}
                          onChange={(e) =>
                             setLocalSettings((prev) => ({
                               ...prev,
                               header_primary_color: e.target.value,
                             }))
                           }
                          placeholder="#FDBF37"
                          style={{ flex: 1 }}
                        />
                      </div>
                      <small>
                        Color principal del header
                      </small>
                    </FormGroup>

                    <FormGroup>
                      <Label htmlFor="header_secondary_color">Color Secundario del Header</Label>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <Input
                          type="color"
                          id="header_secondary_color"
                          name="header_secondary_color"
                          value={localSettings.header_secondary_color || '#242A5B'}
                          onChange={(e) =>
                             setLocalSettings((prev) => ({
                               ...prev,
                               header_secondary_color: e.target.value,
                             }))
                           }
                          style={{ width: '60px', height: '40px', padding: '2px', border: '1px solid #ccc', borderRadius: '4px' }}
                        />
                        <Input
                          type="text"
                          value={localSettings.header_secondary_color || '#242A5B'}
                          onChange={(e) =>
                             setLocalSettings((prev) => ({
                               ...prev,
                               header_secondary_color: e.target.value,
                             }))
                           }
                          placeholder="#242A5B"
                          style={{ flex: 1 }}
                        />
                      </div>
                      <small>
                        Color secundario del header
                      </small>
                    </FormGroup>
                  </div>

                  {/* Contenedor de logos en columnas */}
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginTop: '20px' }}>
                    <FormGroup>
                      <Label htmlFor="desktop_logo">Logo para Escritorio</Label>
                      <Input
                        type="file"
                        id="desktop_logo"
                        name="desktop_logo"
                        accept="image/*"
                        onChange={(e) => handleLogoUpload(e, 'desktop')}
                      />
                      {pendingLogos.desktop && (
                        <div style={{
                          marginTop: '8px',
                          padding: '8px 12px',
                          backgroundColor: '#e8f5e8',
                          border: '1px solid #4caf50',
                          borderRadius: '4px',
                          fontSize: '14px',
                          color: '#2e7d32'
                        }}>
                          ✓ Archivo seleccionado: {pendingLogos.desktop.name}
                        </div>
                      )}
                      {localSettings.desktop_logo && (
                        <div style={{ 
                          marginTop: '10px', 
                          padding: '20px',
                          backgroundColor: '#f5f5f5',
                          borderRadius: '8px',
                          border: '1px solid #e0e0e0',
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'center',
                          minHeight: '150px'
                        }}>
                          <img 
                            src={pendingLogos.desktop ? URL.createObjectURL(pendingLogos.desktop) : getImageUrl(localSettings.desktop_logo || null)} 
                            alt="Logo Escritorio" 
                            style={{ 
                              maxWidth: '300px', 
                              maxHeight: '150px', 
                              objectFit: 'contain',
                              display: 'block'
                            }}
                            onError={(e) => {
                              const target = e.target as HTMLImageElement;
                              target.src = '/placeholder-image.svg';
                            }}
                          />
                        </div>
                      )}
                      <small>
                        Imagen del logo que se mostrará en dispositivos de escritorio
                      </small>
                    </FormGroup>

                    <FormGroup>
                      <Label htmlFor="mobile_logo">Logo para Móvil</Label>
                      <Input
                        type="file"
                        id="mobile_logo"
                        name="mobile_logo"
                        accept="image/*"
                        onChange={(e) => handleLogoUpload(e, 'mobile')}
                      />
                      {pendingLogos.mobile && (
                        <div style={{
                          marginTop: '8px',
                          padding: '8px 12px',
                          backgroundColor: '#e8f5e8',
                          border: '1px solid #4caf50',
                          borderRadius: '4px',
                          fontSize: '14px',
                          color: '#2e7d32'
                        }}>
                          ✓ Archivo seleccionado: {pendingLogos.mobile.name}
                        </div>
                      )}
                      {localSettings.mobile_logo && (
                        <div style={{ 
                          marginTop: '10px', 
                          padding: '20px',
                          backgroundColor: '#f5f5f5',
                          borderRadius: '8px',
                          border: '1px solid #e0e0e0',
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'center',
                          minHeight: '150px'
                        }}>
                          <img 
                            src={pendingLogos.mobile ? URL.createObjectURL(pendingLogos.mobile) : getImageUrl(localSettings.mobile_logo || null)} 
                            alt="Logo Móvil" 
                            style={{ 
                              maxWidth: '300px', 
                              maxHeight: '150px', 
                              objectFit: 'contain',
                              display: 'block'
                            }}
                            onError={(e) => {
                              const target = e.target as HTMLImageElement;
                              target.src = '/placeholder-image.svg';
                            }}
                          />
                        </div>
                      )}
                      <small>
                        Imagen del logo que se mostrará en dispositivos móviles
                      </small>
                    </FormGroup>
                  </div>

                  <ButtonGroup>
                    <Button
                      type="submit"
                      variant="primary"
                      disabled={settingsLoading}
                    >
                      {settingsLoading
                        ? "Guardando..."
                        : "Guardar Configuraciones"}
                    </Button>
                  </ButtonGroup>
                </Form>
              </div>
            )}

            {activeTab === "daily-deals" && hasPermission('daily-deals') && (
              <div>
                <ActionButton onClick={handleAddDailyDeal}>
                  <Plus /> Agregar Oferta del Día
                </ActionButton>

                {/* Filtros y búsqueda */}
                <div
                  style={{
                    display: "flex",
                    gap: "1rem",
                    marginBottom: "2rem",
                    flexWrap: "wrap",
                    alignItems: "center",
                    justifyContent: "flex-end",
                  }}
                >
                  <input
                    type="text"
                    placeholder="Buscar ofertas..."
                    value={dailyDealSearchTerm}
                    onChange={(e) => setDailyDealSearchTerm(e.target.value)}
                    style={{
                      padding: "0.75rem",
                      border: "2px solid #ecf0f1",
                      borderRadius: "8px",
                      fontSize: "1rem",
                      minWidth: "250px",
                    }}
                  />
                </div>

                {/* Información de paginación */}
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: "1rem",
                    padding: "1rem",
                    backgroundColor: "#f8f9fa",
                    borderRadius: "8px",
                  }}
                >
                  <span>
                    Mostrando {dailyDeals.length} de {dailyDealsTotalItems} ofertas diarias
                    {dailyDealSearchTerm ? " (filtradas)" : ""}
                  </span>
                  <span>
                    Página {dailyDealsCurrentPage} de {dailyDealsTotalPages}
                  </span>
                </div>

                <Table>
                  <thead>
                    <tr>
                      <TableHeader>Producto</TableHeader>
                      <TableHeader>Estado</TableHeader>
                      <TableHeader>Imagen</TableHeader>
                      <TableHeader>Acciones</TableHeader>
                    </tr>
                  </thead>
                  <tbody>
                    {dailyDeals.map((deal) => (
                      <tr 
                        key={deal.id}
                      >
                        <TableCell>{deal.product ? deal.product.name : "Producto no disponible"}</TableCell>
                        <TableCell>
                          <span
                            style={{
                              padding: "0.25rem 0.5rem",
                              borderRadius: "4px",
                              fontSize: "0.75rem",
                              fontWeight: "600",
                              backgroundColor: deal.active ? "#d4edda" : "#f8d7da",
                              color: deal.active ? "#155724" : "#721c24",
                            }}
                          >
                            {deal.active ? "Activo" : "Inactivo"}
                          </span>
                        </TableCell>
                        <TableCell>
                          <ProductImage
                            src={
                              deal.product
                                ? getImageUrl(deal.product.image_url)
                                : "/placeholder-image.jpg"
                            }
                            alt={
                              deal.product
                                ? deal.product.name
                                : "Producto no disponible"
                            }
                          />
                        </TableCell>
                        <TableCell>
                          <ActionButtons>
                            <IconButton
                              variant="edit"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleEditDailyDeal(deal);
                              }}
                            >
                              <Edit />
                            </IconButton>
                            <IconButton
                              variant="delete"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteDailyDeal(deal.id);
                              }}
                            >
                              <Trash />
                            </IconButton>
                          </ActionButtons>
                        </TableCell>
                      </tr>
                    ))}
                  </tbody>
                </Table>

                {/* Paginación con el mismo estilo que familias */}
                {dailyDealsTotalPages > 1 && (
                  <div style={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    gap: '15px',
                    marginTop: '2rem',
                    padding: '1rem'
                  }}>
                    <button
                      onClick={() => handleDailyDealsPageChange(dailyDealsCurrentPage - 1)}
                      disabled={dailyDealsCurrentPage <= 1}
                      style={{
                        padding: '10px 15px',
                        background: dailyDealsCurrentPage <= 1 ? '#9E9E9E' : '#2A3A53',
                        color: 'white',
                        border: 'none',
                        borderRadius: '8px',
                        fontSize: '14px',
                        fontWeight: '600',
                        cursor: dailyDealsCurrentPage <= 1 ? 'not-allowed' : 'pointer',
                        transition: 'all 0.3s ease',
                        boxShadow: dailyDealsCurrentPage <= 1 ? 'none' : '0 3px 10px rgba(42, 58, 83, 0.3)'
                      }}
                      onMouseEnter={(e) => {
                        if (dailyDealsCurrentPage > 1) {
                          e.currentTarget.style.transform = 'translateY(-2px)';
                          e.currentTarget.style.boxShadow = '0 5px 15px rgba(42, 58, 83, 0.4)';
                          e.currentTarget.style.background = '#1e2a3a';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (dailyDealsCurrentPage > 1) {
                          e.currentTarget.style.transform = 'translateY(0)';
                          e.currentTarget.style.boxShadow = '0 3px 10px rgba(42, 58, 83, 0.3)';
                          e.currentTarget.style.background = '#2A3A53';
                        }
                      }}
                    >
                      ← Anterior
                    </button>
                    
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}>
                      {getPageNumbers(dailyDealsCurrentPage, dailyDealsTotalPages).map((page, index) => (
                        page === '...' ? (
                          <span
                            key={`ellipsis-${index}`}
                            style={{
                              width: '40px',
                              height: '40px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              color: '#6c757d',
                              fontSize: '16px',
                              fontWeight: '600'
                            }}
                          >
                            ...
                          </span>
                        ) : (
                          <button
                            key={page}
                            onClick={() => handleDailyDealsPageChange(page as number)}
                            style={{
                              width: '40px',
                              height: '40px',
                              background: page === dailyDealsCurrentPage 
                                ? 'linear-gradient(135deg, #FDBF37 0%, #FDBF37 100%)' 
                                : 'white',
                              color: page === dailyDealsCurrentPage ? 'white' : '#2c3e50',
                              border: page === dailyDealsCurrentPage ? 'none' : '2px solid #e9ecef',
                              borderRadius: '50%',
                              fontSize: '14px',
                              fontWeight: '600',
                              cursor: 'pointer',
                              transition: 'all 0.3s ease',
                              boxShadow: page === dailyDealsCurrentPage 
                                ? '0 4px 12px rgba(255, 193, 7, 0.4)' 
                                : '0 2px 6px rgba(0, 0, 0, 0.1)'
                            }}
                            onMouseEnter={(e) => {
                              if (page !== dailyDealsCurrentPage) {
                                e.currentTarget.style.background = '#f8f9fa';
                                e.currentTarget.style.transform = 'scale(1.1)';
                              }
                            }}
                            onMouseLeave={(e) => {
                              if (page !== dailyDealsCurrentPage) {
                                e.currentTarget.style.background = 'white';
                                e.currentTarget.style.transform = 'scale(1)';
                              }
                            }}
                          >
                            {page}
                          </button>
                        )
                      ))}
                    </div>
                    
                    <button
                      onClick={() => handleDailyDealsPageChange(dailyDealsCurrentPage + 1)}
                      disabled={dailyDealsCurrentPage >= dailyDealsTotalPages}
                      style={{
                        padding: '10px 15px',
                        background: dailyDealsCurrentPage >= dailyDealsTotalPages ? '#9E9E9E' : '#2A3A53',
                        color: 'white',
                        border: 'none',
                        borderRadius: '8px',
                        fontSize: '14px',
                        fontWeight: '600',
                        cursor: dailyDealsCurrentPage >= dailyDealsTotalPages ? 'not-allowed' : 'pointer',
                        transition: 'all 0.3s ease',
                        boxShadow: dailyDealsCurrentPage >= dailyDealsTotalPages ? 'none' : '0 3px 10px rgba(42, 58, 83, 0.3)'
                      }}
                      onMouseEnter={(e) => {
                        if (dailyDealsCurrentPage < dailyDealsTotalPages) {
                          e.currentTarget.style.transform = 'translateY(-2px)';
                          e.currentTarget.style.boxShadow = '0 5px 15px rgba(42, 58, 83, 0.4)';
                          e.currentTarget.style.background = '#1e2a3a';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (dailyDealsCurrentPage < dailyDealsTotalPages) {
                          e.currentTarget.style.transform = 'translateY(0)';
                          e.currentTarget.style.boxShadow = '0 3px 10px rgba(42, 58, 83, 0.3)';
                          e.currentTarget.style.background = '#2A3A53';
                        }
                      }}
                    >
                      Siguiente →
                    </button>
                  </div>
                )}
              </div>
            )}

            {activeTab === "banners" && hasPermission('banners') && <BannersTab />}
            {activeTab === "excel-upload" && hasPermission('excel-upload') && <ExcelUpload />}
            {activeTab === "spaces" && hasPermission('spaces') && <EspaciosSection />}

            {activeTab === "combos" && hasPermission('combos') && (
              <CombosTab />
            )}

            {activeTab === "footer" && hasPermission('footer') && <FooterTab />}

          </ContentCard>

          <Modal show={showModal}>
            <ModalContent>
              <h2>{editingProduct ? "Editar Producto" : "Agregar Producto"}</h2>
              <Form onSubmit={handleSubmit}>
                <FormGroup>
                  <Label>Nombre del Producto</Label>
                  <Input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                  />
                </FormGroup>

                <FormGroup>
                  <Label>Descripción</Label>
                  <TextArea
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    required
                  />
                </FormGroup>

                <FormGroup>
                  <Label>Categoría</Label>
                  <Select
                    name="category_id"
                    value={formData.category_id}
                    onChange={handleInputChange}
                    required
                  >
                    <option value="">Seleccionar categoría</option>
                    {categories.map((category) => (
                      <option key={category.id} value={category.id}>
                        {category.name}
                      </option>
                    ))}
                  </Select>
                </FormGroup>

                <FormGroup>
                  <Label>Familia</Label>
                  <FamilySelector
                    categoryId={formData.category_id}
                    selectedFamilyId={formData.family_id}
                    onFamilyChange={(familyId) => {
                      setFormData(prev => ({
                        ...prev,
                        family_id: familyId,
                        subfamily_id: "" // Limpiar subfamilia cuando cambia la familia
                      }));
                      // Cargar subfamilias si se selecciona una familia
                      if (familyId) {
                        fetchSubfamilies(familyId);
                      } else {
                        setFilteredSubfamilies([]);
                      }
                    }}
                    disabled={!formData.category_id}
                    placeholder="Buscar familia..."
                  />
                </FormGroup>

                <FormGroup>
                  <Label>Subfamilia</Label>
                  <SubfamilySelector
                    familyId={formData.family_id}
                    selectedSubfamilyId={formData.subfamily_id}
                    onSubfamilyChange={(subfamilyId) => {
                      setFormData(prev => ({
                        ...prev,
                        subfamily_id: subfamilyId
                      }));
                    }}
                    disabled={!formData.family_id}
                    placeholder="Buscar subfamilia..."
                  />
                </FormGroup>

                <FormGroup>
                  <Label>Marca</Label>
                  <Input
                    type="text"
                    name="brand"
                    value={formData.brand || ""}
                    onChange={handleInputChange}
                    placeholder="Opcional"
                  />
                </FormGroup>

                <FormGroup>
                  <Label>Modelo</Label>
                  <Input
                    type="text"
                    name="model"
                    value={formData.model || ""}
                    onChange={handleInputChange}
                    placeholder="Opcional"
                  />
                </FormGroup>

                <FormGroup>
                  <Label>Imagen Principal del Producto</Label>
                  <Input
                    type="file"
                    name="image"
                    onChange={handleInputChange}
                    accept="image/*"
                  />
                  {formData.image_url && (
                    <div style={{ marginTop: "10px" }}>
                      <img
                        src={getImageUrl(formData.image_url)}
                        alt="Vista previa"
                        style={{ maxWidth: "100px", maxHeight: "100px" }}
                      />
                      <p>URL actual: {formData.image_url}</p>
                    </div>
                  )}
                </FormGroup>

                <FormGroup>
                  <Label>Imágenes Adicionales</Label>
                  <Input
                    type="file"
                    name="additional_images"
                    onChange={handleInputChange}
                    accept="image/*"
                    multiple
                  />
                  {additionalImageFiles.length > 0 && (
                    <div style={{ marginTop: "10px" }}>
                      <p>
                        Imágenes adicionales seleccionadas:{" "}
                        {additionalImageFiles.length}
                      </p>
                      <div
                        style={{ display: "flex", flexWrap: "wrap", gap: "10px" }}
                      >
                        {additionalImageFiles.map((file, index) => (
                          <div key={index} style={{ position: "relative" }}>
                            <img
                              src={URL.createObjectURL(file)}
                              alt={`Imagen adicional ${index + 1}`}
                              style={{
                                width: "80px",
                                height: "80px",
                                objectFit: "cover",
                              }}
                            />
                            <button
                              type="button"
                              style={{
                                position: "absolute",
                                top: "-8px",
                                right: "-8px",
                                background: "#ff4d4f",
                                color: "white",
                                border: "none",
                                borderRadius: "50%",
                                width: "20px",
                                height: "20px",
                                cursor: "pointer",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                fontSize: "12px",
                              }}
                              onClick={() => {
                                const newFiles = [...additionalImageFiles];
                                newFiles.splice(index, 1);
                                setAdditionalImageFiles(newFiles);
                              }}
                            >
                              ×
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </FormGroup>

                <FormGroup>
                  <Label>Stock</Label>
                  <Input
                    type="number"
                    name="stock"
                    value={formData.stock}
                    onChange={handleInputChange}
                    min="0"
                    required
                  />
                </FormGroup>

                <FormGroup>
                  <Label>
                    <Input
                      type="checkbox"
                      name="featured"
                      checked={formData.featured}
                      onChange={handleInputChange}
                      style={{ width: "auto", marginRight: "0.5rem" }}
                    />
                    Producto Destacado
                  </Label>
                </FormGroup>

                {/* Se eliminaron los FormGroup de isNew y onSale */}

                <ButtonGroup>
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={() => setShowModal(false)}
                  >
                    Cancelar
                  </Button>
                  <Button type="submit" variant="primary">
                    {editingProduct ? "Actualizar" : "Agregar"}
                  </Button>
                </ButtonGroup>
              </Form>
            </ModalContent>
          </Modal>

          {/* Modal para categorías */}
          <Modal show={showCategoryModal}>
            <ModalContent>
              <h2>
                {editingCategory ? "Editar Categoría" : "Agregar Categoría"}
              </h2>
              <Form onSubmit={handleCategorySubmit}>
                <FormGroup>
                  <Label>Nombre de la Categoría</Label>
                  <Input
                    type="text"
                    name="name"
                    value={categoryFormData.name}
                    onChange={handleCategoryInputChange}
                    required
                  />
                </FormGroup>

                <FormGroup>
                  <Label>Descripción</Label>
                  <TextArea
                    name="description"
                    value={categoryFormData.description}
                    onChange={handleCategoryInputChange}
                    placeholder="Opcional"
                  />
                </FormGroup>

                <FormGroup>
                  <Label>Imagen de la Categoría</Label>
                  <div style={{
                    fontSize: '13px',
                    color: '#6c757d',
                    fontWeight: '600',
                    marginBottom: '8px',
                    padding: '8px 12px',
                    background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
                    borderRadius: '8px',
                    border: '1px solid #dee2e6',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                  }}>
                    🏷️ Tamaño recomendado: 80 × 80 píxeles
                  </div>
                  <Input
                    type="file"
                    name="image"
                    onChange={handleCategoryInputChange}
                    accept="image/*"
                    required={!editingCategory}
                  />
                  {editingCategory && editingCategory.image_url && (
                    <div style={{ marginTop: "10px" }}>
                      <img
                        src={getImageUrl(editingCategory.image_url)}
                        alt="Vista previa"
                        style={{ maxWidth: "100px", maxHeight: "100px" }}
                      />
                    </div>
                  )}
                </FormGroup>

                <FormGroup>
                  <Label>
                    Productos destacados para el carrusel de inicio (máx. 5)
                  </Label>

                  {/* Mensaje de advertencia para productos duplicados */}
                  {duplicateProductMessage && (
                    <div
                      style={{
                        marginBottom: "10px",
                        padding: "10px",
                        backgroundColor: "#fff3cd",
                        border: "1px solid #ffeaa7",
                        borderRadius: "6px",
                        fontSize: "0.9rem",
                        color: "#856404",
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                      }}
                    >
                      ⚠️ <strong>{duplicateProductMessage}</strong>
                      <button
                        type="button"
                        onClick={() => setDuplicateProductMessage("")}
                        style={{
                          marginLeft: "auto",
                          background: "none",
                          border: "none",
                          fontSize: "1.2rem",
                          cursor: "pointer",
                          color: "#856404",
                        }}
                      >
                        ×
                      </button>
                    </div>
                  )}

                  {/* Contador de productos seleccionados (sin funcionalidad de lista) */}
                  <div
                    style={{
                      marginBottom: "15px",
                      padding: "12px",
                      backgroundColor: "#f8f9fa",
                      border: "1px solid #dee2e6",
                      borderRadius: "8px",
                    }}
                  >
                    {/* Solo mostrar el contador, sin botón clickeable */}
                    <div
                      style={{
                        width: "100%",
                        padding: "10px",
                        backgroundColor:
                          tempSelectedProducts.length > 0 ? "#007bff" : "#6c757d",
                        color: "white",
                        border: "none",
                        borderRadius: "6px",
                        fontSize: "1rem",
                        fontWeight: "bold",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        transition: "all 0.3s ease",
                      }}
                    >
                      📋 Productos Seleccionados ({tempSelectedProducts.length}/5)
                    </div>

                    {/* Comentado: Lista desplegable de productos seleccionados */}
                    {/*
                {showSelectedProducts && (
                  <div style={{
                    marginTop: '10px',
                    maxHeight: '300px',
                    overflowY: 'auto',
                    border: '1px solid #dee2e6',
                    borderRadius: '6px',
                    backgroundColor: 'white'
                  }}>
                    {tempSelectedProducts.length === 0 ? (
                      <div style={{
                        padding: '20px',
                        textAlign: 'center',
                        color: '#6c757d',
                        fontStyle: 'italic'
                      }}>
                        🚫 No hay productos seleccionados para el carrusel
                      </div>
                    ) : (
                      tempSelectedProducts.map((productId, index) => {
                        const product = products.find(p => p.id === productId);
                        if (!product) return null;
                        
                        return (
                          <div
                            key={`${productId}-${index}`}
                            style={{
                              padding: '12px',
                              borderBottom: index < tempSelectedProducts.length - 1 ? '1px solid #eee' : 'none',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '12px',
                              backgroundColor: index % 2 === 0 ? '#f8f9fa' : 'white'
                            }}
                          >
                            <div style={{
                              backgroundColor: '#007bff',
                              color: 'white',
                              borderRadius: '50%',
                              width: '30px',
                              height: '30px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontSize: '0.9rem',
                              fontWeight: 'bold',
                              flexShrink: 0
                            }}>
                              {index + 1}
                            </div>
                            
                            <ProductMiniImg 
                              src={getImageUrl(product.image_url)} 
                              alt={product.name}
                              style={{
                                width: '50px',
                                height: '50px',
                                objectFit: 'cover',
                                borderRadius: '6px',
                                flexShrink: 0
                              }}
                            />
                            
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{
                                fontWeight: 'bold',
                                color: '#333',
                                marginBottom: '4px',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap'
                              }}>
                                {product.name}
                              </div>
                              <div style={{
                                fontSize: '0.85rem',
                                color: '#666'
                              }}>
                                Categoría: {categories.find(c => c.id === product.category_id)?.name || 'Sin categoría'}
                              </div>
                            </div>
                            
                            <div style={{
                              display: 'flex',
                              gap: '8px',
                              flexShrink: 0
                            }}>
                              {index > 0 && (
                                <button
                                  type="button"
                                  onClick={() => {
                                    const newProducts = [...tempSelectedProducts];
                                    [newProducts[index], newProducts[index - 1]] = [newProducts[index - 1], newProducts[index]];
                                    setTempSelectedProducts(newProducts);
                                  }}
                                  style={{
                                    padding: '6px 8px',
                                    backgroundColor: '#28a745',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '4px',
                                    cursor: 'pointer',
                                    fontSize: '0.8rem'
                                  }}
                                  title="Subir posición"
                                >
                                  ↑
                                </button>
                              )}
                              
                              {index < tempSelectedProducts.length - 1 && (
                                <button
                                  type="button"
                                  onClick={() => {
                                    const newProducts = [...tempSelectedProducts];
                                    [newProducts[index], newProducts[index + 1]] = [newProducts[index + 1], newProducts[index]];
                                    setTempSelectedProducts(newProducts);
                                  }}
                                  style={{
                                    padding: '6px 8px',
                                    backgroundColor: '#ffc107',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '4px',
                                    cursor: 'pointer',
                                    fontSize: '0.8rem'
                                  }}
                                  title="Bajar posición"
                                >
                                  ↓
                                </button>
                              )}
                              
                              <button
                                type="button"
                                onClick={() => {
                                  setTempSelectedProducts(prev => prev.filter((_, i) => i !== index));
                                  setDuplicateProductMessage('');
                                }}
                                style={{
                                  padding: '6px 8px',
                                  backgroundColor: '#dc3545',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '4px',
                                  cursor: 'pointer',
                                  fontSize: '0.8rem'
                                }}
                                title="Eliminar del carrusel"
                              >
                                🗑️
                              </button>
                            </div>
                          </div>
                        );
                      })
                    )}
                  </div>
                )}
                */}
                  </div>

                  <FormGroup>
                    <Label>Buscar productos para seleccionar</Label>
                    <Input
                      type="text"
                      placeholder="Buscar por nombre de producto..."
                      value={productSearchTerm}
                      onChange={(e) => setProductSearchTerm(e.target.value)}
                    />
                  </FormGroup>

                  <FeaturedProductsList>
                    {loadingAllProducts ? (
                      <div
                        style={{
                          padding: "20px",
                          textAlign: "center",
                          color: "#6c757d",
                        }}
                      >
                        🔄 Cargando productos de la categoría...
                      </div>
                    ) : (
                      <>
                        {(() => {
                          // Filtrar productos por búsqueda
                          const filteredProducts = allProducts
                            .filter((product) =>
                              product.name
                                .toLowerCase()
                                .includes(productSearchTerm.toLowerCase())
                            )
                            .sort((a, b) => {
                              // Ordenar: productos seleccionados primero, luego disponibles
                              const aSelected = tempSelectedProducts.some(
                                (selectedId) =>
                                  String(selectedId) === String(a.id)
                              );
                              const bSelected = tempSelectedProducts.some(
                                (selectedId) =>
                                  String(selectedId) === String(b.id)
                              );
                              if (aSelected && !bSelected) return -1;
                              if (!aSelected && bSelected) return 1;
                              return a.name.localeCompare(b.name);
                            });

                          // Calcular paginación
                          const totalFilteredProducts = filteredProducts.length;
                          const totalPages = Math.ceil(
                            totalFilteredProducts / productSelectorPerPage
                          );
                          const startIndex =
                            (productSelectorPage - 1) * productSelectorPerPage;
                          const endIndex = startIndex + productSelectorPerPage;
                          const currentPageProducts = filteredProducts.slice(
                            startIndex,
                            endIndex
                          );

                          return (
                            <>
                              {/* Información de paginación */}
                              <div
                                style={{
                                  fontSize: "0.85rem",
                                  color: "#6c757d",
                                  marginBottom: "10px",
                                  padding: "8px",
                                  backgroundColor: "#f8f9fa",
                                  borderRadius: "4px",
                                  textAlign: "center",
                                  border: "1px solid #e9ecef",
                                }}
                              >
                                📦 Mostrando {startIndex + 1}-
                                {Math.min(endIndex, totalFilteredProducts)} de{" "}
                                {totalFilteredProducts} productos
                                {productSearchTerm && (
                                  <span
                                    style={{
                                      marginLeft: "10px",
                                      fontStyle: "italic",
                                    }}
                                  >
                                    (filtrados por: "{productSearchTerm}")
                                  </span>
                                )}
                              </div>

                              {/* Lista de productos */}
                              {currentPageProducts.map((product) => {
                                // Convertir ambos IDs a string para comparación consistente
                                const productIdStr = String(product.id);
                                const isSelected = tempSelectedProducts.some(
                                  (selectedId) =>
                                    String(selectedId) === productIdStr
                                );
                                const isMaxReached =
                                  tempSelectedProducts.length >= 5;
                                const canSelect = !isSelected && !isMaxReached;

                                return (
                                  <FeaturedProductItem
                                    key={product.id}
                                    onClick={() => {
                                      if (isSelected) {
                                        // Si está seleccionado, quitarlo
                                        setTempSelectedProducts((prev) =>
                                          prev.filter(
                                            (pid) => String(pid) !== productIdStr
                                          )
                                        );
                                        setDuplicateProductMessage("");
                                      } else if (canSelect) {
                                        // Si no está seleccionado y se puede seleccionar, agregarlo
                                        setTempSelectedProducts((prev) => [
                                          ...prev,
                                          productIdStr,
                                        ]);
                                        setDuplicateProductMessage("");
                                      } else {
                                        // Si no se puede seleccionar, mostrar mensaje
                                        if (isMaxReached) {
                                          setDuplicateProductMessage(
                                            "Ya has seleccionado el máximo de 5 productos para el carrusel."
                                          );
                                        } else {
                                          setDuplicateProductMessage(
                                            "Este producto ya está seleccionado para el carrusel."
                                          );
                                        }
                                        setTimeout(
                                          () => setDuplicateProductMessage(""),
                                          3000
                                        );
                                      }
                                    }}
                                    disabled={!canSelect && !isSelected}
                                    style={{
                                      backgroundColor: isSelected
                                        ? "#e8f5e8"
                                        : !canSelect
                                          ? "#f5f5f5"
                                          : "white",
                                      border: isSelected
                                        ? "2px solid #4caf50"
                                        : !canSelect
                                          ? "1px solid #ccc"
                                          : "1px solid #ddd",
                                      opacity:
                                        !canSelect && !isSelected ? 0.6 : 1,
                                      cursor: isSelected
                                        ? "pointer"
                                        : canSelect
                                          ? "pointer"
                                          : "not-allowed",
                                      position: "relative",
                                      transition: "all 0.3s ease",
                                    }}
                                  >
                                    <Input
                                      type="checkbox"
                                      checked={isSelected}
                                      readOnly
                                      disabled={!canSelect && !isSelected}
                                      style={{
                                        cursor:
                                          isSelected || canSelect
                                            ? "pointer"
                                            : "not-allowed",
                                        accentColor: isSelected
                                          ? "#4caf50"
                                          : "#007bff",
                                      }}
                                    />
                                    <ProductMiniImg
                                      src={getImageUrl(product.image_url)}
                                      alt={product.name}
                                      style={{
                                        filter:
                                          !canSelect && !isSelected
                                            ? "grayscale(50%)"
                                            : "none",
                                      }}
                                    />
                                    <div style={{ flex: 1 }}>
                                      <span
                                        style={{
                                          fontWeight: isSelected
                                            ? "600"
                                            : "normal",
                                          color: isSelected
                                            ? "#2e7d32"
                                            : !canSelect
                                              ? "#999"
                                              : "#333",
                                        }}
                                      >
                                        {isSelected && "✓ "}
                                        {product.name}
                                      </span>
                                      <small
                                        style={{
                                          display: "block",
                                          color: "#666",
                                        }}
                                      >
                                        Categoría:{" "}
                                        {categories.find(
                                          (c) => c.id === product.category_id
                                        )?.name || "Sin categoría"}
                                      </small>

                                      {/* Indicadores de estado */}
                                      {isSelected && (
                                        <small
                                          style={{
                                            display: "block",
                                            color: "#4caf50",
                                            fontWeight: "bold",
                                            fontSize: "0.8rem",
                                          }}
                                        >
                                          ✓ Seleccionado para carrusel
                                        </small>
                                      )}

                                      {!canSelect && !isSelected && (
                                        <small
                                          style={{
                                            display: "block",
                                            color: "#dc3545",
                                            fontStyle: "italic",
                                            fontSize: "0.8rem",
                                          }}
                                        >
                                          🔒 Máximo alcanzado (5/5)
                                        </small>
                                      )}
                                    </div>

                                    {/* Indicador visual de posición para productos seleccionados */}
                                    {isSelected && (
                                      <div
                                        style={{
                                          position: "absolute",
                                          top: "5px",
                                          right: "5px",
                                          backgroundColor: "#4caf50",
                                          color: "white",
                                          borderRadius: "50%",
                                          width: "24px",
                                          height: "24px",
                                          display: "flex",
                                          alignItems: "center",
                                          justifyContent: "center",
                                          fontSize: "0.8rem",
                                          fontWeight: "bold",
                                          boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
                                        }}
                                      >
                                        {tempSelectedProducts.findIndex(
                                          (selectedId) =>
                                            String(selectedId) === productIdStr
                                        ) + 1}
                                      </div>
                                    )}
                                  </FeaturedProductItem>
                                );
                              })}

                              {/* Controles de paginación */}
                              {totalPages > 1 && (
                                <div
                                  style={{
                                    display: "flex",
                                    justifyContent: "center",
                                    alignItems: "center",
                                    gap: "10px",
                                    marginTop: "15px",
                                    padding: "10px",
                                    backgroundColor: "#f8f9fa",
                                    borderRadius: "8px",
                                    border: "1px solid #e9ecef",
                                  }}
                                >
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setProductSelectorPage((prev) =>
                                        Math.max(1, prev - 1)
                                      );
                                    }}
                                    disabled={productSelectorPage === 1}
                                    style={{
                                      padding: "8px 12px",
                                      backgroundColor:
                                        productSelectorPage === 1
                                          ? "#e9ecef"
                                          : "#007bff",
                                      color:
                                        productSelectorPage === 1
                                          ? "#6c757d"
                                          : "white",
                                      border: "none",
                                      borderRadius: "4px",
                                      cursor:
                                        productSelectorPage === 1
                                          ? "not-allowed"
                                          : "pointer",
                                      fontSize: "0.9rem",
                                    }}
                                  >
                                    ← Anterior
                                  </button>

                                  <span
                                    style={{
                                      fontSize: "0.9rem",
                                      color: "#495057",
                                      fontWeight: "500",
                                    }}
                                  >
                                    Página {productSelectorPage} de {totalPages}
                                  </span>

                                  <button
                                    type="button"
                                    onClick={() => {
                                      setProductSelectorPage((prev) =>
                                        Math.min(totalPages, prev + 1)
                                      );
                                    }}
                                    disabled={productSelectorPage === totalPages}
                                    style={{
                                      padding: "8px 12px",
                                      backgroundColor:
                                        productSelectorPage === totalPages
                                          ? "#e9ecef"
                                          : "#007bff",
                                      color:
                                        productSelectorPage === totalPages
                                          ? "#6c757d"
                                          : "white",
                                      border: "none",
                                      borderRadius: "4px",
                                      cursor:
                                        productSelectorPage === totalPages
                                          ? "not-allowed"
                                          : "pointer",
                                      fontSize: "0.9rem",
                                    }}
                                  >
                                    Siguiente →
                                  </button>
                                </div>
                              )}

                              {/* Mensaje cuando no hay productos */}
                              {currentPageProducts.length === 0 && (
                                <div
                                  style={{
                                    padding: "20px",
                                    textAlign: "center",
                                    color: "#6c757d",
                                    fontStyle: "italic",
                                  }}
                                >
                                  {productSearchTerm
                                    ? `No se encontraron productos que coincidan con "${productSearchTerm}"`
                                    : "No hay productos disponibles en esta página"}
                                </div>
                              )}
                            </>
                          );
                        })()}
                      </>
                    )}
                  </FeaturedProductsList>

                  {/* Contador y estado */}
                  {/*
              <div style={{ 
                fontSize: '0.95rem', 
                marginTop: '8px',
                padding: '8px',
                backgroundColor: tempSelectedProducts.length === 5 ? '#e8f5e8' : '#f8f9fa',
                border: `1px solid ${tempSelectedProducts.length === 5 ? '#4caf50' : '#dee2e6'}`,
                borderRadius: '6px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between'
              }}>
                <span style={{ 
                  color: tempSelectedProducts.length === 5 ? '#2e7d32' : '#b85c00',
                  fontWeight: 'bold'
                }}>
                  {tempSelectedProducts.length} / 5 productos seleccionados
                </span>
                {tempSelectedProducts.length === 5 && (
                  <span style={{ color: '#4caf50', fontWeight: 'bold' }}>
                    ✓ Completo
                  </span>
                )}
                {tempSelectedProducts.length === 0 && (
                  <span style={{ color: '#6c757d', fontStyle: 'italic' }}>
                    Ningún producto seleccionado
                  </span>
                )}
              </div>
              */}
                </FormGroup>

                <FormGroup>
                  <Label>
                    <Input
                      type="checkbox"
                      name="show_in_home"
                      checked={categoryFormData.show_in_home}
                      onChange={handleCategoryInputChange}
                      style={{ width: "auto", marginRight: "0.5rem" }}
                    />
                    Mostrar esta categoría en el carrusel de inicio
                  </Label>
                </FormGroup>

                <ButtonGroup>
                  <Button type="submit" variant="primary">
                    {editingCategory ? "Actualizar" : "Crear"}
                  </Button>
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={() => setShowCategoryModal(false)}
                  >
                    Cancelar
                  </Button>
                </ButtonGroup>
              </Form>
            </ModalContent>
          </Modal>

          {/* Modal para slider */}
          <Modal show={showSliderModal}>
            <ModalContent>
              <h2>
                {editingSliderItem
                  ? "Editar Elemento del Slider"
                  : "Agregar Elemento al Slider"}
              </h2>
              <Form onSubmit={handleSliderSubmit}>
                <FormGroup>
                  <Label>Título</Label>
                  <Input
                    type="text"
                    name="title"
                    value={sliderFormData.title}
                    onChange={handleSliderInputChange}
                    required
                  />
                </FormGroup>

                <FormGroup>
                  <Label>Descripción</Label>
                  <TextArea
                    name="description"
                    value={sliderFormData.description}
                    onChange={handleSliderInputChange}
                    required
                  />
                </FormGroup>

                <FormGroup>
                  <Label>Enlace (opcional)</Label>
                  <Input
                    type="text"
                    name="link"
                    value={sliderFormData.link}
                    onChange={handleSliderInputChange}
                    placeholder="https://..."
                  />
                </FormGroup>

                <FormGroup>
                  <Label>Productos para Contenedores</Label>
                  
                  {/* Contenedor 1 - Selector de Categoría */}
                  <div style={{ marginBottom: "20px" }}>
                    <Label style={{ marginBottom: 6 }}>Contenedor 1 (Categoría)</Label>
                    <Select
                      name="category_id"
                      value={sliderFormData.category_id || ""}
                      onChange={handleSliderInputChange}
                    >
                      <option value="">Seleccionar categoría...</option>
                      {categories.map((category) => (
                        <option key={category.id} value={category.id}>
                          {category.name}
                        </option>
                      ))}
                    </Select>
                  </div>
                  {sliderContainers.map((containerKey) => {
                    const key = containerKey as SliderContainerKey;
                    const selectedProductId =
                      sliderFormData.container_products[key];
                    const selectedProduct = allProducts.find(
                      (p) => String(p.id) === String(selectedProductId)
                    );
                    return (
                      <div
                        key={key}
                        style={{
                          marginBottom: 20,
                          position: "relative",
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "flex-start",
                        }}
                      >
                        <Label style={{ marginBottom: 6 }}>
                          Contenedor {key.replace("container", "")}
                        </Label>
                        {selectedProduct ? (
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: 12,
                              background: "#f8f9fa",
                              borderRadius: 8,
                              padding: 10,
                              border: "1px solid #eee",
                              marginBottom: 8,
                              width: "100%",
                            }}
                          >
                            <img
                              src={getImageUrl(selectedProduct.image_url)}
                              alt={selectedProduct.name}
                              style={{
                                width: 50,
                                height: 50,
                                objectFit: "cover",
                                borderRadius: 6,
                              }}
                            />
                            <div style={{ flex: 1 }}>
                              <div style={{ fontWeight: 600 }}>
                                {selectedProduct.name}
                              </div>
                              <div style={{ fontSize: 13, color: "#666" }}>
                                {selectedProduct.brand || ""}{" "}
                                {selectedProduct.model || ""}
                              </div>
                            </div>
                            <button
                              type="button"
                              onClick={() => handleRemoveSliderProduct(key)}
                              style={{
                                background: "#dc3545",
                                color: "white",
                                border: "none",
                                borderRadius: 4,
                                padding: "6px 12px",
                                cursor: "pointer",
                              }}
                            >
                              Quitar
                            </button>
                          </div>
                        ) : (
                          <div style={{ width: "100%", position: "relative" }}>
                            <Input
                              type="text"
                              placeholder="Buscar producto..."
                              value={sliderProductSearch[key]}
                              onChange={(e) =>
                                handleSliderProductSearch(key, e.target.value)
                              }
                              autoComplete="off"
                              style={{ width: "100%", marginBottom: 0 }}
                            />
                            {sliderProductResults[key].length > 0 && (
                              <div
                                style={{
                                  border: "1px solid #eee",
                                  borderRadius: 6,
                                  background: "white",
                                  maxHeight: 180,
                                  overflowY: "auto",
                                  marginTop: 2,
                                  zIndex: 10,
                                  position: "absolute",
                                  width: "100%",
                                  top: "100%",
                                  left: 0,
                                  boxShadow: "0 4px 16px rgba(0,0,0,0.08)",
                                }}
                              >
                                {sliderProductResults[key].map(
                                  (product: Product) => (
                                    <div
                                      key={product.id}
                                      style={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 10,
                                        padding: 8,
                                        cursor: "pointer",
                                        borderBottom: "1px solid #f0f0f0",
                                      }}
                                      onClick={() =>
                                        handleSelectSliderProduct(key, product)
                                      }
                                    >
                                      <img
                                        src={getImageUrl(product.image_url)}
                                        alt={product.name}
                                        style={{
                                          width: 36,
                                          height: 36,
                                          objectFit: "cover",
                                          borderRadius: 4,
                                        }}
                                      />
                                      <div style={{ flex: 1 }}>
                                        <div style={{ fontWeight: 500 }}>
                                          {product.name}
                                        </div>
                                        <div
                                          style={{ fontSize: 12, color: "#888" }}
                                        >
                                          {product.brand || ""}{" "}
                                          {product.model || ""}
                                        </div>
                                      </div>
                                    </div>
                                  )
                                )}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </FormGroup>

                <FormGroup>
                  <Label>Imagen del Slider (Desktop)</Label>
                  <div style={{
                    fontSize: '13px',
                    color: '#6c757d',
                    fontWeight: '600',
                    marginBottom: '8px',
                    padding: '8px 12px',
                    background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
                    borderRadius: '8px',
                    border: '1px solid #dee2e6',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                  }}>
                    🖥️ Tamaño recomendado: 1920 × 533 píxeles
                  </div>
                  <Input
                    type="file"
                    name="image"
                    onChange={handleSliderInputChange}
                    accept="image/*"
                    required={!editingSliderItem}
                  />
                  {editingSliderItem && editingSliderItem.image_url && (
                    <div style={{ marginTop: "10px" }}>
                      <img
                        src={getImageUrl(editingSliderItem.image_url)}
                        alt="Vista previa desktop"
                        style={{ maxWidth: "200px", maxHeight: "100px" }}
                      />
                    </div>
                  )}
                </FormGroup>

                <FormGroup>
                  <Label>Imagen del Slider (Mobile - Opcional)</Label>
                  <div style={{
                    fontSize: '13px',
                    color: '#6c757d',
                    fontWeight: '600',
                    marginBottom: '8px',
                    padding: '8px 12px',
                    background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
                    borderRadius: '8px',
                    border: '1px solid #dee2e6',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                  }}>
                    📱 Tamaño recomendado: 1080 × 1080 píxeles (Cuadrado)
                  </div>
                  <Input
                    type="file"
                    name="mobile_image"
                    onChange={handleSliderInputChange}
                    accept="image/*"
                  />
                  {editingSliderItem && editingSliderItem.mobile_image_url && (
                    <div style={{ marginTop: "10px" }}>
                      <img
                        src={getImageUrl(editingSliderItem.mobile_image_url)}
                        alt="Vista previa mobile"
                        style={{ maxWidth: "200px", maxHeight: "100px" }}
                      />
                    </div>
                  )}
                </FormGroup>

                <FormGroup>
                  <Label>
                    <Input
                      type="checkbox"
                      name="active"
                      checked={sliderFormData.active}
                      onChange={handleSliderInputChange}
                      style={{ width: "auto", marginRight: "0.5rem" }}
                    />
                    Activo
                  </Label>
                </FormGroup>

                <ButtonGroup>
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={() => setShowSliderModal(false)}
                  >
                    Cancelar
                  </Button>
                  <Button type="submit" variant="primary">
                    {editingSliderItem ? "Actualizar" : "Agregar"}
                  </Button>
                </ButtonGroup>
              </Form>
            </ModalContent>
          </Modal>

          {/* Modal para ofertas diarias */}
          <Modal show={showDailyDealModal}>
            <ModalContent style={{ maxWidth: "600px", width: "90vw" }}>
              <h2>
                {editingDailyDeal ? "Editar Oferta" : "Agregar Oferta del Día"}
              </h2>
              <Form onSubmit={handleDailyDealSubmit}>
                <FormGroup>
                  <Label>Buscar Producto</Label>
                  <Input
                    type="text"
                    placeholder="Buscar por nombre, descripción, marca o modelo..."
                    value={productSearchTerm}
                    onChange={(e) => handleDailyDealProductSearch(e.target.value)}
                    style={{ marginBottom: "15px" }}
                  />

                  <Label>Seleccionar Producto</Label>
                  {paginatedProductsForDailyDeal.length > 0 ? (
                    <>
                    <ProductGrid>
                      {paginatedProductsForDailyDeal.map((product) => (
                        <ProductCard
                          key={product.id}
                          selected={dailyDealFormData.product_id === product.id}
                          onClick={() =>
                            handleSelectProductForDailyDeal(product.id)
                          }
                        >
                          <ProductName>{product.name}</ProductName>
                          <ProductInfo>
                            {product.brand && `Marca: ${product.brand}`}
                            {product.brand && product.model && " | "}
                            {product.model && `Modelo: ${product.model}`}
                          </ProductInfo>
                          <ProductInfo
                            style={{ marginTop: "5px", fontWeight: "500" }}
                          >
                            Stock: {product.stock}
                          </ProductInfo>
                        </ProductCard>
                      ))}
                    </ProductGrid>
                    
                    {/* Controles de paginación */}
                    {filteredProductsForDailyDeal.length > dailyDealProductPerPage && (
                      <div style={{ 
                        display: 'flex', 
                        justifyContent: 'center', 
                        alignItems: 'center', 
                        gap: '10px', 
                        marginTop: '15px',
                        padding: '10px'
                      }}>
                        <button
                          type="button"
                          onClick={() => setDailyDealProductPage(prev => Math.max(1, prev - 1))}
                          disabled={dailyDealProductPage === 1}
                          style={{
                            padding: '5px 10px',
                            border: '1px solid #ddd',
                            borderRadius: '4px',
                            backgroundColor: dailyDealProductPage === 1 ? '#f5f5f5' : '#fff',
                            cursor: dailyDealProductPage === 1 ? 'not-allowed' : 'pointer'
                          }}
                        >
                          Anterior
                        </button>
                        
                        <span style={{ fontSize: '14px', color: '#666' }}>
                          Página {dailyDealProductPage} de {Math.ceil(filteredProductsForDailyDeal.length / dailyDealProductPerPage)}
                        </span>
                        
                        <button
                          type="button"
                          onClick={() => setDailyDealProductPage(prev => 
                            Math.min(Math.ceil(filteredProductsForDailyDeal.length / dailyDealProductPerPage), prev + 1)
                          )}
                          disabled={dailyDealProductPage >= Math.ceil(filteredProductsForDailyDeal.length / dailyDealProductPerPage)}
                          style={{
                            padding: '5px 10px',
                            border: '1px solid #ddd',
                            borderRadius: '4px',
                            backgroundColor: dailyDealProductPage >= Math.ceil(filteredProductsForDailyDeal.length / dailyDealProductPerPage) ? '#f5f5f5' : '#fff',
                            cursor: dailyDealProductPage >= Math.ceil(filteredProductsForDailyDeal.length / dailyDealProductPerPage) ? 'not-allowed' : 'pointer'
                          }}
                        >
                          Siguiente
                        </button>
                      </div>
                    )}
                    </>
                  ) : (
                    <div
                      style={{
                        padding: "20px",
                        textAlign: "center",
                        color: "#666",
                        border: "1px solid #ddd",
                        borderRadius: "8px",
                        backgroundColor: "#f9f9f9",
                      }}
                    >
                      {productSearchTerm
                        ? "No se encontraron productos con ese criterio de búsqueda"
                        : "No hay productos disponibles"}
                    </div>
                  )}

                  {productSearchTerm &&
                    filteredProductsForDailyDeal.length > 0 && (
                      <small
                        style={{
                          color: "#666",
                          marginTop: "10px",
                          display: "block",
                        }}
                      >
                        Mostrando {filteredProductsForDailyDeal.length}{" "}
                        resultado(s)
                      </small>
                    )}

                  {dailyDealFormData.product_id && (
                    <div
                      style={{
                        marginTop: "10px",
                        padding: "10px",
                        backgroundColor: "#e8f5e8",
                        border: "1px solid #4caf50",
                        borderRadius: "5px",
                        fontSize: "14px",
                      }}
                    >
                      ✓ Producto seleccionado:{" "}
                      {
                        products.find(
                          (p) => p.id === dailyDealFormData.product_id
                        )?.name
                      }
                    </div>
                  )}
                </FormGroup>

                <FormGroup>
                  <Label>
                    <Input
                      type="checkbox"
                      name="active"
                      checked={dailyDealFormData.active}
                      onChange={handleDailyDealInputChange}
                      style={{ width: "auto", marginRight: "0.5rem" }}
                    />
                    Activo
                  </Label>
                </FormGroup>

                <ButtonGroup>
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={() => {
                      setShowDailyDealModal(false);
                      setProductSearchTerm("");
                      setDailyDealProductPage(1); // Resetear paginación
                    }}
                  >
                    Cancelar
                  </Button>
                  <Button
                    type="submit"
                    variant="primary"
                    disabled={!dailyDealFormData.product_id}
                  >
                    {editingDailyDeal ? "Actualizar" : "Agregar"}
                  </Button>
                </ButtonGroup>
              </Form>
            </ModalContent>
          </Modal>

          {/* Modal para familias */}
          <Modal show={showFamilyModal}>
            <ModalContent style={{ maxWidth: "600px", width: "90vw" }}>
              <h2>{editingFamily ? "Editar Familia" : "Agregar Familia"}</h2>
              <Form onSubmit={handleFamilySubmit}>
                <FormGroup>
                  <Label>Nombre</Label>
                  <Input
                    type="text"
                    name="name"
                    value={familyFormData.name}
                    onChange={handleFamilyInputChange}
                    required
                  />
                </FormGroup>

                <FormGroup>
                  <Label>Descripción</Label>
                  <TextArea
                    name="description"
                    value={familyFormData.description}
                    onChange={handleFamilyInputChange}
                  />
                </FormGroup>

                <FormGroup>
                  <Label>Categoría</Label>
                  <div style={{ position: 'relative' }} ref={categoryDropdownRef}>
                    <div style={{ position: 'relative', display: 'inline-block', width: '100%' }}>
                      <Input
                        type="text"
                        placeholder="Buscar categoría..."
                        value={categorySearchTerm}
                        onChange={(e) => handleCategorySearch(e.target.value)}
                        onFocus={() => {
                          if (categorySearchTerm.trim() !== "") {
                            setShowCategoryDropdown(true);
                          }
                        }}
                        style={{ width: '100%', paddingRight: '30px' }}
                        required={!selectedCategoryName}
                      />
                      <button
                        type="button"
                        onClick={handleClearCategorySelection}
                        style={{
                          position: 'absolute',
                          right: '8px',
                          top: '50%',
                          transform: 'translateY(-50%)',
                          background: 'transparent',
                          border: 'none',
                          cursor: 'pointer',
                          fontSize: '16px',
                          color: selectedCategoryName ? '#999' : 'transparent',
                          padding: '0',
                          width: '20px',
                          height: '20px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          pointerEvents: selectedCategoryName ? 'auto' : 'none'
                        }}
                      >
                        ×
                      </button>
                    </div>
                    
                    {showCategoryDropdown && filteredCategoriesForFamily.length > 0 && (
                      <div style={{
                        position: 'absolute',
                        top: '100%',
                        left: 0,
                        right: 0,
                        backgroundColor: 'white',
                        border: '1px solid #ddd',
                        borderRadius: '4px',
                        maxHeight: '200px',
                        overflowY: 'auto',
                        zIndex: 1000,
                        boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                      }}>
                        {filteredCategoriesForFamily.map((category) => (
                          <div
                            key={category.id}
                            onClick={() => handleCategorySelect(category)}
                            style={{
                              padding: '12px',
                              cursor: 'pointer',
                              borderBottom: '1px solid #eee',
                              transition: 'background-color 0.2s'
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.backgroundColor = '#f8f9fa';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.backgroundColor = 'white';
                            }}
                          >
                            {category.name}
                          </div>
                        ))}
                      </div>
                    )}
                    

                  </div>
                </FormGroup>

                <FormGroup>
                  <Label>Imagen</Label>
                  <Input
                    type="file"
                    name="image"
                    accept="image/*"
                    onChange={handleFamilyInputChange}
                  />
                  {familyImageFile && (
                    <div
                      style={{
                        marginTop: "10px",
                        fontSize: "14px",
                        color: "#666",
                      }}
                    >
                      Archivo seleccionado: {familyImageFile.name}
                    </div>
                  )}
                </FormGroup>

                <ButtonGroup>
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={() => setShowFamilyModal(false)}
                  >
                    Cancelar
                  </Button>
                  <Button type="submit" variant="primary">
                    {editingFamily ? "Actualizar" : "Agregar"}
                  </Button>
                </ButtonGroup>
              </Form>
            </ModalContent>
          </Modal>

          {/* Modal para subfamilias */}
          <Modal show={showSubfamilyModal}>
            <ModalContent style={{ maxWidth: "600px", width: "90vw" }}>
              <h2>
                {editingSubfamily ? "Editar Subfamilia" : "Agregar Subfamilia"}
              </h2>
              <Form onSubmit={handleSubfamilySubmit}>
                <FormGroup>
                  <Label>Nombre</Label>
                  <Input
                    type="text"
                    name="name"
                    value={subfamilyFormData.name}
                    onChange={handleSubfamilyInputChange}
                    required
                  />
                </FormGroup>

                <FormGroup>
                  <Label>Descripción</Label>
                  <TextArea
                    name="description"
                    value={subfamilyFormData.description}
                    onChange={handleSubfamilyInputChange}
                  />
                </FormGroup>

                <FormGroup>
                  <Label>Familia</Label>
                  <div style={{ position: 'relative' }} ref={familyDropdownRef}>
                    <div style={{ position: 'relative', display: 'inline-block', width: '100%' }}>
                      <Input
                        type="text"
                        placeholder="Buscar familia..."
                        value={modalFamilySearchTerm}
                        onChange={(e) => handleModalFamilySearch(e.target.value)}
                        onFocus={() => {
                          if (modalFamilySearchTerm.trim() === "") {
                            setModalFilteredFamilies(allFamiliesForSubfamilies.slice(0, 5));
                          }
                          setModalShowFamilyDropdown(true);
                        }}
                        onBlur={() => {
                          setTimeout(() => setModalShowFamilyDropdown(false), 200);
                        }}
                        style={{ width: '100%', paddingRight: '30px' }}
                        required={!modalSelectedFamilyName}
                      />
                      <button
                        type="button"
                        onClick={handleClearFamilySelection}
                        style={{
                          position: 'absolute',
                          right: '8px',
                          top: '50%',
                          transform: 'translateY(-50%)',
                          background: 'transparent',
                          border: 'none',
                          cursor: 'pointer',
                          fontSize: '16px',
                          color: modalSelectedFamilyName ? '#999' : 'transparent',
                          padding: '0',
                          width: '20px',
                          height: '20px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          pointerEvents: modalSelectedFamilyName ? 'auto' : 'none'
                        }}
                      >
                        ×
                      </button>
                    </div>
                    
                    {modalShowFamilyDropdown && modalFilteredFamilies.length > 0 && (
                      <div style={{
                        position: 'absolute',
                        top: '100%',
                        left: 0,
                        right: 0,
                        backgroundColor: 'white',
                        border: '1px solid #ddd',
                        borderRadius: '4px',
                        maxHeight: '200px',
                        overflowY: 'auto',
                        zIndex: 1000,
                        boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                      }}>
                        {modalFilteredFamilies.map((family) => (
                          <div
                            key={family.id}
                            onClick={() => handleFamilySelectForModal(family)}
                            style={{
                              padding: '12px',
                              cursor: 'pointer',
                              borderBottom: '1px solid #eee',
                              transition: 'background-color 0.2s'
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.backgroundColor = '#f8f9fa';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.backgroundColor = 'white';
                            }}
                          >
                            {family.name}
                          </div>
                        ))}
                      </div>
                    )}
                    

                  </div>
                </FormGroup>



                <ButtonGroup>
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={() => setShowSubfamilyModal(false)}
                  >
                    Cancelar
                  </Button>
                  <Button type="submit" variant="primary">
                    {editingSubfamily ? "Actualizar" : "Agregar"}
                  </Button>
                </ButtonGroup>
              </Form>
            </ModalContent>
          </Modal>
          </ContentWrapper>
        </MainContent>
        
        {/* Botón para volver al home */}
        <BackButton onClick={() => navigate("/")}>
          <ArrowLeft />
        </BackButton>
      </AdminLayout>
    );
}

export default AdminPanel;